import { jsx, jsxs, Fragment as Fragment$1 } from "react/jsx-runtime";
import * as React from "react";
import React__default, { useCallback, isValidElement, useState, useRef, useEffect, createElement, Fragment, useMemo, forwardRef, createContext, useContext, Children } from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "react-i18next";
import { CentralIcon } from "@central-icons-react/square-filled-radius-0-stroke-1.5";
import { CentralIcon as CentralIcon$1 } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { CentralIcon as CentralIcon$2 } from "@central-icons-react/square-filled-radius-0-stroke-1";
import { CentralIcon as CentralIcon$3 } from "@central-icons-react/square-outlined-radius-0-stroke-1";
import { AnimatePresence, motion } from "motion/react";
import { XIcon, ChevronDown, CheckIcon as CheckIcon$1 } from "lucide-react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { MagnifyingGlassIcon, ChevronRightIcon, CheckIcon as CheckIcon$2, DotFilledIcon, DividerHorizontalIcon, DotsHorizontalIcon, ChevronDownIcon } from "@radix-ui/react-icons";
import { Command as Command$1 } from "cmdk";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as SeparatorPrimitive from "@radix-ui/react-separator";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { DayPicker } from "react-day-picker";
import * as RechartsPrimitive from "recharts";
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import * as LabelPrimitive from "@radix-ui/react-label";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { useSensors, useSensor, PointerSensor, KeyboardSensor, DndContext, closestCenter } from "@dnd-kit/core";
import { restrictToParentElement, restrictToWindowEdges } from "@dnd-kit/modifiers";
import { sortableKeyboardCoordinates, SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { isFragment } from "react-is";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import * as ToastPrimitives from "@radix-ui/react-toast";
import * as VisuallyHiddenPrimative from "@radix-ui/react-visually-hidden";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
function createComponentDisplayNameFilter(name) {
  return (child) => isValidElement(child) && typeof child.type === "function" && child.type.displayName === name;
}
const mergeRefs = (...refs) => {
  return (node) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    });
  };
};
function useDefaultTranslation() {
  const { t } = useTranslation();
  const defaultT = useCallback(
    (tid, fallback, values) => tid !== void 0 ? t(tid, values) : fallback,
    [t]
  );
  return {
    t: defaultT
  };
}
const LEGACY_ICON_MAP = {
  // Existing mappings
  "ar-cube-1": "IconArCube1",
  archive: "IconArchive",
  "arrow-box-right": "IconArrowBoxRight",
  "arrow-corner-down-left": "IconArrowCornerDownLeft",
  "arrow-down": "IconArrowDown",
  "arrow-inbox": "IconArrowInbox",
  "arrow-left-right": "IconArrowLeftRight",
  "arrow-left-up-circle": "IconArrowLeftUpCircle",
  "arrow-left-x": "IconArrowLeftX",
  "arrow-left": "IconArrowLeft",
  "arrow-out-of-box": "IconArrowOutOfBox",
  "arrow-right": "IconArrowRight",
  "arrow-rotate-clockwise-right": "IconArrowRotateClockwise",
  "arrow-rotate-right-left": "IconArrowRotateRightLeft",
  "arrow-share-right": "IconArrowShareRight",
  "arrow-top-bottom": "IconArrowTopBottom",
  "arrow-triangle-bottom": "IconArrowTriangleBottom",
  "arrow-triangle-top": "IconArrowTriangleTop",
  "arrow-up-down-left-right": "IconArrowUpDownLeftRight",
  "arrow-up-right": "IconArrowUpRight",
  "arrow-up": "IconArrowUp",
  at: "IconAt",
  avocado: "IconAvocado",
  "bag-2": "IconBag2",
  bank: "IconBank",
  "bars-three": "IconBarsThree",
  "bars-three-2": "IconBarsThree2",
  "bars-three-3": "IconBarsThree3",
  "basket-1": "IconBasket1",
  "bell-2": "IconBell2",
  "bezier-curve": "IconBezierCurve",
  "boolean-group-intersect-3": "IconBooleanGroupIntersect3",
  book: "IconBook",
  "brackets-1": "IconBrackets1",
  "brackets-2": "IconBrackets2",
  "broken-chain-link-3": "IconBrokenChainLink3",
  "bubble-5": "IconBubble5",
  "bubble-heart": "IconBubbleHeart",
  "bullet-list": "IconBulletList",
  calculator: "IconCalculator",
  "calendar-1": "IconCalendar1",
  "calendar-3": "IconCalendar3",
  "camera-1": "IconCamera1",
  cat: "IconCat",
  "chain-link-1": "IconChainLink1",
  "chain-link-4": "IconChainLink4",
  "checkmark-2-small": "IconCheckmark2Small",
  "checkmark-2": "IconCheckmark2",
  "chevron-bottom": "IconChevronBottom",
  "chevron-down-small": "IconChevronDownSmall",
  "chevron-grabber-vertical": "IconChevronGrabberVertical",
  "chevron-left": "IconChevronLeft",
  "chevron-right": "IconChevronRight",
  "chevron-top": "IconChevronTop",
  "circle-check": "IconCircleCheck",
  "circle-info": "IconCircleInfo",
  "circle-plus": "IconCirclePlus",
  "circle-questionmark": "IconCircleQuestionmark",
  "circle-x": "IconCircleX",
  circle: "IconCircle",
  "circles-three": "IconCirclesThree",
  "clipboard-2": "IconClipboard2",
  "cloud-simple-download": "IconCloudSimpleDownload",
  "cloud-simple-upload": "IconCloudSimpleUpload",
  "cloudy-sun": "IconCloudySun",
  "cmd-box": "IconCmdBox",
  "code-brackets": "IconCodeBrackets",
  code: "IconCode",
  "color-palette": "IconColorPalette",
  "color-swatch": "IconColorSwatch",
  colors: "IconColors",
  "column-wide-add": "IconColumnWideAdd",
  "column-wide-remove": "IconColumnWideRemove",
  console: "IconConsole",
  "cross-large": "IconCrossLarge",
  "cross-small": "IconCrossSmall",
  "cursor-click": "IconCursorClick",
  "cursor-list": "IconCursorList",
  "date-repeat": "IconCalendarRepeat",
  "divider-line": "IconDivider",
  dossier: "IconDossier",
  "dot-grid-1x3-horizontal": "IconDotGrid1x3Horizontal",
  "dot-grid-1x3-vertical": "IconDotGrid1x3Vertical",
  drop: "IconDrop",
  earth: "IconEarth",
  "edit-small-2": "IconEditSmall2",
  electrocardiogram: "IconElectrocardiogram",
  "email-2": "IconEmail2",
  email: "IconEmail1",
  "emoji-smile": "IconEmojiSmile",
  "eye-open": "IconEyeOpen",
  "eye-slash": "IconEyeSlash",
  "file-bend": "IconFileBend",
  "file-download": "IconFileDownload",
  "file-text": "IconFileText",
  files: "IconFiles",
  "filter-1": "IconFilter1",
  "filter-2": "IconFilter2",
  "filter-asc": "IconFilterAsc",
  "filter-ascending": "IconFilterAscending",
  "filter-desc": "IconFilterDesc",
  "filter-descending": "IconFilterDescending",
  "fire-1": "IconFire1",
  "focus-flash": "IconFocusFlash",
  "folder-2": "IconFolder2",
  "folder-add-left": "IconFolderAddLeft",
  "folder-open": "IconFolderOpen",
  "folder-paper": "IconFolderPaper",
  "folder-upload": "IconFolderUpload",
  "gift-1": "IconGift1",
  "git-pull-request": "IconPullRequest",
  globus: "IconGlobus",
  "group-1": "IconGroup1",
  growth: "IconGrowth",
  "hand-5-finger": "IconHand5Finger",
  haptic: "IconHaptic",
  hashtag: "IconHashtag",
  "hat-bunny": "IconHatBunny",
  heart: "IconHeart",
  history: "IconHistory",
  "home-line": "IconHomeLine",
  hourglass: "IconHourglass",
  imac: "IconImac",
  "inbox-empty": "IconInboxEmpty",
  "key-2": "IconKey2",
  "layout-column": "IconLayoutColumn",
  "layout-grid-2": "IconLayoutGrid2",
  "layers-three": "IconLayersThree",
  "light-bulb": "IconLightBulb",
  lightning: "IconLightning",
  "live-activity": "IconLiveActivity",
  "live-full": "IconLiveFull",
  loader: "IconLoader",
  lock: "IconLock",
  "magic-wand-2": "IconMagicWand2",
  "magnifying-glass": "IconMagnifyingGlass",
  "make-it-pop": "IconMakeItPop",
  "map-pin-flat": "IconMapPinFlat",
  "minus-small": "IconMinusSmall",
  newspaper: "IconNewspaper",
  "numbered-list": "IconNumberedList",
  "page-text-add": "IconPageTextAdd",
  "page-text-search": "IconPageTextSearch",
  "page-text": "IconPageText",
  pencil: "IconPencil",
  "people-add-2": "IconPeopleAdd2",
  "people-circle-circle": "IconPeopleCircle",
  "people-like": "IconPeopleLike",
  "people-remove-2": "IconPeopleRemove2",
  people: "IconPeople",
  pets: "IconPets",
  pin: "IconPin",
  play: "IconPlay",
  "plus-large": "IconPlusLarge",
  "plus-small": "IconPlusSmall",
  "postcard-2": "IconPostcard2",
  "rainy-light": "IconRainyLight",
  reorder: "IconReorder",
  rocket: "IconRocket",
  school: "IconSchool",
  "scissors-2": "IconScissors2",
  "settings-gear-2": "IconSettingsGear2",
  "settings-slider-three": "IconSettingsSliderThree",
  "settings-toggle-1": "IconSettingsToggle1",
  "shield-check": "IconShieldCheck",
  shovel: "IconShovel",
  "sidebar-left-arrow": "IconSidebarLeftArrow",
  sidebar: "IconSidebar",
  summary: "IconSummary",
  "sparkles-soft": "IconSparklesSoft",
  "square-arrow-top-right": "IconSquareArrowTopRight",
  "square-behind-square-1": "IconSquareBehindSquare1",
  "square-checklist-bell": "IconSquareChecklistBell",
  "square-grid-circle": "IconSquareGridCircle",
  "step-back": "IconStepBack",
  stop: "IconStop",
  sunrise: "IconSunrise",
  sword: "IconSword",
  table: "IconTable",
  "text-1": "IconText1",
  "title-case": "IconTitleCase",
  toolbox: "IconToolbox",
  translate: "IconTranslate",
  "trash-can-2": "IconTrashCan",
  "trash-paper": "IconTrashPaper",
  "trending-4": "IconTrending4",
  "triangle-exclamation": "IconTriangleExclamation",
  truck: "IconTruck",
  unicorn: "IconUnicorn",
  "vector-logo": "IconVectorLogo",
  // Documentation icons mapped to @central-icons
  add: "IconPlusLarge",
  alertCircle: "IconCircleInfo",
  alertTriangle: "IconTriangleExclamation",
  arrowProgress: "IconArrowRight",
  arrowRotateLeft: "IconArrowRotateCounterClockwise",
  arrowsMaximize: "IconArrowsAllSides",
  ban: "IconCircleBanSign",
  boxLogo: "IconBox2",
  boxOpen: "IconBox2AltFill",
  building: "IconBuildings",
  calendarLight: "IconCalendar3",
  chat: "IconBubble5",
  checkmark: "IconCheckmark2",
  checkmarkCircle: "IconCircleCheck",
  checkmarkShield: "IconShieldCheck",
  chevronDoubleDown: "IconChevronDoubleDown",
  chevronDown: "IconChevronBottom",
  clipboardCheck: "IconClipboard2",
  columns: "IconLayoutColumn",
  connection: "IconChainLink1",
  connectionSlash: "IconBrokenChainLink1",
  cubeTransparent: "IconArCube1",
  database: "IconServer1",
  diff: "IconCodeBrackets",
  documentAdd: "IconPageAdd",
  documentCopy: "IconPageText",
  dotsVertical: "IconDotGrid1x3Vertical",
  download: "IconArrowDown",
  enter: "IconArrowRight",
  fileCircleInfo: "IconFileText",
  fileCode: "IconCodeBrackets",
  filterList: "IconFilter1",
  googleDrive: "IconGooglePlayStore",
  info: "IconCircleInfo",
  lightningBolt: "IconLightning",
  listTimeline: "IconFilterTimeline",
  mail: "IconEmail1",
  objectGroup: "IconGroup1",
  penSlash: "IconPencilLine",
  planetRinged: "IconSatellite1",
  questionCircle: "IconCircleQuestionmark",
  selector: "IconCursor1",
  sink: "IconDrop",
  spaceShip: "IconRocket",
  tableDot: "IconSquareGridCircle",
  upload: "IconArrowUp",
  userAdd: "IconPeopleAdd",
  userGroup: "IconPeopleCircle",
  userRemove: "IconPeopleRemove",
  userSecret: "IconShieldKeyhole",
  wandSparkles: "IconMagicWand2",
  xCircle: "IconCircleX"
};
const CUSTOM_LIST = [
  "analytics",
  "app",
  "arrow-box-left",
  "arrow-up",
  "assign",
  "at",
  "blur",
  "brain",
  "broom-cleaning",
  "commit",
  "copy-squares",
  "chat-plus",
  "check",
  "chevron-bottom-small",
  "data-clip",
  "date-weekly",
  "dot-grid-2-3",
  "dots-grid-2x3",
  "expand-simple",
  "expand",
  "external-link",
  "first-available",
  "flatfile-logo",
  "flower",
  "focus-square",
  "help-circle",
  "high-priority",
  "layout-window",
  "library",
  "list",
  "list-sparkle",
  "list-bullets",
  "low-priority",
  "magic-book",
  "medium-priority",
  "merge",
  "minimize",
  "paperclip",
  "pause",
  "prepare-vector",
  "refresh",
  "save",
  "sidebar-right-arrow",
  "sidebar-right-arrow-thin",
  "single-value",
  "sparkles-three",
  "split",
  "split-down",
  "square-behind-square",
  "status-checkmark-circle",
  "status-circle",
  "status-dotted-circle",
  "status-half-circle",
  "status-warning-circle",
  "status-warning-triangle",
  "status-x-circle",
  "stop-circle",
  "string",
  "three-dots-horizontal",
  "wrench",
  "sidebar-left",
  "chat-square",
  "transform-wand"
];
const AnalyticsIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M4 12H8L10 15.5L14 8.5L16 12H20M4 4H20V20H4V4Z", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "square" }) });
const AppIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("rect", { x: "2.5", y: "3.5", width: "7", height: "7", stroke: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("rect", { x: "2.5", y: "14.5", width: "7", height: "7", stroke: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("rect", { x: "14.5", y: "14.5", width: "7", height: "7", stroke: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M18 8V4M18 8H22M18 8V12M18 8H14", stroke: "currentColor" })
] });
const ArrowBoxLeftIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M13 20H20L20 4L13 4M15 12H5M8.5 16.5L4 12L8.5 7.5", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "square" }) });
const ArrowUpIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M6 10L12 4L18 10M12 5V20", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "square" }) });
const AssignIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "16", height: "17", viewBox: "0 0 16 17", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M8 11.625C8.9375 11.625 9.78125 11.1328 10.25 10.3125C10.7422 9.51562 10.7422 8.50781 10.25 7.6875C9.78125 6.89062 8.9375 6.375 8 6.375C7.03906 6.375 6.19531 6.89062 5.72656 7.6875C5.23438 8.50781 5.23438 9.51562 5.72656 10.3125C6.19531 11.1328 7.03906 11.625 8 11.625ZM11.7031 9.5625C11.4219 11.3672 9.875 12.75 8 12.75C6.10156 12.75 4.55469 11.3672 4.27344 9.5625H1.0625C0.734375 9.5625 0.5 9.32812 0.5 9C0.5 8.69531 0.734375 8.4375 1.0625 8.4375H4.27344C4.55469 6.63281 6.10156 5.25 8 5.25C9.875 5.25 11.4219 6.63281 11.7031 8.4375H14.9375C15.2422 8.4375 15.5 8.69531 15.5 9C15.5 9.32812 15.2422 9.5625 14.9375 9.5625H11.7031Z", fill: "currentColor" }) });
const AtIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M16.7368 19.654C15.3609 20.5074 13.738 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 13.9262 20.0428 15.9154 17.8101 15.7125C15.9733 15.4545 14.6512 13.8737 14.9121 12.0479L15.4274 8.5M14.8581 12.4675C14.5589 14.596 12.8066 16.109 10.9442 15.8476C9.08174 15.5858 7.81443 13.6481 8.11357 11.5196C8.41271 9.39108 10.165 7.87778 12.0275 8.13952C13.8899 8.40127 15.1573 10.339 14.8581 12.4675Z", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "square" }) });
const BlurIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { d: "M13 17.75C13 17.1977 13.4477 16.75 14 16.75C14.5523 16.75 15 17.1977 15 17.75C15 18.3023 14.5523 18.75 14 18.75C13.4477 18.75 13 18.3023 13 17.75Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M16.5 17.75C16.5 17.1977 16.9477 16.75 17.5 16.75C18.0523 16.75 18.5 17.1977 18.5 17.75C18.5 18.3023 18.0523 18.75 17.5 18.75C16.9477 18.75 16.5 18.3023 16.5 17.75Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M13 6.25C13 5.69771 13.4477 5.25 14 5.25C14.5523 5.25 15 5.69771 15 6.25C15 6.80229 14.5523 7.25 14 7.25C13.4477 7.25 13 6.80229 13 6.25Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M13.25 20.75C13.25 20.3358 13.5858 20 14 20C14.4142 20 14.75 20.3358 14.75 20.75C14.75 21.1642 14.4142 21.5 14 21.5C13.5858 21.5 13.25 21.1642 13.25 20.75Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M19.5 14C19.5 13.5858 19.8358 13.25 20.25 13.25C20.6642 13.25 21 13.5858 21 14C21 14.4142 20.6642 14.75 20.25 14.75C19.8358 14.75 19.5 14.4142 19.5 14Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M19.5 10C19.5 9.58579 19.8358 9.25 20.25 9.25C20.6642 9.25 21 9.58579 21 10C21 10.4142 20.6642 10.75 20.25 10.75C19.8358 10.75 19.5 10.4142 19.5 10Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M13.25 3.25C13.25 2.83579 13.5858 2.5 14 2.5C14.4142 2.5 14.75 2.83579 14.75 3.25C14.75 3.66421 14.4142 4 14 4C13.5858 4 13.25 3.66421 13.25 3.25Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M12.5 14C12.5 13.1716 13.1716 12.5 14 12.5C14.8284 12.5 15.5 13.1716 15.5 14C15.5 14.8284 14.8284 15.5 14 15.5C13.1716 15.5 12.5 14.8284 12.5 14Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M12.5 10C12.5 9.17157 13.1716 8.5 14 8.5C14.8284 8.5 15.5 9.17157 15.5 10C15.5 10.8284 14.8284 11.5 14 11.5C13.1716 11.5 12.5 10.8284 12.5 10Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M16.5 14C16.5 13.4477 16.9477 13 17.5 13C18.0523 13 18.5 13.4477 18.5 14C18.5 14.5523 18.0523 15 17.5 15C16.9477 15 16.5 14.5523 16.5 14Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M16.5 10C16.5 9.44771 16.9477 9 17.5 9C18.0523 9 18.5 9.44771 18.5 10C18.5 10.5523 18.0523 11 17.5 11C16.9477 11 16.5 10.5523 16.5 10Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M16.5 6.25C16.5 5.69771 16.9477 5.25 17.5 5.25C18.0523 5.25 18.5 5.69771 18.5 6.25C18.5 6.80229 18.0523 7.25 17.5 7.25C16.9477 7.25 16.5 6.80229 16.5 6.25Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M11 6.25C11 6.80228 10.5523 7.25 10 7.25C9.44772 7.25 9 6.80228 9 6.25C9 5.69772 9.44772 5.25 10 5.25C10.5523 5.25 11 5.69772 11 6.25Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M7.5 6.25C7.5 6.80228 7.05228 7.25 6.5 7.25C5.94772 7.25 5.5 6.80228 5.5 6.25C5.5 5.69772 5.94772 5.25 6.5 5.25C7.05228 5.25 7.5 5.69772 7.5 6.25Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M11 17.75C11 18.3023 10.5523 18.75 10 18.75C9.44772 18.75 9 18.3023 9 17.75C9 17.1977 9.44772 16.75 10 16.75C10.5523 16.75 11 17.1977 11 17.75Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M10.75 3.25C10.75 3.66421 10.4142 4 10 4C9.58579 4 9.25 3.66421 9.25 3.25C9.25 2.83579 9.58579 2.5 10 2.5C10.4142 2.5 10.75 2.83579 10.75 3.25Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M4.5 10C4.5 10.4142 4.16421 10.75 3.75 10.75C3.33579 10.75 3 10.4142 3 10C3 9.58579 3.33579 9.25 3.75 9.25C4.16421 9.25 4.5 9.58579 4.5 10Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M4.5 14C4.5 14.4142 4.16421 14.75 3.75 14.75C3.33579 14.75 3 14.4142 3 14C3 13.5858 3.33579 13.25 3.75 13.25C4.16421 13.25 4.5 13.5858 4.5 14Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M10.75 20.75C10.75 21.1642 10.4142 21.5 10 21.5C9.58579 21.5 9.25 21.1642 9.25 20.75C9.25 20.3358 9.58579 20 10 20C10.4142 20 10.75 20.3358 10.75 20.75Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M11.5 10C11.5 10.8284 10.8284 11.5 10 11.5C9.17157 11.5 8.5 10.8284 8.5 10C8.5 9.17157 9.17157 8.5 10 8.5C10.8284 8.5 11.5 9.17157 11.5 10Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M11.5 14C11.5 14.8284 10.8284 15.5 10 15.5C9.17157 15.5 8.5 14.8284 8.5 14C8.5 13.1716 9.17157 12.5 10 12.5C10.8284 12.5 11.5 13.1716 11.5 14Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M7.5 10C7.5 10.5523 7.05228 11 6.5 11C5.94772 11 5.5 10.5523 5.5 10C5.5 9.44772 5.94772 9 6.5 9C7.05228 9 7.5 9.44772 7.5 10Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M7.5 14C7.5 14.5523 7.05228 15 6.5 15C5.94772 15 5.5 14.5523 5.5 14C5.5 13.4477 5.94772 13 6.5 13C7.05228 13 7.5 13.4477 7.5 14Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M7.5 17.75C7.5 18.3023 7.05228 18.75 6.5 18.75C5.94772 18.75 5.5 18.3023 5.5 17.75C5.5 17.1977 5.94772 16.75 6.5 16.75C7.05228 16.75 7.5 17.1977 7.5 17.75Z", fill: "currentColor" })
] });
const BrainIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { d: "M4.61112 5.63888L8.83334 3L12 4.05556L15.1667 3L19.3889 5.63888V8.27778L21.5 9.86112V14.0833L19.3889 15.6667V19.3611L15.1667 22L12 20.9444L8.83334 22L4.61112 19.3611V15.6667L2.5 14.0833V9.86112L4.61112 8.27778V5.63888Z", stroke: "currentColor", strokeLinecap: "square" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M12 4.5V8.5L13.75 10.25M12 20.5V16.5L10.25 14.75", stroke: "currentColor", strokeLinecap: "square" }),
  " ",
  /* @__PURE__ */ jsx("circle", { cx: "9", cy: "13.5", r: "1.5", stroke: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("circle", { cx: "15", cy: "11.5", r: "1.5", stroke: "currentColor" })
] });
const BroomCleaningIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsxs("g", { transform: "translate(5,5) scale(1.5)", children: [
  " ",
  /* @__PURE__ */ jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M9.4567 6.82099L12.4623 1.53092L11.4479 0.95459L8.34481 6.4163L8.00594 6.29296C6.76544 5.84145 5.5417 6.53689 4.92542 7.50639C4.41483 8.30963 3.73515 8.86452 2.88506 9.43125L1.98226 10.0331L8.88878 12.9415L9.16634 12.6581C10.2668 11.5342 10.863 10.5267 11.059 9.1307C11.2065 8.07964 10.4972 7.19968 9.61008 6.87682L9.4567 6.82099ZM5.91001 8.13225C6.32942 7.47246 7.03324 7.18046 7.60692 7.38926L9.21106 7.97312C9.69484 8.14921 9.95888 8.5749 9.90362 8.96851C9.76652 9.94515 9.39588 10.684 8.60343 11.5555L6.54722 10.6896L6.99939 9.33307L5.44124 10.2238L4.39207 9.78202C4.96003 9.33055 5.47983 8.80899 5.91001 8.13225Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M5.63873 2.13873L6.12484 1.1665L6.61095 2.13873L7.58317 2.62484L6.61095 3.11095L6.12484 4.08317L5.63873 3.11095L4.6665 2.62484L5.63873 2.13873Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M3.20817 2.9165L2.52761 4.27761L1.1665 4.95817L2.52761 5.63873L3.20817 6.99984L3.88873 5.63873L5.24984 4.95817L3.88873 4.27761L3.20817 2.9165Z", fill: "currentColor" }),
  " "
] }) });
const CommitIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("rect", { width: "24", height: "24", fill: "white", fillOpacity: "0.01" }),
  " ",
  /* @__PURE__ */ jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M15.9195 11.9998C15.9195 14.1646 14.1646 15.9195 11.9998 15.9195C9.83506 15.9195 8.08016 14.1646 8.08016 11.9998C8.08016 9.83508 9.83506 8.08018 11.9998 8.08018C14.1646 8.08018 15.9195 9.83508 15.9195 11.9998ZM17.3811 12.8C16.9941 15.4249 14.7322 17.4395 11.9998 17.4395C9.26742 17.4395 7.00555 15.4249 6.61859 12.8H0.8C0.358173 12.8 0 12.4418 0 12C0 11.5582 0.358173 11.2 0.8 11.2H6.61854C7.00536 8.57498 9.2673 6.56018 11.9998 6.56018C14.7324 6.56018 16.9942 8.57498 17.3811 11.2H23.2C23.6418 11.2 24 11.5582 24 12C24 12.4418 23.6418 12.8 23.2 12.8H17.3811Z", fill: "currentColor" })
] });
const CopySquaresIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M8 8V4H20V16H16M16 8V20H4V8H16Z", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }) });
const ChatPlusIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "17", height: "18", viewBox: "0 0 12 13", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M5.99988 4.50001L5.99988 6M5.99988 6V7.50001M5.99988 6H4.49988M5.99988 6H7.49988M2.00098 2.50001L10.001 2.5V9.51787L7.50763 9.51789L5.99884 10.7679L4.50763 9.51789H2.00098V2.50001Z", stroke: "currentColor", strokeLinecap: "square" }) });
const CheckIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M4 13L9 18L20 6", stroke: "#2B9A66", strokeWidth: "3", strokeLinecap: "square" }) });
const ChevronBottomSmallIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "26", viewBox: "0 0 24 26", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M4 5.5L6 7.5L8 5.5", stroke: "currentColor", strokeLinecap: "square", strokeWidth: "1" }) });
const DataClipIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsxs("g", { clipPath: "url(#clip0_1474_21)", children: [
    " ",
    /* @__PURE__ */ jsx("path", { d: "M7 5.667C7 4.95967 7.28099 4.28131 7.78115 3.78115C8.28131 3.28099 8.95967 3 9.667 3H18.333C18.6832 3 19.03 3.06898 19.3536 3.20301C19.6772 3.33704 19.9712 3.53349 20.2189 3.78115C20.4665 4.0288 20.663 4.32281 20.797 4.64638C20.931 4.96996 21 5.31676 21 5.667V14.333C21 14.6832 20.931 15.03 20.797 15.3536C20.663 15.6772 20.4665 15.9712 20.2189 16.2189C19.9712 16.4665 19.6772 16.663 19.3536 16.797C19.03 16.931 18.6832 17 18.333 17H9.667C8.95967 17 8.28131 16.719 7.78115 16.2189C7.28099 15.7187 7 15.0403 7 14.333V5.667Z", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
    " ",
    /* @__PURE__ */ jsx("path", { d: "M4.012 7.25977C3.70534 7.43459 3.45027 7.68727 3.27258 7.99227C3.09488 8.29727 3.00085 8.64378 3 8.99677V18.9968C3 20.0968 3.9 20.9968 5 20.9968H15C15.75 20.9968 16.158 20.6118 16.5 19.9968", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
    " ",
    /* @__PURE__ */ jsxs("g", { clipPath: "url(#clip1_1474_21)", children: [
      " ",
      /* @__PURE__ */ jsx("path", { d: "M10.5 7.91797C10.5 8.24949 10.6317 8.56743 10.8661 8.80185C11.1005 9.03627 11.4185 9.16797 11.75 9.16797C12.0815 9.16797 12.3995 9.03627 12.6339 8.80185C12.8683 8.56743 13 8.24949 13 7.91797C13 7.58645 12.8683 7.26851 12.6339 7.03409C12.3995 6.79966 12.0815 6.66797 11.75 6.66797C11.4185 6.66797 11.1005 6.79966 10.8661 7.03409C10.6317 7.26851 10.5 7.58645 10.5 7.91797Z", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round" }),
      " ",
      /* @__PURE__ */ jsx("path", { d: "M10.5 12.084C10.5 12.4155 10.6317 12.7334 10.8661 12.9679C11.1005 13.2023 11.4185 13.334 11.75 13.334C12.0815 13.334 12.3995 13.2023 12.6339 12.9679C12.8683 12.7334 13 12.4155 13 12.084C13 11.7525 12.8683 11.4345 12.6339 11.2001C12.3995 10.9657 12.0815 10.834 11.75 10.834C11.4185 10.834 11.1005 10.9657 10.8661 11.2001C10.6317 11.4345 10.5 11.7525 10.5 12.084Z", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round" }),
      " ",
      /* @__PURE__ */ jsx("path", { d: "M12.832 8.58398L17.1654 12.9173", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round" }),
      " ",
      /* @__PURE__ */ jsx("path", { d: "M12.832 11.4173L17.1654 7.08398", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round" }),
      " "
    ] }),
    " "
  ] }),
  " ",
  /* @__PURE__ */ jsxs("defs", { children: [
    " ",
    /* @__PURE__ */ jsxs("clipPath", { id: "clip0_1474_21", children: [
      " ",
      /* @__PURE__ */ jsx("rect", { width: "24", height: "24", fill: "white" }),
      " "
    ] }),
    " ",
    /* @__PURE__ */ jsxs("clipPath", { id: "clip1_1474_21", children: [
      " ",
      /* @__PURE__ */ jsx("rect", { width: "10", height: "10", fill: "white", transform: "translate(9 5.00098)" }),
      " "
    ] }),
    " "
  ] })
] });
const DateWeeklyIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { d: "M20 5.5C20 6.32843 19.3284 7 18.5 7C17.6716 7 17 6.32843 17 5.5C17 4.67157 17.6716 4 18.5 4C19.3284 4 20 4.67157 20 5.5Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M20 12C20 12.8284 19.3284 13.5 18.5 13.5C17.6716 13.5 17 12.8284 17 12C17 11.1716 17.6716 10.5 18.5 10.5C19.3284 10.5 20 11.1716 20 12Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M7 12C7 12.8284 6.32843 13.5 5.5 13.5C4.67157 13.5 4 12.8284 4 12C4 11.1716 4.67157 10.5 5.5 10.5C6.32843 10.5 7 11.1716 7 12Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M7 18.5C7 19.3284 6.32843 20 5.5 20C4.67157 20 4 19.3284 4 18.5C4 17.6716 4.67157 17 5.5 17C6.32843 17 7 17.6716 7 18.5Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M12 3C10.6193 3 9.5 4.11929 9.5 5.5C9.5 6.88071 10.6193 8 12 8C13.3807 8 14.5 6.88071 14.5 5.5C14.5 4.11929 13.3807 3 12 3Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M12 9.5C10.6193 9.5 9.5 10.6193 9.5 12C9.5 13.3807 10.6193 14.5 12 14.5C13.3807 14.5 14.5 13.3807 14.5 12C14.5 10.6193 13.3807 9.5 12 9.5Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M12 16C10.6193 16 9.5 17.1193 9.5 18.5C9.5 19.8807 10.6193 21 12 21C13.3807 21 14.5 19.8807 14.5 18.5C14.5 17.1193 13.3807 16 12 16Z", fill: "currentColor" })
] });
const DotGrid23Icon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { d: "M7.5 5.75C7.5 4.7835 8.2835 4 9.25 4C10.2165 4 11 4.7835 11 5.75C11 6.7165 10.2165 7.5 9.25 7.5C8.2835 7.5 7.5 6.7165 7.5 5.75Z" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M13 5.75C13 4.7835 13.7835 4 14.75 4C15.7165 4 16.5 4.7835 16.5 5.75C16.5 6.7165 15.7165 7.5 14.75 7.5C13.7835 7.5 13 6.7165 13 5.75Z" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M7.5 18.25C7.5 17.2835 8.2835 16.5 9.25 16.5C10.2165 16.5 11 17.2835 11 18.25C11 19.2165 10.2165 20 9.25 20C8.2835 20 7.5 19.2165 7.5 18.25Z" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M13 18.25C13 17.2835 13.7835 16.5 14.75 16.5C15.7165 16.5 16.5 17.2835 16.5 18.25C16.5 19.2165 15.7165 20 14.75 20C13.7835 20 13 19.2165 13 18.25Z" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M7.5 11.9C7.5 10.9335 8.2835 10.15 9.25 10.15C10.2165 10.15 11 10.9335 11 11.9V12C11 12.9665 10.2165 13.75 9.25 13.75C8.2835 13.75 7.5 12.9665 7.5 12V11.9Z" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M13 11.9C13 10.9335 13.7835 10.15 14.75 10.15C15.7165 10.15 16.5 10.9335 16.5 11.9V12C16.5 12.9665 15.7165 13.75 14.75 13.75C13.7835 13.75 13 12.9665 13 12V11.9Z" })
] });
const DotsGrid2x3Icon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { d: "M8 5C8 4.44772 8.44772 4 9 4V4C9.55228 4 10 4.44772 10 5V5C10 5.55228 9.55228 6 9 6V6C8.44772 6 8 5.55228 8 5V5Z", stroke: "currentColor", strokeWidth: "2" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M8 12C8 11.4477 8.44772 11 9 11V11C9.55228 11 10 11.4477 10 12V12C10 12.5523 9.55228 13 9 13V13C8.44772 13 8 12.5523 8 12V12Z", stroke: "currentColor", strokeWidth: "2" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M10 19C10 18.4477 9.55228 18 9 18V18C8.44772 18 8 18.4477 8 19V19C8 19.5523 8.44772 20 9 20V20C9.55228 20 10 19.5523 10 19V19Z", stroke: "currentColor", strokeWidth: "2" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M14 5C14 4.44772 14.4477 4 15 4V4C15.5523 4 16 4.44772 16 5V5C16 5.55228 15.5523 6 15 6V6C14.4477 6 14 5.55228 14 5V5Z", stroke: "currentColor", strokeWidth: "2" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M16 12C16 11.4477 15.5523 11 15 11V11C14.4477 11 14 11.4477 14 12V12C14 12.5523 14.4477 13 15 13V13C15.5523 13 16 12.5523 16 12V12Z", stroke: "currentColor", strokeWidth: "2" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M14 19C14 18.4477 14.4477 18 15 18V18C15.5523 18 16 18.4477 16 19V19C16 19.5523 15.5523 20 15 20V20C14.4477 20 14 19.5523 14 19V19Z", stroke: "currentColor", strokeWidth: "2" })
] });
const ExpandSimpleIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M5.5 12.5V18.5H11.5M12.5 5.5H18.5V11.5", stroke: "currentColor", strokeLinecap: "square" }) });
const ExpandIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "18", height: "18", viewBox: "0 0 18 18", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M10.75 0.75H17.25V7.25M10.75 7.25L16.4766 1.52344M7.25 10.75L1.52344 16.4766M0.75 10.75V17.25H7.25", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "square" }) });
const ExternalLinkIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M15 4H20V9M20 4L10 14M8 6H4C3.44772 6 3 6.44772 3 7V20C3 20.5523 3.44772 21 4 21H17C17.5523 21 18 20.5523 18 20V16", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) });
const FirstAvailableIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "16", height: "17", viewBox: "0 0 16 17", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsxs("g", { id: "Frame", children: [
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector", d: "M11.3333 12.5L7.33325 8.5L11.3333 4.5", stroke: "currentColor", strokeWidth: "1.33333", strokeLinecap: "round", strokeLinejoin: "round" }),
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector_2", d: "M4.66675 4.5V12.5", stroke: "currentColor", strokeWidth: "1.33333", strokeLinecap: "round", strokeLinejoin: "round" }),
  " "
] }) });
const FlatfileLogoIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "15", height: "16", viewBox: "0 0 15 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { d: "M15.0001 5.65683H8.73386L6.49231 3.38817V0.00195312H15.0001V5.65683Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M5.6581 0V6.26395L3.3886 8.50354H0V0H5.6581Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M0 9.37744H6.26628L8.50783 11.6461V15.0334H0V9.37744Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M9.34198 15.0348V8.77197L11.6115 6.53125H15.0001V15.0348H9.34198Z", fill: "currentColor" })
] });
const FlowerIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M12 14V21M12 20.1429C12 17.3025 14.3025 15 17.1429 15C17.4136 15 17.6794 15.0209 17.9388 15.0612C17.9791 15.3206 18 15.5864 18 15.8571C18 18.6975 15.6975 21 12.8571 21C12.5864 21 12.3206 20.9791 12.0612 20.9388C12.0209 20.6794 12 20.4136 12 20.1429ZM12 20.1429C12 17.3025 9.69746 15 6.85714 15C6.58644 15 6.32062 15.0209 6.06122 15.0612C6.02091 15.3206 6 15.5864 6 15.8571C6 18.6975 8.30254 21 11.1429 21C11.4136 21 11.6794 20.9791 11.9388 20.9388C11.9791 20.6794 12 20.4136 12 20.1429ZM7 4V8C7 10.7614 9.23858 13 12 13C14.7614 13 17 10.7614 17 8V4L14.5 5L12 3L9.5 5L7 4Z", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "square" }) });
const FocusSquareIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M3.5 8V3.5H8M3.5 16V20.5H8M16 3.5H20.5V8M20.5 16V20.5H16M8.5 8.5H15.5V15.5H8.5V8.5Z", stroke: "currentColor", strokeLinecap: "square" }) });
const HelpCircleIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className, ...props, children: [
  /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10" }),
  /* @__PURE__ */ jsx("path", { d: "M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" }),
  /* @__PURE__ */ jsx("line", { x1: "12", y1: "17", x2: "12.01", y2: "17" })
] });
const HighPriorityIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsxs("g", { id: "High priority", children: [
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector", d: "M1.5 13.125C1.5 12.5037 2.00368 12 2.625 12H4.875C5.49632 12 6 12.5037 6 13.125V19.875C6 20.4963 5.49632 21 4.875 21H2.625C2.00368 21 1.5 20.4963 1.5 19.875V13.125Z", fill: "currentColor", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector_2", d: "M9.75 8.625C9.75 8.00368 10.2537 7.5 10.875 7.5H13.125C13.7463 7.5 14.25 8.00368 14.25 8.625V19.875C14.25 20.4963 13.7463 21 13.125 21H10.875C10.2537 21 9.75 20.4963 9.75 19.875V8.625Z", fill: "currentColor", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector_3", d: "M18 4.125C18 3.50368 18.5037 3 19.125 3H21.375C21.9963 3 22.5 3.50368 22.5 4.125V19.875C22.5 20.4963 21.9963 21 21.375 21H19.125C18.5037 21 18 20.4963 18 19.875V4.125Z", fill: "currentColor", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
  " "
] }) });
const LayoutWindowIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { d: "M20.5 3.5H21V3H20.5V3.5ZM20.5 20.5V21H21V20.5H20.5ZM3.5 20.5H3V21H3.5V20.5ZM3.5 3.5V3H3V3.5H3.5ZM20 3.5V20.5H21V3.5H20ZM20.5 20H3.5V21H20.5V20ZM4 20.5V3.5H3V20.5H4ZM3.5 4H20.5V3H3.5V4Z" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M3.5 12H20.5", stroke: "currentColor", strokeLinecap: "square" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M12.5 3.5V3H11.5V3.5H12.5ZM11.5 20.5V21H12.5V20.5H11.5ZM11.5 3.5V12H12.5V3.5H11.5ZM11.5 12V20.5H12.5V12H11.5Z" })
] });
const LibraryIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M4 4V20M9 4V20M14 4.25L20 20", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "square", strokeLinejoin: "round" }) });
const ListIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "16", height: "17", viewBox: "0 0 16 17", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M4.0625 3.75H5.5625C5.86719 3.75 6.125 4.00781 6.125 4.3125C6.125 4.64062 5.86719 4.875 5.5625 4.875H4.0625C3.94531 4.875 3.875 4.96875 3.875 5.0625V12.9375C3.875 13.0547 3.94531 13.125 4.0625 13.125H5.5625C5.86719 13.125 6.125 13.3828 6.125 13.6875C6.125 14.0156 5.86719 14.25 5.5625 14.25H4.0625C3.33594 14.25 2.75 13.6641 2.75 12.9375V5.0625C2.75 4.35937 3.33594 3.75 4.0625 3.75ZM11.9375 3.75C12.6406 3.75 13.25 4.35937 13.25 5.0625V12.9375C13.25 13.6641 12.6406 14.25 11.9375 14.25H10.4375C10.1094 14.25 9.875 14.0156 9.875 13.6875C9.875 13.3828 10.1094 13.125 10.4375 13.125H11.9375C12.0313 13.125 12.125 13.0547 12.125 12.9375V5.0625C12.125 4.96875 12.0313 4.875 11.9375 4.875H10.4375C10.1094 4.875 9.875 4.64062 9.875 4.3125C9.875 4.00781 10.1094 3.75 10.4375 3.75H11.9375Z", fill: "currentColor" }) });
const ListSparkleIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M4 18H7M4 12H9M4 6H20M17 10.5L18.5 13.5L21.5 15L18.5 16.5L17 19.5L15.5 16.5L12.5 15L15.5 13.5L17 10.5Z", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "square" }) });
const ListBulletsIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M8.5 8.00001C8.5 8.13808 8.38808 8.25001 8.25 8.25001M8.5 8.00001C8.5 7.86194 8.38808 7.75001 8.25 7.75001M8.5 8.00001H8M8.25 8.25001C8.11193 8.25001 8 8.13808 8 8.00001M8.25 8.25001V7.75001M8 8.00001C8 7.86194 8.11193 7.75001 8.25 7.75001M8.46655 7.87501C8.53558 7.99458 8.49462 8.14748 8.37504 8.21652M8.46655 7.87501C8.39751 7.75544 8.24462 7.71447 8.12504 7.7835M8.46655 7.87501L8.03354 8.12501M8.37504 8.21652C8.25547 8.28555 8.10257 8.24458 8.03354 8.12501M8.37504 8.21652L8.12504 7.7835M8.03354 8.12501C7.9645 8.00544 8.00547 7.85254 8.12504 7.7835M8.37508 7.78352C8.49465 7.85256 8.53562 8.00545 8.46659 8.12503M8.37508 7.78352C8.25551 7.71448 8.10261 7.75545 8.03357 7.87503M8.37508 7.78352L8.12508 8.21653M8.46659 8.12503C8.39755 8.2446 8.24465 8.28557 8.12508 8.21653M8.46659 8.12503L8.03357 7.87503M8.12508 8.21653C8.00551 8.1475 7.96454 7.9946 8.03357 7.87503M8.5 12C8.5 12.1381 8.38808 12.25 8.25 12.25M8.5 12C8.5 11.8619 8.38808 11.75 8.25 11.75M8.5 12H8M8.25 12.25C8.11193 12.25 8 12.1381 8 12M8.25 12.25V11.75M8 12C8 11.8619 8.11193 11.75 8.25 11.75M8.46655 11.875C8.53558 11.9946 8.49462 12.1475 8.37504 12.2165M8.46655 11.875C8.39751 11.7554 8.24462 11.7145 8.12504 11.7835M8.46655 11.875L8.03354 12.125M8.37504 12.2165C8.25547 12.2856 8.10257 12.2446 8.03354 12.125M8.37504 12.2165L8.12504 11.7835M8.03354 12.125C7.9645 12.0054 8.00547 11.8525 8.12504 11.7835M8.37508 11.7835C8.49465 11.8526 8.53562 12.0055 8.46659 12.125M8.37508 11.7835C8.25551 11.7145 8.10261 11.7555 8.03357 11.875M8.37508 11.7835L8.12508 12.2165M8.46659 12.125C8.39755 12.2446 8.24465 12.2856 8.12508 12.2165M8.46659 12.125L8.03357 11.875M8.12508 12.2165C8.00551 12.1475 7.96454 11.9946 8.03357 11.875M8.5 16C8.5 16.1381 8.38808 16.25 8.25 16.25M8.5 16C8.5 15.8619 8.38808 15.75 8.25 15.75M8.5 16H8M8.25 16.25C8.11193 16.25 8 16.1381 8 16M8.25 16.25V15.75M8 16C8 15.8619 8.11193 15.75 8.25 15.75M8.46655 15.875C8.53558 15.9946 8.49462 16.1475 8.37504 16.2165M8.46655 15.875C8.39751 15.7554 8.24462 15.7145 8.12504 15.7835M8.46655 15.875L8.03354 16.125M8.37504 16.2165C8.25547 16.2856 8.10257 16.2446 8.03354 16.125M8.37504 16.2165L8.12504 15.7835M8.03354 16.125C7.9645 16.0054 8.00547 15.8525 8.12504 15.7835M8.37508 15.7835C8.49465 15.8526 8.53562 16.0055 8.46659 16.125M8.37508 15.7835C8.25551 15.7145 8.10261 15.7555 8.03357 15.875M8.37508 15.7835L8.12508 16.2165M8.46659 16.125C8.39755 16.2446 8.24465 16.2856 8.12508 16.2165M8.46659 16.125L8.03357 15.875M8.12508 16.2165C8.00551 16.1475 7.96454 15.9946 8.03357 15.875M4 4H20V20H4V4Z", stroke: "currentColor", strokeWidth: "2" }) });
const LowPriorityIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsxs("g", { id: "Low priority", children: [
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector", d: "M1.5 13.125C1.5 12.5037 2.00368 12 2.625 12H4.875C5.49632 12 6 12.5037 6 13.125V19.875C6 20.4963 5.49632 21 4.875 21H2.625C2.00368 21 1.5 20.4963 1.5 19.875V13.125Z", fill: "currentColor", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector_2", d: "M9.75 8.625C9.75 8.00368 10.2537 7.5 10.875 7.5H13.125C13.7463 7.5 14.25 8.00368 14.25 8.625V19.875C14.25 20.4963 13.7463 21 13.125 21H10.875C10.2537 21 9.75 20.4963 9.75 19.875V8.625Z", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector_3", d: "M18 4.125C18 3.50368 18.5037 3 19.125 3H21.375C21.9963 3 22.5 3.50368 22.5 4.125V19.875C22.5 20.4963 21.9963 21 21.375 21H19.125C18.5037 21 18 20.4963 18 19.875V4.125Z", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
  " "
] }) });
const MagicBookIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "17", height: "18", viewBox: "0 0 12 13", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M2 1.5H10V11.5H2V1.5ZM3 9.5V10.5H9V9.5H3ZM9 8.5H3V2.5H9V8.5Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M6.16667 4.16667L6.75 3L7.33333 4.16667L8.5 4.75L7.33333 5.33333L6.75 6.5L6.16667 5.33333L5 4.75L6.16667 4.16667Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M4.33333 6.33333L4.75 5.5L5.16667 6.33333L6 6.75L5.16667 7.16667L4.75 8L4.33333 7.16667L3.5 6.75L4.33333 6.33333Z", fill: "currentColor" })
] });
const MediumPriorityIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsxs("g", { id: "Medium priority", children: [
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector", d: "M1.5 13.125C1.5 12.5037 2.00368 12 2.625 12H4.875C5.49632 12 6 12.5037 6 13.125V19.875C6 20.4963 5.49632 21 4.875 21H2.625C2.00368 21 1.5 20.4963 1.5 19.875V13.125Z", fill: "currentColor", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector_2", d: "M9.75 8.625C9.75 8.00368 10.2537 7.5 10.875 7.5H13.125C13.7463 7.5 14.25 8.00368 14.25 8.625V19.875C14.25 20.4963 13.7463 21 13.125 21H10.875C10.2537 21 9.75 20.4963 9.75 19.875V8.625Z", fill: "currentColor", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector_3", d: "M18 4.125C18 3.50368 18.5037 3 19.125 3H21.375C21.9963 3 22.5 3.50368 22.5 4.125V19.875C22.5 20.4963 21.9963 21 21.375 21H19.125C18.5037 21 18 20.4963 18 19.875V4.125Z", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
  " "
] }) });
const MergeIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "16", height: "22", viewBox: "0 0 16 17", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M0 0.750366C0 0.343918 0.312653 0 0.750366 0H3.00147H4.5022C5.03371 0 5.56522 0.281387 5.87787 0.719101L9.28578 5.15877C9.34831 5.2213 9.41085 5.25257 9.47338 5.25257H13.4441L12.2247 4.03322C11.9121 3.75183 11.9121 3.28285 12.2247 3.00147C12.5061 2.68881 12.9751 2.68881 13.2877 3.00147L15.789 5.50269C16.0703 5.78408 16.0703 6.25305 15.789 6.53444L13.2877 9.03566C12.9751 9.34832 12.5061 9.34832 12.2247 9.03566C11.9121 8.75428 11.9121 8.2853 12.2247 8.00391L13.4441 6.78456H9.47338C9.41085 6.78456 9.31705 6.81583 9.28578 6.87836L5.87787 11.318C5.56522 11.7557 5.03371 12.0059 4.5022 12.0059H3.00147H0.750366C0.312653 12.0059 0 11.6932 0 11.2555C0 10.849 0.312653 10.5051 0.750366 10.5051H3.00147H4.5022C4.56473 10.5051 4.65852 10.4739 4.68979 10.4113L8.03517 6.00293L4.68979 1.62579C4.65852 1.56326 4.56473 1.50073 4.5022 1.50073H3.00147H0.750366C0.312653 1.50073 0 1.18808 0 0.750366Z", fill: "currentColor" }) });
const MinimizeIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M13.5 3.5V10.5H20.5M20.5 3.5L14 10M10.5 20.5V13.5H3.5M3.5 20.5L10 14", stroke: "currentColor", strokeLinecap: "square" }) });
const PaperclipIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M6 11V15C6 18.3137 8.68629 21 12 21C15.3137 21 18 18.3137 18 15V7C18 4.79086 16.2091 3 14 3C11.7909 3 10 4.79086 10 7V15C10 16.1046 10.8954 17 12 17C13.1046 17 14 16.1046 14 15V8", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "square" }) });
const PauseIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { d: "M4.5 3.5H9.5V20.5H4.5V3.5Z", stroke: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M14.5 3.5H19.5V20.5H14.5V3.5Z", stroke: "currentColor" })
] });
const PrepareVectorIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "16", height: "24", viewBox: "0 0 16 21", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { d: "M7.00202 1.57455C7.61976 1.21888 8.38011 1.21889 8.99785 1.57455L14.1331 4.53115C14.753 4.8881 15.1351 5.54901 15.1351 6.2644V11.7356C15.1351 12.451 14.753 13.1119 14.1331 13.4689L8.99781 16.4255C8.38009 16.7811 7.61977 16.7811 7.00203 16.4255L1.86688 13.4691C1.24687 13.1122 0.864746 12.4513 0.864746 11.7359L0.864746 6.26438C0.864746 5.54899 1.24685 4.88807 1.86683 4.53112L7.00202 1.57455Z", stroke: "currentColor", strokeWidth: "1.2", strokeLinecap: "square" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M10.8108 9.00006C10.8108 10.5524 9.55234 11.8109 7.99996 11.8109C6.44758 11.8109 5.18913 10.5524 5.18913 9.00006C5.18913 7.44768 6.44758 6.18923 7.99996 6.18923C9.55234 6.18923 10.8108 7.44768 10.8108 9.00006Z", stroke: "currentColor", strokeWidth: "1.2", strokeLinecap: "square" })
] });
const RefreshIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { d: "M5.49833 7C7.01028 5.18896 9.46856 4.01468 11.9995 4.01468C16.4094 4.01468 19.9844 7.58962 19.9844 11.9995C19.9844 12.338 19.9633 12.6713 19.9226 12.9983L20.945 13.1258C20.991 12.7566 21.0147 12.3807 21.0147 11.9995C21.0147 7.0206 16.9785 2.98438 11.9995 2.98438C9.33311 2.98438 6.7266 4.14482 5 6.0071V3H4V8H9V7H5.49833Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M4.01468 11.9995C4.01468 11.6611 4.03571 11.3277 4.07648 11.0008L3.0541 10.8733C3.00806 11.2424 2.98438 11.6183 2.98438 11.9995C2.98438 16.9785 7.0206 21.0147 11.9995 21.0147C14.6725 21.0147 17.2853 19.8485 19.0118 17.9781V21H20.0118V16H15.0118V17H18.4999C16.9879 18.8105 14.5301 19.9844 11.9995 19.9844C7.58962 19.9844 4.01468 16.4094 4.01468 11.9995Z", fill: "currentColor" })
] });
const SaveIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M8 4V8H16V4M4 4V20H20V7L17 4H4ZM8 13V20H16V13H8Z", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "square" }) });
const SidebarRightArrowIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { d: "M20 20V4H4V20H20Z", stroke: "currentColor", strokeWidth: "2" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M9 4V20", stroke: "currentColor", strokeWidth: "2" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M13.5 14L15.5 12L13.5 10", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "square" })
] });
const SidebarRightArrowThinIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { d: "M20 20V4H4V20H20Z", stroke: "currentColor", strokeWidth: "1.5" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M9 4V20", stroke: "currentColor", strokeWidth: "1.5" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M13.5 14L15.5 12L13.5 10", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "square" })
] });
const SingleValueIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "16", height: "17", viewBox: "0 0 16 17", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M3.875 4.875V6C3.875 6.32812 3.61719 6.5625 3.3125 6.5625C2.98438 6.5625 2.75 6.32812 2.75 6V4.6875C2.75 4.17187 3.14844 3.75 3.6875 3.75H12.3125C12.8281 3.75 13.25 4.17187 13.25 4.6875V6C13.25 6.32812 12.9922 6.5625 12.6875 6.5625C12.3594 6.5625 12.125 6.32812 12.125 6V4.875H8.5625V13.125H9.6875C9.99219 13.125 10.25 13.3828 10.25 13.6875C10.25 14.0156 9.99219 14.25 9.6875 14.25H6.3125C5.98438 14.25 5.75 14.0156 5.75 13.6875C5.75 13.3828 5.98438 13.125 6.3125 13.125H7.4375V4.875H3.875Z", fill: "currentColor" }) });
const SparklesThreeIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5 16V14C7.65569 14 9.33966 13.4128 10.3762 12.3762C11.4128 11.3397 12 9.6557 12 7H14C14 9.6557 14.5872 11.3397 15.6238 12.3762C16.6603 13.4128 18.3443 14 21 14V16C18.3443 16 16.6603 16.5872 15.6238 17.6238C14.5872 18.6603 14 20.3443 14 23H12C12 20.3443 11.4128 18.6603 10.3762 17.6238C9.33966 16.5872 7.65569 16 5 16ZM13 17.8969C13.3177 17.2653 13.7178 16.7013 14.2096 16.2096C14.7013 15.7178 15.2653 15.3177 15.8969 15C15.2653 14.6823 14.7013 14.2822 14.2096 13.7904C13.7178 13.2987 13.3177 12.7347 13 12.1031C12.6823 12.7347 12.2822 13.2987 11.7904 13.7904C11.2987 14.2822 10.7347 14.6823 10.1031 15C10.7347 15.3177 11.2987 15.7178 11.7904 16.2096C12.2822 16.7013 12.6823 17.2653 13 17.8969Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M2 9V8C3.15423 8 3.87123 7.7446 4.30789 7.30789C4.7446 6.87123 5 6.15423 5 5H6C6 6.15423 6.2554 6.87123 6.69211 7.30789C7.12877 7.7446 7.84577 8 9 8V9C7.84577 9 7.12877 9.2554 6.69211 9.69211C6.2554 10.1288 6 10.8458 6 12H5C5 10.8458 4.7446 10.1288 4.30789 9.69211C3.87123 9.2554 3.15423 9 2 9Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8 4V3C8.80703 3 9.27401 2.82097 9.54747 2.54747C9.82097 2.27401 10 1.80703 10 1H11C11 1.80703 11.179 2.27401 11.4525 2.54747C11.726 2.82097 12.193 3 13 3V4C12.193 4 11.726 4.17903 11.4525 4.45253C11.179 4.72599 11 5.19297 11 6H10C10 5.19297 9.82097 4.72599 9.54747 4.45253C9.27401 4.17903 8.80703 4 8 4Z", fill: "currentColor" })
] });
const SplitIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "16", height: "23", viewBox: "0 0 16 19", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M12.2307 0.450157C12.5122 0.168632 12.9814 0.168632 13.263 0.450157L15.7654 2.9526C16.0782 3.26541 16.0782 3.73461 15.7654 4.01614L13.263 6.51858C12.9814 6.83139 12.5122 6.83139 12.2307 6.51858C11.9179 6.23706 11.9179 5.76785 12.2307 5.45504L13.4506 4.2351H10.8231C10.479 4.2351 10.1662 4.39151 9.94721 4.61047L7.03812 7.48828L9.94721 10.3974C10.1662 10.6163 10.479 10.7415 10.8231 10.7415H13.4506L12.2307 9.52152C11.9179 9.23999 11.9179 8.77078 12.2307 8.48926C12.5122 8.17645 12.9814 8.17645 13.263 8.48926L15.7654 10.9917C16.0782 11.2732 16.0782 11.7424 15.7654 12.024L13.263 14.5264C12.9814 14.8392 12.5122 14.8392 12.2307 14.5264C11.9179 14.2449 11.9179 13.7757 12.2307 13.4941L13.4506 12.2742H10.8231C10.1036 12.2742 9.38416 11.9614 8.88368 11.4609L5.69306 8.23901H0.750733C0.312805 8.23901 0 7.92621 0 7.48828C0 7.08163 0.312805 6.73755 0.750733 6.73755H5.69306L8.88368 3.54693C9.38416 3.04644 10.1036 2.73364 10.8231 2.73364H13.4506L12.2307 1.5137C11.9179 1.23217 11.9179 0.762963 12.2307 0.481438V0.450157Z", fill: "currentColor" }) });
const SplitDownIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "16", height: "24", viewBox: "0 0 16 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M15.0498 12.2307C15.3314 12.5122 15.3314 12.9814 15.0498 13.263L12.5474 15.7654C12.2346 16.0782 11.7654 16.0782 11.4839 15.7654L8.98142 13.263C8.66861 12.9814 8.66861 12.5122 8.98142 12.2307C9.26294 11.9179 9.73215 11.9179 10.045 12.2307L11.2649 13.4506L11.2649 10.8231C11.2649 10.479 11.1085 10.1662 10.8895 9.94721L8.01172 7.03812L5.10263 9.94721C4.88366 10.1662 4.75854 10.479 4.75854 10.8231L4.75854 13.4506L5.97848 12.2307C6.26001 11.9179 6.72922 11.9179 7.01074 12.2307C7.32355 12.5122 7.32355 12.9814 7.01074 13.263L4.5083 15.7654C4.22677 16.0782 3.75756 16.0782 3.47604 15.7654L0.973595 13.263C0.660789 12.9814 0.660789 12.5122 0.973595 12.2307C1.25512 11.9179 1.72433 11.9179 2.00585 12.2307L3.22579 13.4506L3.22579 10.8231C3.22579 10.1036 3.5386 9.38416 4.03909 8.88367L7.26099 5.69306L7.26099 0.750733C7.26099 0.312805 7.57379 -3.36018e-07 8.01172 -3.16876e-07C8.41837 -2.991e-07 8.76245 0.312805 8.76245 0.750733L8.76245 5.69306L11.9531 8.88368C12.4536 9.38416 12.7664 10.1036 12.7664 10.8231L12.7664 13.4506L13.9863 12.2307C14.2678 11.9179 14.737 11.9179 15.0186 12.2307L15.0498 12.2307Z", fill: "currentColor" }) });
const SquareBehindSquareIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M8 8V4H20V16H16M16 8V20H4V8H16Z", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }) });
const StatusCheckmarkCircleIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsxs("g", { id: "Status icons", children: [
  " ",
  /* @__PURE__ */ jsx("path", { id: "Exclude", fillRule: "evenodd", clipRule: "evenodd", d: "M6.4443 3.6853C8.08879 2.58649 10.0222 2 12 2C14.6506 2.00508 17.1912 3.06028 19.0655 4.93455C20.9397 6.80881 21.9949 9.3494 22 12C22 13.9778 21.4135 15.9112 20.3147 17.5557C19.2159 19.2002 17.6541 20.4819 15.8268 21.2388C13.9996 21.9957 11.9889 22.1937 10.0491 21.8078C8.10929 21.422 6.32746 20.4696 4.92894 19.0711C3.53041 17.6725 2.578 15.8907 2.19215 13.9509C1.8063 12.0111 2.00433 10.0004 2.76121 8.17316C3.51809 6.3459 4.79981 4.78412 6.4443 3.6853ZM16.7617 10.2489C17.0691 9.95563 17.0806 9.46872 16.7873 9.16133C16.4941 8.85394 16.0072 8.84248 15.6998 9.13573L10.587 14.0134L8.30068 11.8285C7.99354 11.535 7.50662 11.546 7.2131 11.8532C6.91959 12.1603 6.93064 12.6472 7.23778 12.9407L10.0551 15.633C10.3522 15.917 10.8201 15.9172 11.1175 15.6335L16.7617 10.2489Z", fill: "currentColor" }),
  " "
] }) });
const StatusCircleIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsxs("g", { id: "Status icons", children: [
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector", fillRule: "evenodd", clipRule: "evenodd", d: "M12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3ZM1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12Z", fill: "currentColor" }),
  " "
] }) });
const StatusDottedCircleIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsxs("g", { id: "Status icons", children: [
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector", fillRule: "evenodd", clipRule: "evenodd", d: "M22.7906 14.1468L20.8286 13.7586C20.9409 13.1915 21 12.6036 21 12C21 11.3964 20.9409 10.8085 20.8286 10.2414L22.7906 9.85322C22.928 10.5475 23 11.2654 23 12C23 12.7346 22.928 13.4525 22.7906 14.1468ZM21.1472 5.88823L19.4853 7.00087C18.8276 6.01854 17.9815 5.17237 16.9991 4.51472L18.1118 2.85278C19.3113 3.65587 20.3441 4.68866 21.1472 5.88823ZM14.1468 1.20937C13.4525 1.07202 12.7346 1 12 1C11.2654 1 10.5475 1.07202 9.85322 1.20937L10.2414 3.17135C10.8085 3.05914 11.3964 3 12 3C12.6036 3 13.1915 3.05914 13.7586 3.17135L14.1468 1.20937ZM5.88823 2.85278L7.00087 4.51472C6.01854 5.17237 5.17237 6.01854 4.51472 7.00087L2.85278 5.88823C3.65587 4.68866 4.68866 3.65587 5.88823 2.85278ZM1.20937 9.85322C1.07202 10.5475 1 11.2654 1 12C1 12.7346 1.07202 13.4525 1.20937 14.1468L3.17135 13.7586C3.05914 13.1915 3 12.6036 3 12C3 11.3964 3.05914 10.8085 3.17135 10.2414L1.20937 9.85322ZM2.85278 18.1118L4.51472 16.9991C5.17237 17.9815 6.01854 18.8276 7.00087 19.4853L5.88823 21.1472C4.68866 20.3441 3.65587 19.3113 2.85278 18.1118ZM9.85322 22.7906L10.2414 20.8286C10.8085 20.9409 11.3964 21 12 21C12.6036 21 13.1915 20.9409 13.7586 20.8286L14.1468 22.7906C13.4525 22.928 12.7346 23 12 23C11.2654 23 10.5475 22.928 9.85322 22.7906ZM18.1118 21.1472L16.9991 19.4853C17.9815 18.8276 18.8276 17.9815 19.4853 16.9991L21.1472 18.1118C20.3441 19.3113 19.3113 20.3441 18.1118 21.1472Z", fill: "currentColor" }),
  " "
] }) });
const StatusHalfCircleIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsxs("g", { id: "Status icons", children: [
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector", d: "M23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12ZM3.86 12C3.86 16.4956 7.5044 20.14 12 20.14C16.4956 20.14 20.14 16.4956 20.14 12C20.14 7.5044 16.4956 3.86 12 3.86C7.5044 3.86 3.86 7.5044 3.86 12Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector_2", d: "M12 18C13.5913 18 15.1174 17.3679 16.2426 16.2426C17.3679 15.1174 18 13.5913 18 12C18 10.4087 17.3679 8.88258 16.2426 7.75736C15.1174 6.63214 13.5913 6 12 6L12 12L12 18Z", fill: "currentColor" }),
  " "
] }) });
const StatusWarningCircleIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsxs("g", { id: "Status icons", children: [
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector", d: "M12 2C10.0222 2 8.08879 2.58649 6.4443 3.6853C4.79981 4.78412 3.51809 6.3459 2.76121 8.17316C2.00433 10.0004 1.8063 12.0111 2.19215 13.9509C2.578 15.8907 3.53041 17.6725 4.92894 19.0711C6.32746 20.4696 8.10929 21.422 10.0491 21.8078C11.9889 22.1937 13.9996 21.9957 15.8268 21.2388C17.6541 20.4819 19.2159 19.2002 20.3147 17.5557C21.4135 15.9112 22 13.9778 22 12C21.9949 9.3494 20.9397 6.80881 19.0655 4.93455C17.1912 3.06028 14.6506 2.00508 12 2ZM11.2308 7.38461C11.2308 7.1806 11.3118 6.98495 11.4561 6.84069C11.6003 6.69643 11.796 6.61538 12 6.61538C12.204 6.61538 12.3997 6.69643 12.5439 6.84069C12.6882 6.98495 12.7692 7.1806 12.7692 7.38461V12.7692C12.7692 12.9732 12.6882 13.1689 12.5439 13.3132C12.3997 13.4574 12.204 13.5385 12 13.5385C11.796 13.5385 11.6003 13.4574 11.4561 13.3132C11.3118 13.1689 11.2308 12.9732 11.2308 12.7692V7.38461ZM12 17.3846C11.7718 17.3846 11.5487 17.3169 11.359 17.1902C11.1692 17.0634 11.0213 16.8832 10.934 16.6723C10.8467 16.4615 10.8238 16.2295 10.8683 16.0057C10.9128 15.7818 11.0227 15.5762 11.1841 15.4149C11.3455 15.2535 11.5511 15.1436 11.7749 15.0991C11.9987 15.0546 12.2307 15.0774 12.4416 15.1648C12.6524 15.2521 12.8326 15.4 12.9594 15.5897C13.0862 15.7795 13.1538 16.0026 13.1538 16.2308C13.1538 16.5368 13.0323 16.8303 12.8159 17.0467C12.5995 17.263 12.306 17.3846 12 17.3846Z", fill: "currentColor" }),
  " "
] }) });
const StatusWarningTriangleIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsxs("g", { id: "Status icons", children: [
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector", d: "M21.7063 17.7604L13.8573 4.08226C13.6694 3.75329 13.3988 3.48004 13.0727 3.29004C12.7467 3.10004 12.3767 3 12 3C11.6233 3 11.2533 3.10004 10.9273 3.29004C10.6012 3.48004 10.3306 3.75329 10.1427 4.08226L2.29373 17.7604C2.10305 18.0877 2.00172 18.46 2.00002 18.8396C1.99832 19.2191 2.09631 19.5923 2.28404 19.9214C2.47177 20.2504 2.74255 20.5235 3.06889 20.7129C3.39523 20.9024 3.76551 21.0014 4.14211 21H19.8579C20.2345 21.0014 20.6048 20.9024 20.9311 20.7129C21.2574 20.5235 21.5282 20.2504 21.716 19.9214C21.9037 19.5923 22.0017 19.2191 22 18.8396C21.9983 18.46 21.8969 18.0877 21.7063 17.7604ZM11.2856 10.2014C11.2856 10.0105 11.3609 9.82739 11.4949 9.69239C11.6288 9.55738 11.8105 9.48153 12 9.48153C12.1895 9.48153 12.3712 9.55738 12.5051 9.69239C12.6391 9.82739 12.7144 10.0105 12.7144 10.2014V13.801C12.7144 13.9919 12.6391 14.175 12.5051 14.31C12.3712 14.445 12.1895 14.5209 12 14.5209C11.8105 14.5209 11.6288 14.445 11.4949 14.31C11.3609 14.175 11.2856 13.9919 11.2856 13.801V10.2014ZM12 18.1204C11.7881 18.1204 11.5809 18.057 11.4047 17.9384C11.2285 17.8197 11.0911 17.6511 11.01 17.4538C10.9289 17.2564 10.9077 17.0393 10.9491 16.8298C10.9904 16.6204 11.0925 16.428 11.2423 16.2769C11.3922 16.1259 11.5831 16.0231 11.791 15.9814C11.9988 15.9397 12.2143 15.9611 12.4101 16.0429C12.6059 16.1246 12.7732 16.263 12.8909 16.4406C13.0087 16.6182 13.0715 16.8269 13.0715 17.0405C13.0715 17.3269 12.9586 17.6016 12.7577 17.8041C12.5567 18.0066 12.2842 18.1204 12 18.1204Z", fill: "currentColor" }),
  " "
] }) });
const StatusXCircleIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsxs("g", { id: "Status icons", children: [
  " ",
  /* @__PURE__ */ jsx("path", { id: "Vector", d: "M12 2.25C10.0716 2.25 8.18657 2.82183 6.58319 3.89317C4.97982 4.96451 3.73013 6.48726 2.99218 8.26884C2.25422 10.0504 2.06114 12.0108 2.43735 13.9021C2.81355 15.7934 3.74215 17.5307 5.10571 18.8943C6.46928 20.2579 8.20656 21.1865 10.0979 21.5627C11.9892 21.9389 13.9496 21.7458 15.7312 21.0078C17.5127 20.2699 19.0355 19.0202 20.1068 17.4168C21.1782 15.8134 21.75 13.9284 21.75 12C21.745 9.41566 20.7162 6.93859 18.8888 5.11118C17.0614 3.28378 14.5843 2.25496 12 2.25ZM15.5344 14.4656C15.6752 14.6078 15.7542 14.7999 15.7542 15C15.7542 15.2001 15.6752 15.3922 15.5344 15.5344C15.391 15.673 15.1994 15.7505 15 15.7505C14.8006 15.7505 14.609 15.673 14.4656 15.5344L12 13.0594L9.53438 15.5344C9.39102 15.673 9.19942 15.7505 9 15.7505C8.80059 15.7505 8.60898 15.673 8.46563 15.5344C8.32479 15.3922 8.24578 15.2001 8.24578 15C8.24578 14.7999 8.32479 14.6078 8.46563 14.4656L10.9406 12L8.46563 9.53437C8.34603 9.38865 8.28491 9.20366 8.29416 9.01537C8.30341 8.82708 8.38236 8.64896 8.51566 8.51566C8.64896 8.38236 8.82708 8.3034 9.01537 8.29416C9.20366 8.28491 9.38866 8.34603 9.53438 8.46563L12 10.9406L14.4656 8.46563C14.6114 8.34603 14.7963 8.28491 14.9846 8.29416C15.1729 8.3034 15.351 8.38236 15.4843 8.51566C15.6176 8.64896 15.6966 8.82708 15.7058 9.01537C15.7151 9.20366 15.654 9.38865 15.5344 9.53437L13.0594 12L15.5344 14.4656Z", fill: "currentColor" }),
  " "
] }) });
const StopCircleIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M9.5 9.5H14.5V14.5H9.5V9.5Z", fill: "currentColor" })
] });
const StringIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { d: "M3.82634 3H6.9567L9.78304 10.8822H7.19317L6.72024 9.38459H4.04028L3.56735 10.8822H1L3.82634 3ZM6.11218 7.44782L5.67303 6.05154C5.51538 5.57861 5.4253 5.22954 5.38026 5.0156C5.35774 5.13946 5.3127 5.2971 5.26766 5.47727C5.21135 5.65743 5.15505 5.86012 5.08749 6.0628L4.64834 7.44782H6.11218Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M13.0433 10.871C12.773 10.7246 12.5703 10.5444 12.4239 10.3192C12.4465 10.4431 12.4577 10.5894 12.4577 10.7696V10.8822H10.2057V3H12.4577V5.19576C12.4577 5.49979 12.4465 5.72499 12.4239 5.86012C12.5703 5.65743 12.7843 5.47727 13.0658 5.33088C13.3473 5.1845 13.6626 5.10568 14.0229 5.10568C14.4283 5.10568 14.8111 5.21828 15.1714 5.43223C15.5318 5.65743 15.8133 5.98398 16.0385 6.43439C16.2524 6.88481 16.365 7.43656 16.365 8.0784C16.365 8.7315 16.2524 9.29451 16.0498 9.74492C15.8358 10.1953 15.543 10.5331 15.1827 10.7584C14.8224 10.9836 14.417 11.0962 13.9666 11.0962C13.6175 11.0962 13.3135 11.0286 13.0433 10.871ZM13.8202 9.03552C13.9779 8.79906 14.0679 8.48377 14.0679 8.10092C14.0679 7.69555 13.9891 7.38026 13.8315 7.15505C13.6738 6.92985 13.4599 6.80598 13.1896 6.80598C12.9194 6.80598 12.7054 6.92985 12.5478 7.15505C12.3902 7.38026 12.3113 7.69555 12.3113 8.10092C12.3113 8.48377 12.3902 8.79906 12.5478 9.03552C12.7054 9.27199 12.9194 9.38459 13.1896 9.38459C13.4486 9.38459 13.6626 9.27199 13.8202 9.03552Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M18.3495 10.7471C17.8991 10.5106 17.5388 10.1616 17.291 9.71114C17.032 9.26073 16.9082 8.72024 16.9082 8.08966C16.9082 7.47034 17.0433 6.92985 17.3248 6.47943C17.5951 6.02902 17.9779 5.69121 18.4508 5.45475C18.9238 5.22954 19.4417 5.10568 20.016 5.10568C20.9281 5.10568 21.6375 5.3534 22.1555 5.82634C22.6735 6.29927 22.955 6.92985 23 7.69555H20.8042C20.7817 7.44782 20.7142 7.23388 20.5903 7.06497C20.4552 6.90733 20.2863 6.81724 20.0611 6.81724C19.8133 6.81724 19.6106 6.92985 19.453 7.13253C19.2841 7.33522 19.2053 7.65051 19.2053 8.06714C19.2053 8.47251 19.2728 8.79906 19.4305 9.02426C19.5769 9.26073 19.7796 9.37333 20.0385 9.37333C20.2637 9.37333 20.4439 9.29451 20.579 9.13687C20.7142 8.97922 20.7817 8.76528 20.8042 8.49503H23C22.9212 9.30577 22.6171 9.94761 22.0992 10.4093C21.5699 10.871 20.8493 11.0962 19.9259 11.0962C19.3291 11.0962 18.7999 10.9836 18.3495 10.7471Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M2.90299 15.1069C2.76787 15.3321 2.54266 15.501 2.24989 15.6249C1.95713 15.76 1.64184 15.8163 1.31529 15.8163L1.30403 13.4741C1.78822 13.4629 2.20485 13.3503 2.56518 13.1363C2.92551 12.9336 3.16198 12.6183 3.27458 12.213H5.17757V20.0952H2.85795V15.5573C2.85795 15.3771 2.86921 15.2195 2.90299 15.1069Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M8.0135 18.3724C8.19366 17.8994 8.43013 17.5053 8.71164 17.2126C8.99315 16.9198 9.33096 16.627 9.73633 16.3568L10.7047 15.7037C11.0425 15.4785 11.279 15.287 11.4141 15.1294C11.5492 14.983 11.6168 14.8028 11.6168 14.5889C11.6168 14.3975 11.5492 14.2398 11.4366 14.1047C11.3128 13.9808 11.1439 13.9133 10.9299 13.9133C10.6597 13.9133 10.457 14.0034 10.3219 14.1835C10.1867 14.3637 10.1192 14.5889 10.1192 14.8704V15.0618H7.73199V14.9042C7.73199 14.3187 7.85585 13.8119 8.1261 13.3728C8.39635 12.9336 8.76794 12.5958 9.26339 12.3594C9.75885 12.1229 10.3331 11.999 10.9862 11.999C11.6281 11.999 12.1798 12.1116 12.6415 12.3368C13.0919 12.562 13.441 12.8548 13.6662 13.2151C13.8914 13.5867 14.0152 13.9921 14.0152 14.4313C14.0152 15.0844 13.8463 15.6136 13.5085 16.0302C13.1707 16.4581 12.7316 16.8297 12.2023 17.1675L11.4929 17.6179C11.2452 17.7756 11.0763 17.8882 10.9862 17.9557C10.8961 18.0233 10.8286 18.0909 10.7948 18.1584H13.9815V20.0952H7.73199C7.73199 19.4196 7.82207 18.8453 8.0135 18.3724Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M17.1563 19.5209C16.5708 18.9917 16.2668 18.2598 16.2555 17.3252H18.6877C18.6877 17.663 18.7778 17.9332 18.9467 18.1134C19.1156 18.3048 19.3634 18.3949 19.7012 18.3949C19.9714 18.3949 20.1966 18.3386 20.3543 18.2147C20.5119 18.0909 20.5907 17.9107 20.5907 17.6742C20.5907 17.2351 20.2642 17.0099 19.6223 17.0099H18.9129V15.2307H19.5773C19.8476 15.2307 20.0728 15.1857 20.2417 15.0731C20.4106 14.9718 20.5007 14.8028 20.5007 14.5776C20.5007 14.375 20.4331 14.2061 20.298 14.0934C20.1628 13.9808 19.9714 13.9133 19.735 13.9133C19.4534 13.9133 19.2282 13.9921 19.0706 14.1498C18.9129 14.3074 18.8229 14.5213 18.8116 14.7803H16.4019C16.4244 14.2398 16.5708 13.7556 16.8523 13.3277C17.1226 12.9111 17.5167 12.5846 18.0121 12.3481C18.5076 12.1229 19.0931 11.999 19.7575 11.999C20.343 11.999 20.8722 12.1004 21.3452 12.2805C21.8068 12.4607 22.1672 12.731 22.4374 13.0913C22.7077 13.4516 22.8428 13.8682 22.8428 14.3412C22.8428 14.7803 22.7302 15.1519 22.505 15.4447C22.2798 15.7487 21.987 15.9627 21.6492 16.0865C22.0321 16.2216 22.3473 16.4356 22.5838 16.7284C22.809 17.0324 22.9329 17.404 22.9329 17.8319C22.9329 18.3273 22.7865 18.7552 22.5162 19.1268C22.246 19.5097 21.8519 19.8024 21.3564 20.0051C20.861 20.2078 20.2867 20.3091 19.6449 20.3091C18.5751 20.3091 17.7419 20.0502 17.1563 19.5209Z", fill: "currentColor" })
] });
const ThreeDotsHorizontalIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "25", viewBox: "0 0 24 25", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { d: "M2.75 12.5C2.75 11.9477 3.19772 11.5 3.75 11.5V11.5C4.30228 11.5 4.75 11.9477 4.75 12.5V12.5C4.75 13.0523 4.30228 13.5 3.75 13.5V13.5C3.19772 13.5 2.75 13.0523 2.75 12.5V12.5Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M13 12.5C13 11.9477 12.5523 11.5 12 11.5V11.5C11.4477 11.5 11 11.9477 11 12.5V12.5C11 13.0523 11.4477 13.5 12 13.5V13.5C12.5523 13.5 13 13.0523 13 12.5V12.5Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M19.25 12.5C19.25 11.9477 19.6977 11.5 20.25 11.5V11.5C20.8023 11.5 21.25 11.9477 21.25 12.5V12.5C21.25 13.0523 20.8023 13.5 20.25 13.5V13.5C19.6977 13.5 19.25 13.0523 19.25 12.5V12.5Z", fill: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M2.75 12.5C2.75 11.9477 3.19772 11.5 3.75 11.5V11.5C4.30228 11.5 4.75 11.9477 4.75 12.5V12.5C4.75 13.0523 4.30228 13.5 3.75 13.5V13.5C3.19772 13.5 2.75 13.0523 2.75 12.5V12.5Z", stroke: "currentColor", strokeWidth: "3" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M13 12.5C13 11.9477 12.5523 11.5 12 11.5V11.5C11.4477 11.5 11 11.9477 11 12.5V12.5C11 13.0523 11.4477 13.5 12 13.5V13.5C12.5523 13.5 13 13.0523 13 12.5V12.5Z", stroke: "currentColor", strokeWidth: "3" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M19.25 12.5C19.25 11.9477 19.6977 11.5 20.25 11.5V11.5C20.8023 11.5 21.25 11.9477 21.25 12.5V12.5C21.25 13.0523 20.8023 13.5 20.25 13.5V13.5C19.6977 13.5 19.25 13.0523 19.25 12.5V12.5Z", stroke: "currentColor", strokeWidth: "3" })
] });
const WrenchIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M14.5 16C18.0899 16 21 13.0899 21 9.5C21 8.75038 20.8731 8.03041 20.6396 7.36037L18 10C16.8954 11.1046 15.1046 11.1046 14 10C12.8954 8.89543 12.8954 7.10457 14 6L16.6396 3.36037C15.9696 3.12689 15.2496 3 14.5 3C10.9101 3 8 5.91015 8 9.5C8 10.3864 8.17743 11.2314 8.4987 12.0013L3 17.5L6.5 21L11.9987 15.5013C12.7686 15.8226 13.6136 16 14.5 16Z", stroke: "currentColor", strokeWidth: "2" }) });
const SidebarLeftIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsx("path", { d: "M2.25 3.75018H15.75V14.2502H2.25V3.75018Z", stroke: "currentColor" }),
  " ",
  /* @__PURE__ */ jsx("path", { d: "M6.75 3.75018V9.00018V14.2502", stroke: "currentColor" })
] });
const ChatSquareIcon = ({ className, ...props }) => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: /* @__PURE__ */ jsx("path", { d: "M3.00183 3.99999H21.0018V18H12.0018L7.00184 21V18H3.00183V3.99999Z", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "square" }) });
const TransformWandIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className, ...props, children: [
  /* @__PURE__ */ jsxs("g", { transform: "translate(3,3) scale(1.8)", children: [
    " ",
    /* @__PURE__ */ jsxs("g", { clipPath: "url(#clip0_1431_37)", children: [
      " ",
      /* @__PURE__ */ jsxs("mask", { id: "path-1-inside-1_1431_37", fill: "white", children: [
        " ",
        /* @__PURE__ */ jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8.99989 1.54289L10.957 3.5L2.99989 11.4571L1.04279 9.5L8.99989 1.54289ZM7.832 4.125L8.37489 4.66789L9.54278 3.5L8.99989 2.95711L7.832 4.125ZM7.66778 5.375L7.12489 4.83211L2.457 9.5L2.99989 10.0429L7.66778 5.375Z" }),
        " ",
        /* @__PURE__ */ jsx("path", { d: "M4.99989 1.5L5.49989 0.5L5.99989 1.5L6.99989 2L5.99989 2.5L5.49989 3.5L4.99989 2.5L3.99989 2L4.99989 1.5Z" }),
        " ",
        /* @__PURE__ */ jsx("path", { d: "M9.49989 7L9.99989 6L10.4999 7L11.4999 7.5L10.4999 8L9.99989 9L9.49989 8L8.49989 7.5L9.49989 7Z" }),
        " ",
        /* @__PURE__ */ jsx("path", { d: "M2.49989 4L2.99989 3L3.49989 4L4.49989 4.5L3.49989 5L2.99989 6L2.49989 5L1.49989 4.5L2.49989 4Z" }),
        " "
      ] }),
      " ",
      /* @__PURE__ */ jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8.99989 1.54289L10.957 3.5L2.99989 11.4571L1.04279 9.5L8.99989 1.54289ZM7.832 4.125L8.37489 4.66789L9.54278 3.5L8.99989 2.95711L7.832 4.125ZM7.66778 5.375L7.12489 4.83211L2.457 9.5L2.99989 10.0429L7.66778 5.375Z", fill: "currentColor" }),
      " ",
      /* @__PURE__ */ jsx("path", { d: "M4.99989 1.5L5.49989 0.5L5.99989 1.5L6.99989 2L5.99989 2.5L5.49989 3.5L4.99989 2.5L3.99989 2L4.99989 1.5Z", fill: "currentColor" }),
      " ",
      /* @__PURE__ */ jsx("path", { d: "M9.49989 7L9.99989 6L10.4999 7L11.4999 7.5L10.4999 8L9.99989 9L9.49989 8L8.49989 7.5L9.49989 7Z", fill: "currentColor" }),
      " ",
      /* @__PURE__ */ jsx("path", { d: "M2.49989 4L2.99989 3L3.49989 4L4.49989 4.5L3.49989 5L2.99989 6L2.49989 5L1.49989 4.5L2.49989 4Z", fill: "currentColor" }),
      " ",
      /* @__PURE__ */ jsx("path", { d: "M8.99989 1.54289L9.707 0.835786L8.99989 0.128679L8.29278 0.835786L8.99989 1.54289ZM10.957 3.5L11.6641 4.20711L12.3712 3.5L11.6641 2.79289L10.957 3.5ZM2.99989 11.4571L2.29279 12.1642L2.99989 12.8713L3.707 12.1642L2.99989 11.4571ZM1.04279 9.5L0.335679 8.79289L-0.371428 9.5L0.335679 10.2071L1.04279 9.5ZM8.37489 4.66789L7.66778 5.375L8.37489 6.08211L9.082 5.375L8.37489 4.66789ZM7.832 4.125L7.12489 3.41789L6.41778 4.125L7.12489 4.83211L7.832 4.125ZM9.54278 3.5L10.2499 4.20711L10.957 3.5L10.2499 2.79289L9.54278 3.5ZM8.99989 2.95711L9.707 2.25L8.99989 1.54289L8.29278 2.25L8.99989 2.95711ZM7.12489 4.83211L7.832 4.125L7.12489 3.41789L6.41778 4.125L7.12489 4.83211ZM7.66778 5.375L8.37489 6.08211L9.082 5.375L8.37489 4.66789L7.66778 5.375ZM2.457 9.5L1.74989 8.79289L1.04279 9.5L1.74989 10.2071L2.457 9.5ZM2.99989 10.0429L2.29279 10.75L2.99989 11.4571L3.707 10.75L2.99989 10.0429ZM4.99989 1.5L5.44711 2.39443L5.89432 1.94721L4.99989 1.5ZM5.49989 0.5L6.39432 0.0527864L4.60546 0.0527866L5.49989 0.5ZM5.99989 1.5L5.10546 1.94721L5.55268 2.39443L5.99989 1.5ZM6.99989 2L7.4471 2.89443V1.10557L6.99989 2ZM5.99989 2.5L5.55268 1.60557L5.10546 2.05279L5.99989 2.5ZM5.49989 3.5L4.60546 3.94721L6.39432 3.94721L5.49989 3.5ZM4.99989 2.5L5.89432 2.05279L5.44711 1.60557L4.99989 2.5ZM3.99989 2L3.55268 1.10557V2.89443L3.99989 2ZM9.49989 7L9.9471 7.89443L10.3943 7.44721L9.49989 7ZM9.99989 6L10.8943 5.55279H9.10546L9.99989 6ZM10.4999 7L9.60546 7.44721L10.0527 7.89443L10.4999 7ZM11.4999 7.5L11.9471 8.39443V6.60557L11.4999 7.5ZM10.4999 8L10.0527 7.10557L9.60546 7.55279L10.4999 8ZM9.99989 9L9.10546 9.44721H10.8943L9.99989 9ZM9.49989 8L10.3943 7.55279L9.9471 7.10557L9.49989 8ZM8.49989 7.5L8.05268 6.60557V8.39443L8.49989 7.5ZM2.49989 4L2.94711 4.89443L3.39432 4.44721L2.49989 4ZM2.99989 3L3.89432 2.55279H2.10547L2.99989 3ZM3.49989 4L2.60546 4.44721L3.05268 4.89443L3.49989 4ZM4.49989 4.5L4.94711 5.39443V3.60557L4.49989 4.5ZM3.49989 5L3.05268 4.10557L2.60546 4.55279L3.49989 5ZM2.99989 6L2.10547 6.44721H3.89432L2.99989 6ZM2.49989 5L3.39432 4.55279L2.94711 4.10557L2.49989 5ZM1.49989 4.5L1.05268 3.60557V5.39443L1.49989 4.5ZM8.37489 4.66789L9.082 3.96079L8.5391 3.41789L7.832 4.125L7.12489 4.83211L7.66778 5.375L8.37489 4.66789ZM9.54278 3.5L8.83568 2.79289L7.66778 3.96079L8.37489 4.66789L9.082 5.375L10.2499 4.20711L9.54278 3.5ZM8.99989 2.95711L8.29278 3.66421L8.83568 4.20711L9.54278 3.5L10.2499 2.79289L9.707 2.25L8.99989 2.95711ZM7.832 4.125L8.5391 4.83211L9.707 3.66421L8.99989 2.95711L8.29278 2.25L7.12489 3.41789L7.832 4.125ZM7.12489 4.83211L6.41778 5.53921L6.96068 6.08211L7.66778 5.375L8.37489 4.66789L7.832 4.125L7.12489 4.83211ZM2.457 9.5L3.16411 10.2071L7.832 5.53921L7.12489 4.83211L6.41778 4.125L1.74989 8.79289L2.457 9.5ZM2.99989 10.0429L3.707 9.33579L3.16411 8.79289L2.457 9.5L1.74989 10.2071L2.29279 10.75L2.99989 10.0429ZM7.66778 5.375L6.96068 4.66789L2.29279 9.33579L2.99989 10.0429L3.707 10.75L8.37489 6.08211L7.66778 5.375ZM10.957 3.5L11.6641 2.79289L9.707 0.835786L8.99989 1.54289L8.29278 2.25L10.2499 4.20711L10.957 3.5ZM2.99989 11.4571L3.707 12.1642L11.6641 4.20711L10.957 3.5L10.2499 2.79289L2.29279 10.75L2.99989 11.4571ZM1.04279 9.5L0.335679 10.2071L2.29279 12.1642L2.99989 11.4571L3.707 10.75L1.74989 8.79289L1.04279 9.5ZM8.99989 1.54289L8.29278 0.835786L0.335679 8.79289L1.04279 9.5L1.74989 10.2071L9.707 2.25L8.99989 1.54289ZM4.99989 1.5L5.89432 1.94721L6.39432 0.947213L5.49989 0.5L4.60546 0.0527866L4.10546 1.05279L4.99989 1.5ZM5.49989 0.5L4.60546 0.947214L5.10546 1.94721L5.99989 1.5L6.89432 1.05279L6.39432 0.0527864L5.49989 0.5ZM5.99989 1.5L5.55268 2.39443L6.55268 2.89443L6.99989 2L7.4471 1.10557L6.44711 0.605573L5.99989 1.5ZM6.99989 2L6.55268 1.10557L5.55268 1.60557L5.99989 2.5L6.44711 3.39443L7.4471 2.89443L6.99989 2ZM5.99989 2.5L5.10546 2.05279L4.60546 3.05279L5.49989 3.5L6.39432 3.94721L6.89432 2.94721L5.99989 2.5ZM5.49989 3.5L6.39432 3.05279L5.89432 2.05279L4.99989 2.5L4.10546 2.94721L4.60546 3.94721L5.49989 3.5ZM4.99989 2.5L5.44711 1.60557L4.44711 1.10557L3.99989 2L3.55268 2.89443L4.55268 3.39443L4.99989 2.5ZM3.99989 2L4.44711 2.89443L5.44711 2.39443L4.99989 1.5L4.55268 0.605573L3.55268 1.10557L3.99989 2ZM9.49989 7L10.3943 7.44721L10.8943 6.44721L9.99989 6L9.10546 5.55279L8.60546 6.55279L9.49989 7ZM9.99989 6L9.10546 6.44721L9.60546 7.44721L10.4999 7L11.3943 6.55279L10.8943 5.55279L9.99989 6ZM10.4999 7L10.0527 7.89443L11.0527 8.39443L11.4999 7.5L11.9471 6.60557L10.9471 6.10557L10.4999 7ZM11.4999 7.5L11.0527 6.60557L10.0527 7.10557L10.4999 8L10.9471 8.89443L11.9471 8.39443L11.4999 7.5ZM10.4999 8L9.60546 7.55279L9.10546 8.55279L9.99989 9L10.8943 9.44721L11.3943 8.44721L10.4999 8ZM9.99989 9L10.8943 8.55279L10.3943 7.55279L9.49989 8L8.60546 8.44721L9.10546 9.44721L9.99989 9ZM9.49989 8L9.9471 7.10557L8.9471 6.60557L8.49989 7.5L8.05268 8.39443L9.05268 8.89443L9.49989 8ZM8.49989 7.5L8.9471 8.39443L9.9471 7.89443L9.49989 7L9.05268 6.10557L8.05268 6.60557L8.49989 7.5ZM2.49989 4L3.39432 4.44721L3.89432 3.44721L2.99989 3L2.10547 2.55279L1.60547 3.55279L2.49989 4ZM2.99989 3L2.10547 3.44721L2.60546 4.44721L3.49989 4L4.39432 3.55279L3.89432 2.55279L2.99989 3ZM3.49989 4L3.05268 4.89443L4.05268 5.39443L4.49989 4.5L4.94711 3.60557L3.94711 3.10557L3.49989 4ZM4.49989 4.5L4.05268 3.60557L3.05268 4.10557L3.49989 5L3.94711 5.89443L4.94711 5.39443L4.49989 4.5ZM3.49989 5L2.60546 4.55279L2.10547 5.55279L2.99989 6L3.89432 6.44721L4.39432 5.44721L3.49989 5ZM2.99989 6L3.89432 5.55279L3.39432 4.55279L2.49989 5L1.60547 5.44721L2.10547 6.44721L2.99989 6ZM2.49989 5L2.94711 4.10557L1.94711 3.60557L1.49989 4.5L1.05268 5.39443L2.05268 5.89443L2.49989 5ZM1.49989 4.5L1.94711 5.39443L2.94711 4.89443L2.49989 4L2.05268 3.10557L1.05268 3.60557L1.49989 4.5Z", fill: "#646464", mask: "url(#path-1-inside-1_1431_37)" }),
      " "
    ] }),
    " "
  ] }),
  " ",
  /* @__PURE__ */ jsxs("defs", { children: [
    " ",
    /* @__PURE__ */ jsxs("clipPath", { id: "clip0_1431_37", children: [
      " ",
      /* @__PURE__ */ jsx("rect", { width: "12", height: "12", fill: "white", transform: "translate(0 0.5)" }),
      " "
    ] }),
    " "
  ] })
] });
const customIconComponents = {
  "analytics": AnalyticsIcon,
  "app": AppIcon,
  "arrow-box-left": ArrowBoxLeftIcon,
  "arrow-up": ArrowUpIcon,
  "assign": AssignIcon,
  "at": AtIcon,
  "blur": BlurIcon,
  "brain": BrainIcon,
  "broom-cleaning": BroomCleaningIcon,
  "commit": CommitIcon,
  "copy-squares": CopySquaresIcon,
  "chat-plus": ChatPlusIcon,
  "check": CheckIcon,
  "chevron-bottom-small": ChevronBottomSmallIcon,
  "data-clip": DataClipIcon,
  "date-weekly": DateWeeklyIcon,
  "dot-grid-2-3": DotGrid23Icon,
  "dots-grid-2x3": DotsGrid2x3Icon,
  "expand-simple": ExpandSimpleIcon,
  "expand": ExpandIcon,
  "external-link": ExternalLinkIcon,
  "first-available": FirstAvailableIcon,
  "flatfile-logo": FlatfileLogoIcon,
  "flower": FlowerIcon,
  "focus-square": FocusSquareIcon,
  "help-circle": HelpCircleIcon,
  "high-priority": HighPriorityIcon,
  "layout-window": LayoutWindowIcon,
  "library": LibraryIcon,
  "list": ListIcon,
  "list-sparkle": ListSparkleIcon,
  "list-bullets": ListBulletsIcon,
  "low-priority": LowPriorityIcon,
  "magic-book": MagicBookIcon,
  "medium-priority": MediumPriorityIcon,
  "merge": MergeIcon,
  "minimize": MinimizeIcon,
  "paperclip": PaperclipIcon,
  "pause": PauseIcon,
  "prepare-vector": PrepareVectorIcon,
  "refresh": RefreshIcon,
  "save": SaveIcon,
  "sidebar-right-arrow": SidebarRightArrowIcon,
  "sidebar-right-arrow-thin": SidebarRightArrowThinIcon,
  "single-value": SingleValueIcon,
  "sparkles-three": SparklesThreeIcon,
  "split": SplitIcon,
  "split-down": SplitDownIcon,
  "square-behind-square": SquareBehindSquareIcon,
  "status-checkmark-circle": StatusCheckmarkCircleIcon,
  "status-circle": StatusCircleIcon,
  "status-dotted-circle": StatusDottedCircleIcon,
  "status-half-circle": StatusHalfCircleIcon,
  "status-warning-circle": StatusWarningCircleIcon,
  "status-warning-triangle": StatusWarningTriangleIcon,
  "status-x-circle": StatusXCircleIcon,
  "stop-circle": StopCircleIcon,
  "string": StringIcon,
  "three-dots-horizontal": ThreeDotsHorizontalIcon,
  "wrench": WrenchIcon,
  "sidebar-left": SidebarLeftIcon,
  "chat-square": ChatSquareIcon,
  "transform-wand": TransformWandIcon
};
const sizeMap = {
  12: "size-3 text-[12px]",
  16: "size-4 text-[16px]",
  18: "size-[18px] text-[18px]"
};
const Icon = ({ className, ...props }) => {
  const { name, color, size = 16, variant = "outline", ...svgProps } = props;
  const mappedLegacyName = LEGACY_ICON_MAP[name];
  const isCustom = CUSTOM_LIST.includes(name);
  const colorOverride = color ? `text-${color}` : "";
  if (isCustom) {
    const CustomSVGIcon = customIconComponents[name];
    if (CustomSVGIcon) {
      return /* @__PURE__ */ jsx(
        CustomSVGIcon,
        {
          className: cn(colorOverride, sizeMap[size], className),
          ...svgProps
        }
      );
    }
  }
  const weight = size === 18 ? "normal" : "thin";
  const getCentralIconComponent = () => {
    if (weight === "normal" && variant === "filled") return CentralIcon;
    if (weight === "normal" && variant === "outline") return CentralIcon$1;
    if (weight === "thin" && variant === "filled") return CentralIcon$2;
    if (weight === "thin" && variant === "outline") return CentralIcon$3;
    return CentralIcon$3;
  };
  const CentralIconComponent = getCentralIconComponent();
  return /* @__PURE__ */ jsx(
    CentralIconComponent,
    {
      name: mappedLegacyName ?? name,
      className: cn(colorOverride, className),
      size
    }
  );
};
const SubmitButton = ({ disabled, ...props }) => {
  return /* @__PURE__ */ jsx(
    "button",
    {
      className: cn(
        "flex size-7 items-center justify-center rounded border shrink-0 transition-colors",
        {
          "border-transparent bg-black-opacity-1 text-base-white": !disabled,
          "border-input text-input bg-inherit": disabled
        }
      ),
      ...props,
      children: /* @__PURE__ */ jsx(Icon, { name: "arrow-up", size: 12 })
    }
  );
};
const NON_BREAKING_SPACE = " ";
const valueToHTML = (value) => {
  const html = value.split("\n").reduce((memo, text, index, arr) => {
    memo.push(text.replace(/(\s+)$/g, NON_BREAKING_SPACE));
    if (index !== arr.length - 1) {
      memo.push(/* @__PURE__ */ jsx("br", {}));
    }
    return memo;
  }, []);
  if (html.length === 1 && html[0] === "") {
    return [NON_BREAKING_SPACE];
  }
  return html;
};
function MessageInput({
  tidPlaceholder,
  actionSlot,
  onSubmit
}) {
  const { t } = useDefaultTranslation();
  const [htmlValue, setHTMLValue] = useState([
    NON_BREAKING_SPACE
  ]);
  const [height, setHeight] = useState(null);
  const ref = useRef(null);
  const cloneRef = useRef(null);
  const [value, setValue] = useState("");
  const handleSubmit = (e) => {
    e == null ? void 0 : e.preventDefault();
    if (value.trim()) {
      onSubmit(value.trim());
      setValue("");
      setHTMLValue([NON_BREAKING_SPACE]);
    }
  };
  const onKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };
  const onChange = (event) => {
    const newValue = event.target.value;
    const newHTMLValue = valueToHTML(newValue);
    setHTMLValue(newHTMLValue);
    setValue(newValue);
  };
  useEffect(() => {
    if (cloneRef.current) {
      const ro = new ResizeObserver((entries) => {
        const entry = entries[0];
        setHeight(entry.borderBoxSize[0].blockSize);
      });
      ro.observe(cloneRef.current);
      return () => ro.disconnect();
    }
  }, []);
  return /* @__PURE__ */ jsx("form", { onSubmit: handleSubmit, className: "w-full", children: /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn("bg-base-white w-full", {
        "flex flex-col gap-2": actionSlot
      }),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-end gap-3", children: [
          /* @__PURE__ */ jsx(
            "textarea",
            {
              ref,
              className: "w-80 grow resize-none overflow-hidden border-none bg-inherit text-md outline-none transition-all",
              placeholder: t(tidPlaceholder, "Add your comment here…"),
              value,
              onChange,
              onKeyDown,
              style: {
                height: `${height}px`
              }
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              ref: cloneRef,
              className: "pointer-events-none invisible absolute w-80 overflow-hidden py-1.5 pl-1.5 text-md",
              children: createElement(Fragment, {}, ...htmlValue)
            }
          ),
          !actionSlot && /* @__PURE__ */ jsx(SubmitButton, { disabled: !value.trim() })
        ] }),
        actionSlot && /* @__PURE__ */ jsxs("div", { className: "flex items-end gap-2", children: [
          /* @__PURE__ */ jsx("div", { className: "grow", children: actionSlot }),
          /* @__PURE__ */ jsx(SubmitButton, { disabled: !value.trim() })
        ] })
      ]
    }
  ) });
}
const ChatInput = ({
  className,
  tidPlaceholder,
  onSubmit,
  context,
  onRemoveContext
}) => {
  const actions = useMemo(() => {
    return /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
      /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "icon-md", icon: "paperclip" }),
      /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "icon-md", icon: "at" })
    ] });
  }, []);
  const contextSlot = useMemo(() => {
    if (!context) return null;
    return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 bg-surface-base px-3 py-2", children: [
      /* @__PURE__ */ jsx(Icon, { name: "cursor-list", className: "shrink-0" }),
      /* @__PURE__ */ jsx(Text$1, { variant: "callout", className: "grow truncate", children: context }),
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: onRemoveContext,
          className: "shrink-0 rounded-full text-muted hover:text-text-title",
          children: /* @__PURE__ */ jsx(Icon, { name: "circle-x", variant: "outline" })
        }
      )
    ] });
  }, [context, onRemoveContext]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex flex-col items-end rounded-lg border border-border bg-base-white overflow-hidden",
        className
      ),
      children: [
        /* @__PURE__ */ jsx(AnimatePresence, { children: contextSlot && /* @__PURE__ */ jsx(
          motion.div,
          {
            className: "w-full",
            initial: slideDownInitial,
            animate: slideDownAnimate,
            exit: slideDownInitial,
            children: contextSlot
          }
        ) }),
        /* @__PURE__ */ jsx("div", { className: "w-full bg-base-white p-2", children: /* @__PURE__ */ jsx(
          MessageInput,
          {
            tidPlaceholder,
            onSubmit,
            actionSlot: actions
          }
        ) })
      ]
    }
  );
};
const slideDownInitial = {
  // opacity: 0,
  height: 0
};
const slideDownAnimate = {
  // opacity: 1,
  height: "auto"
};
const spinnerVariants = cva("", {
  variants: {
    size: {
      8: "size-2 text-8",
      10: "size-2.5 text-10",
      12: "size-3 text-12",
      16: "size-4 text-16",
      18: "size-[18px] text-18",
      20: "size-5 text-20",
      24: "size-6 text-24",
      32: "size-8 text-32"
    },
    variant: {
      primary: "fill-primary text-icon-strong",
      destructive: "fill-destructive text-surface-sentiment-subtle-destructive"
    }
  },
  defaultVariants: {
    size: 16,
    variant: "primary"
  }
});
const Spinner = ({ className, variant, size = 24 }) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      className: cn(spinnerVariants({ size }), "animate-spin", className),
      "data-testid": "spinner-icon",
      role: "img",
      viewBox: "0 0 24 24",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx("title", { children: "Loading spinner" }),
        /* @__PURE__ */ jsx(
          "path",
          {
            className: "fill-gray-4",
            fillRule: "evenodd",
            d: "M12 19a7 7 0 100-14 7 7 0 000 14zm0 3c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z",
            clipRule: "evenodd",
            opacity: "0.5"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            className: cn(spinnerVariants({ variant })),
            d: "M2 12C2 6.477 6.477 2 12 2v3a7 7 0 00-7 7H2z"
          }
        )
      ]
    }
  );
};
const variantStyles$1 = {
  primary: "bg-primary hover:bg-interactive-fill-primary-hover text-text-inverted-title disabled:bg-surface-sentiment-subtle-neutral disabled:text-text-inverted-muted disabled:hover:bg-surface-sentiment-subtle-neutral",
  destructive: "bg-destructive text-text-inverted-title border border-gray-1 hover:bg-destructive-90 disabled:bg-surface-base-elevated disabled:text-surface-sentiment-subtle-destructive disabled:border-surface-sentiment-subtle-destructive disabled:hover:bg-surface-base-elevated",
  warning: "bg-warning disabled:bg-warning-30 hover:bg-warning-70 disabled:hover:bg-warning-30",
  outline: "bg-transparent border border-input hover:bg-interactive-fill-secondary-hover disabled:text-text-inverted-muted disabled:border-input disabled:bg-overlay-darken-300 disabled:hover:bg-overlay-darken-300 disabled:hover:border-input",
  "outline-destructive": "text-surface-sentiment-destructive border border-black-opacity-7 hover:bg-interactive-fill-secondary-hover disabled:text-surface-sentiment-subtle-destructive disabled:border-interactive-fill-destructive-hover disabled:hover:bg-transparent disabled:hover:border-interactive-fill-destructive-hover",
  secondary: "bg-secondary text-primary border hover:bg-overlay-lighten-900 disabled:bg-overlay-lighten-800 disabled:text-text-inverted-muted disabled:hover:bg-overlay-lighten-800 disabled:hover:border",
  ghost: "text-text-default border-none hover:bg-interactive-fill-secondary-hover disabled:text-text-inverted-muted disabled:hover:bg-transparent",
  "ghost-destructive": "text-surface-sentiment-destructive hover:bg-transparent disabled:text-surface-sentiment-subtle-destructive",
  "ghost-affirmative": "text-green-9 hover:bg-transparent disabled:text-green-5",
  link: "text-primary underline-offset-4 hover:underline disabled:hover:no-underline"
};
const buttonSizes = {
  md: "gap-1.5 rounded-sm px-3.5 py-1.5 text-base font-medium",
  sm: "gap-1.5 rounded-sm px-3 py-1 text-base font-medium",
  lg: "gap-1.5 rounded-md px-3 py-2.5 text-base font-medium",
  "icon-sm": "size-4 border-transparent rounded-xs flex-shrink-0",
  "icon-md": "size-7 border-transparent rounded-sm flex-shrink-0",
  "icon-lg": "size-8 border-transparent rounded-sm flex-shrink-0"
};
const activeStyles = {
  true: "",
  false: ""
};
const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
  {
    variants: {
      variant: variantStyles$1,
      size: buttonSizes,
      active: activeStyles
    },
    compoundVariants: [
      {
        variant: "primary",
        active: true,
        class: "bg-interactive-fill-primary-hover"
      },
      {
        variant: "primary",
        active: false,
        class: "bg-primary"
      },
      {
        variant: "destructive",
        active: true,
        class: "bg-destructive-90"
      },
      {
        variant: "destructive",
        active: false,
        class: "bg-destructive"
      },
      {
        variant: "warning",
        active: true,
        class: "bg-warning-70"
      },
      {
        variant: "warning",
        active: false,
        class: "bg-warning"
      },
      {
        variant: "outline",
        active: true,
        class: "border-transparent bg-interactive-fill-secondary-hover"
      },
      {
        variant: "outline",
        active: false,
        class: "bg-transparent"
      },
      {
        variant: "outline-destructive",
        active: true,
        class: "bg-interactive-fill-secondary-hover"
      },
      {
        variant: "outline-destructive",
        active: false,
        class: ""
      },
      {
        variant: "secondary",
        active: true,
        class: "border-transparent bg-overlay-lighten-800"
      },
      {
        variant: "secondary",
        active: false,
        class: "bg-secondary"
      },
      {
        variant: "ghost",
        active: true,
        class: "bg-overlay-darken-300 text-text-default"
      }
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
      active: void 0
    }
  }
);
const iconSizeMap = {
  md: 16,
  sm: 16,
  lg: 18,
  "icon-sm": 12,
  "icon-md": 16,
  "icon-lg": 18
};
const Button = React.forwardRef(
  ({
    active = false,
    asChild = false,
    children,
    className,
    icon,
    iconPlacement = "left",
    iconProps,
    loading,
    size,
    slotLeft,
    slotRight,
    variant,
    disableTheming,
    ...props
  }, ref) => {
    const Comp = asChild ? Slot : "button";
    const buttonSize = size ?? "md";
    const rightIcon = icon && iconPlacement === "right";
    const leftIcon = icon && iconPlacement === "left";
    const overrideTargetId = `ff_baseline_${variant ?? "primary"}_button`;
    return /* @__PURE__ */ jsxs(
      Comp,
      {
        "data-override-targetid": overrideTargetId,
        className: cn(
          buttonVariants({ variant, size, className, active }),
          "disabled:pointer-events-auto",
          disableTheming && "theme-static"
        ),
        ref,
        ...props,
        children: [
          leftIcon && /* @__PURE__ */ jsx(
            Icon,
            {
              className: cn(
                "pointer-events-none",
                disableTheming && "theme-static"
              ),
              name: icon,
              variant: "outline",
              size: iconSizeMap[buttonSize],
              ...iconProps
            }
          ),
          loading && leftIcon && /* @__PURE__ */ jsx(Spinner, { size: iconSizeMap[buttonSize], variant }),
          slotLeft,
          children,
          slotRight,
          rightIcon && !loading && /* @__PURE__ */ jsx(
            Icon,
            {
              className: cn(
                "pointer-events-none",
                disableTheming && "theme-static"
              ),
              name: icon,
              variant: "outline",
              size: iconSizeMap[buttonSize],
              ...iconProps
            }
          ),
          loading && !leftIcon && /* @__PURE__ */ jsx(
            Spinner,
            {
              className: cn(disableTheming && "theme-static"),
              size: iconSizeMap[buttonSize],
              variant
            }
          )
        ]
      }
    );
  }
);
Button.displayName = "Button";
const variantStyles = {
  largeTitle: "text-3xl",
  title1: "text-2xl",
  title2: "text-xl",
  title3: "text-lg",
  headline: "text-base",
  body: "text-base",
  callout: "text-sm",
  subheadline: "text-xs",
  footnote: "text-xxs",
  caption1: "text-xxs",
  caption2: "text-xxs",
  link: "text-base underline"
};
const textVariants = cva("font-sans", {
  variants: {
    variant: variantStyles,
    emphasized: {
      false: "font-normal",
      true: ""
    }
  },
  compoundVariants: [
    {
      variant: "largeTitle",
      emphasized: true,
      class: "font-bold"
    },
    {
      variant: "title1",
      emphasized: true,
      class: "font-bold"
    },
    {
      variant: "title2",
      emphasized: true,
      class: "font-bold"
    },
    {
      variant: "title3",
      emphasized: true,
      class: "font-medium"
    },
    {
      variant: "headline",
      emphasized: true,
      class: "font-bold"
    },
    {
      variant: "body",
      emphasized: true,
      class: "font-medium"
    },
    {
      variant: "callout",
      emphasized: true,
      class: "font-medium"
    },
    {
      variant: "subheadline",
      emphasized: true,
      class: "font-medium"
    },
    {
      variant: "footnote",
      emphasized: true,
      class: "font-medium"
    },
    {
      variant: "caption1",
      emphasized: true,
      class: "font-medium"
    },
    {
      variant: "caption2",
      emphasized: false,
      class: "font-medium"
    },
    {
      variant: "caption2",
      emphasized: true,
      class: "font-bold"
    }
  ],
  defaultVariants: {
    variant: "body"
  }
});
const Text$1 = React.forwardRef(
  ({
    as = "span",
    children,
    className,
    color,
    emphasized = false,
    tid,
    values,
    variant,
    ...props
  }, ref) => {
    const Comp = as;
    const { t } = useDefaultTranslation();
    const colorOverride = color ? `text-${color}` : "";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        ref,
        className: cn(
          textVariants({ variant, className, emphasized }),
          colorOverride
        ),
        ...props,
        children: t(tid, children, values)
      }
    );
  }
);
Text$1.displayName = "Text";
const Dialog = DialogPrimitive.Root;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;
const DialogCloseButton = React.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(DialogClose, { asChild: true, ref, children: /* @__PURE__ */ jsx(Button, { variant: "secondary", ...props, children: children || "Close" }) }));
DialogCloseButton.displayName = "DialogCloseButton";
const DialogTrigger = React.forwardRef(({ children }, ref) => /* @__PURE__ */ jsx(DialogPrimitive.Trigger, { ref, asChild: true, children }));
DialogTrigger.displayName = DialogPrimitive.Trigger.displayName;
const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn(
      className,
      !(className == null ? void 0 : className.includes("absolute")) && "fixed",
      "inset-0 z-50 backdrop-blur data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
    ),
    ...props
  }
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
const DialogContent = React.forwardRef(
  ({
    className,
    children,
    disablePortal = false,
    fullscreen = false,
    ...props
  }, ref) => {
    const regularAnimationClasses = "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]";
    const fullscreenAnimationClasses = "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-right-full";
    const content = /* @__PURE__ */ jsxs(Fragment$1, { children: [
      /* @__PURE__ */ jsx(
        DialogOverlay,
        {
          className: cn(
            disablePortal && "absolute",
            fullscreen && "bg-popover"
          )
        }
      ),
      /* @__PURE__ */ jsx(
        DialogPrimitive.Content,
        {
          ref,
          className: cn(
            "shadow-dialog break-word-legacy fixed z-50 grid gap-4 border border-gray-3 bg-popover p-6 duration-200",
            fullscreen ? fullscreenAnimationClasses : regularAnimationClasses,
            fullscreen ? "inset-0 w-full h-full border-0 shadow-none" : "min-w-0 left-[50%] top-[50%] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] sm:rounded-lg",
            // Regular dialog styles
            "focus:outline-none",
            className
          ),
          ...props,
          children
        }
      )
    ] });
    return disablePortal ? content : /* @__PURE__ */ jsx(DialogPortal, { children: content });
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;
const DialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx("div", { className: cn("flex flex-col space-y-1.5", className), ...props });
DialogHeader.displayName = "DialogHeader";
const DialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
DialogFooter.displayName = "DialogFooter";
const DialogTitle = React.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx(DialogPrimitive.Title, { ref, children: /* @__PURE__ */ jsx(Text$1, { variant: "title3", emphasized: true, ...props }) }));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
const DialogDescription = React.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx(DialogPrimitive.Description, { ref, children: /* @__PURE__ */ jsx(Text$1, { variant: "body", className: "text-gray-11", ...props }) }));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
const BoundedDialogContainer = React.forwardRef(({ children, className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("relative h-full", className), ...props, children }));
BoundedDialogContainer.displayName = "BoundedDialogContainer";
const Command = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  Command$1,
  {
    ref,
    className: cn(
      "flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground",
      className
    ),
    ...props
  }
));
Command.displayName = Command$1.displayName;
const CommandInput = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsxs("div", { className: "flex items-center border-b px-3", "cmdk-input-wrapper": "", children: [
  /* @__PURE__ */ jsx(MagnifyingGlassIcon, { className: "mr-2 size-4 shrink-0 opacity-50" }),
  /* @__PURE__ */ jsx(
    Command$1.Input,
    {
      ref,
      className: cn(
        "flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-text-default disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ...props
    }
  )
] }));
CommandInput.displayName = Command$1.Input.displayName;
const CommandList = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  Command$1.List,
  {
    ref,
    className: cn("max-h-[300px] overflow-y-auto overflow-x-hidden", className),
    ...props
  }
));
CommandList.displayName = Command$1.List.displayName;
const CommandEmpty = React.forwardRef((props, ref) => /* @__PURE__ */ jsx(
  Command$1.Empty,
  {
    ref,
    className: "py-6 text-center text-sm",
    ...props
  }
));
CommandEmpty.displayName = Command$1.Empty.displayName;
const CommandGroup = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  Command$1.Group,
  {
    ref,
    className: cn(
      "overflow-hidden p-1 text-text-title [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-text-default",
      className
    ),
    ...props
  }
));
CommandGroup.displayName = Command$1.Group.displayName;
const CommandSeparator = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  Command$1.Separator,
  {
    ref,
    className: cn("-mx-1 h-px bg-border", className),
    ...props
  }
));
CommandSeparator.displayName = Command$1.Separator.displayName;
const CommandItem = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  Command$1.Item,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-overlay-darken-300 data-[selected=true]:text-text-weak data-[disabled=true]:opacity-50",
      className
    ),
    ...props
  }
));
CommandItem.displayName = Command$1.Item.displayName;
const Popover = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;
const PopoverAnchor = PopoverPrimitive.Anchor;
const PopoverContent = React.forwardRef(({ className, align = "center", sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx(
  PopoverPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 max-h-[80vh] overflow-y-auto",
      className
    ),
    ...props
  }
) }));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;
const Separator = React.forwardRef(
  ({ className, orientation = "horizontal", decorative = true, ...props }, ref) => /* @__PURE__ */ jsx(
    SeparatorPrimitive.Root,
    {
      ref,
      decorative,
      orientation,
      className: cn(
        "shrink-0 bg-border",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className
      ),
      ...props
    }
  )
);
Separator.displayName = SeparatorPrimitive.Root.displayName;
const useTriggerWidth = (isPopoverOpen, ref) => {
  const [popoverWidth, setPopoverWidth] = useState(0);
  useEffect(() => {
    if (isPopoverOpen && ref.current) {
      setPopoverWidth(ref.current.offsetWidth);
    }
  }, [isPopoverOpen]);
  return { width: popoverWidth };
};
const DropdownInput = React.forwardRef(
  ({
    options,
    onValueChange,
    defaultValue = [],
    defaultOpen = false,
    placeholder = "Select option",
    modalPopover = true,
    asChild = false,
    className,
    value,
    matchTriggerWidth = true,
    controls = false,
    disableClear = false,
    disableSearch = false,
    variant = "default",
    icon,
    label,
    ...props
  }, ref) => {
    const rendered = React.useRef(false);
    const buttonRef = React.useRef(null);
    const mergedRef = mergeRefs(buttonRef, ref);
    const isGhost = variant === "ghost";
    const [selectedValues, setSelectedValues] = React.useState(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(defaultOpen);
    const popoverStyle = useTriggerWidth(isPopoverOpen, buttonRef);
    React.useEffect(() => {
      const valueToSet = Array.isArray(value) ? value[0] : value;
      if (valueToSet) {
        setSelectedValues([valueToSet]);
      } else if (rendered.current) {
        setSelectedValues([]);
      }
    }, [value]);
    React.useEffect(() => {
      rendered.current = true;
    }, []);
    const handleInputKeyDown = (event) => {
      if (event.key === "Enter") {
        setIsPopoverOpen(true);
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };
    const toggleOption = (option) => {
      setSelectedValues([option]);
      onValueChange([option]);
      setIsPopoverOpen(false);
    };
    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };
    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };
    return /* @__PURE__ */ jsxs(
      Popover,
      {
        open: isPopoverOpen,
        onOpenChange: setIsPopoverOpen,
        modal: modalPopover,
        children: [
          /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
            Button,
            {
              ref: mergedRef,
              ...props,
              onClick: handleTogglePopover,
              variant: "secondary",
              icon,
              className: cn(
                "flex w-full px-2.5 py-2 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit",
                { "border-none": isGhost },
                { "pl-3": icon },
                className
              ),
              children: selectedValues.length > 0 && !label ? /* @__PURE__ */ jsxs("div", { className: "flex w-full items-center justify-between", children: [
                /* @__PURE__ */ jsx("div", { className: "flex flex-wrap items-center", children: selectedValues.slice(0, 1).map((value2, index) => {
                  const option = options.find((o) => o.value === value2);
                  return /* @__PURE__ */ jsx(
                    "span",
                    {
                      className: "mx-2 text-text-title",
                      children: option == null ? void 0 : option.label
                    },
                    `${value2}-${index}`
                  );
                }) }),
                /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
                  !disableClear && /* @__PURE__ */ jsxs(Fragment$1, { children: [
                    /* @__PURE__ */ jsx(
                      "div",
                      {
                        role: "button",
                        "aria-label": "clear",
                        onClick: (event) => {
                          event.stopPropagation();
                          handleClear();
                        },
                        children: /* @__PURE__ */ jsx(XIcon, { className: "mx-2 h-4 cursor-pointer text-text-title" })
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      Separator,
                      {
                        orientation: "vertical",
                        className: "flex h-full min-h-6"
                      }
                    )
                  ] }),
                  /* @__PURE__ */ jsx(ChevronDown, { className: "mx-2 h-4 cursor-pointer text-text-title" })
                ] })
              ] }) : /* @__PURE__ */ jsxs("div", { className: "mx-auto flex w-full items-center justify-between", children: [
                /* @__PURE__ */ jsx("span", { className: "mx-3 text-sm text-muted", children: label ?? placeholder }),
                /* @__PURE__ */ jsx(ChevronDown, { className: "mx-2 h-4 cursor-pointer text-text-title" })
              ] })
            }
          ) }),
          /* @__PURE__ */ jsx(
            PopoverContent,
            {
              className: "w-auto p-0",
              align: "start",
              onEscapeKeyDown: () => setIsPopoverOpen(false),
              style: matchTriggerWidth ? popoverStyle : void 0,
              children: /* @__PURE__ */ jsxs(Command, { children: [
                !disableSearch && /* @__PURE__ */ jsx(
                  CommandInput,
                  {
                    placeholder: "Search...",
                    onKeyDown: handleInputKeyDown
                  }
                ),
                /* @__PURE__ */ jsxs(CommandList, { children: [
                  /* @__PURE__ */ jsx(CommandEmpty, { children: "No results found." }),
                  /* @__PURE__ */ jsx(CommandGroup, { children: options.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return /* @__PURE__ */ jsxs(
                      CommandItem,
                      {
                        onSelect: () => toggleOption(option.value),
                        className: "cursor-pointer",
                        "data-testid": `option-${option.label}`,
                        children: [
                          /* @__PURE__ */ jsx(
                            "div",
                            {
                              className: cn(
                                "mr-2 flex h-4 w-4 items-center justify-center",
                                isSelected ? "" : "opacity-50 [&_svg]:invisible"
                              ),
                              children: /* @__PURE__ */ jsx(CheckIcon$1, { className: "size-4" })
                            }
                          ),
                          /* @__PURE__ */ jsx("span", { children: option.label })
                        ]
                      },
                      option.value.toString()
                    );
                  }) }),
                  controls && /* @__PURE__ */ jsxs(Fragment$1, { children: [
                    /* @__PURE__ */ jsx(CommandSeparator, {}),
                    /* @__PURE__ */ jsx(CommandGroup, { children: /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
                      selectedValues.length > 0 && /* @__PURE__ */ jsxs(Fragment$1, { children: [
                        /* @__PURE__ */ jsx(
                          CommandItem,
                          {
                            onSelect: handleClear,
                            className: "flex-1 cursor-pointer justify-center",
                            children: "Clear"
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          Separator,
                          {
                            orientation: "vertical",
                            className: "flex h-full min-h-6"
                          }
                        )
                      ] }),
                      /* @__PURE__ */ jsx(
                        CommandItem,
                        {
                          onSelect: () => setIsPopoverOpen(false),
                          className: "max-w-full flex-1 cursor-pointer justify-center",
                          children: "Close"
                        }
                      )
                    ] }) })
                  ] })
                ] })
              ] })
            }
          )
        ]
      }
    );
  }
);
DropdownInput.displayName = "DropdownInput";
const badgeVariantOptions = {
  default: "bg-primary text-text-inverted-strong border-transparent shadow",
  secondary: "bg-gray-3 text-gray-12 border-transparent",
  destructive: "bg-destructive text-text-inverted-strong border-transparent shadow",
  outline: "text-text-title",
  "outline-success": "text-green-9 border-green-5 bg-white-opacity-1"
};
const badgeRadiusOptions = {
  default: "rounded-sm",
  full: "rounded-full"
};
const badgeVariants = cva(
  "inline-flex h-5 items-center border px-2.5 py-0.5 text-sm font-medium leading-none transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2",
  {
    variants: {
      variant: badgeVariantOptions,
      radius: badgeRadiusOptions
    },
    defaultVariants: {
      variant: "default",
      radius: "default"
    }
  }
);
function Badge({ className, variant, radius, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(badgeVariants({ variant, radius }), className),
      ...props
    }
  );
}
const MultiSelect = React.forwardRef(
  ({
    options,
    onValueChange,
    defaultValue = [],
    defaultOpen = false,
    placeholder = "Select options",
    maxCount = 3,
    modalPopover = true,
    asChild = false,
    className,
    values,
    matchTriggerWidth = true,
    disableSelectAll = false,
    tidClear,
    tidClose,
    tidPlaceholder,
    tidSelectAll,
    tidMore,
    tidNoResults,
    tidSearch,
    onClose,
    disableSearch = false,
    icon,
    ...props
  }, ref) => {
    const { t } = useTranslation();
    const [selectedValues, setSelectedValues] = React.useState(defaultValue);
    const [isPopoverOpen, setIsPopoverOpenState] = React.useState(defaultOpen);
    const setIsPopoverOpen = (isOpen) => {
      setIsPopoverOpenState(isOpen);
      if (!isOpen) {
        onClose == null ? void 0 : onClose(selectedValues);
      }
    };
    const buttonRef = React.useRef(null);
    const mergedRef = mergeRefs(buttonRef, ref);
    const popoverStyle = useTriggerWidth(isPopoverOpen, buttonRef);
    React.useEffect(() => {
      if (values) {
        setSelectedValues(values);
      } else {
        setSelectedValues([]);
      }
    }, [values]);
    const handleInputKeyDown = (event) => {
      if (event.key === "Enter") {
        setIsPopoverOpen(true);
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange == null ? void 0 : onValueChange(newSelectedValues);
      }
    };
    const toggleOption = (option) => {
      const newSelectedValues = selectedValues.includes(option) ? selectedValues.filter((value) => value !== option) : [...selectedValues, option];
      setSelectedValues(newSelectedValues);
      onValueChange == null ? void 0 : onValueChange(newSelectedValues);
    };
    const handleClear = () => {
      setSelectedValues([]);
      onValueChange == null ? void 0 : onValueChange([]);
    };
    const handleTogglePopover = () => {
      setIsPopoverOpen(!isPopoverOpen);
    };
    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount);
      setSelectedValues(newSelectedValues);
      onValueChange == null ? void 0 : onValueChange(newSelectedValues);
    };
    const toggleAll = () => {
      if (selectedValues.length === options.length) {
        handleClear();
      } else {
        const allValues = options.map((option) => option.value);
        setSelectedValues(allValues);
        onValueChange == null ? void 0 : onValueChange(allValues);
      }
    };
    return /* @__PURE__ */ jsxs(
      Popover,
      {
        open: isPopoverOpen,
        onOpenChange: setIsPopoverOpen,
        modal: modalPopover,
        children: [
          /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
            Button,
            {
              ref: mergedRef,
              ...props,
              onClick: handleTogglePopover,
              variant: "secondary",
              icon,
              className: cn(
                "flex w-full p-1 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit hover:border-inherit",
                { "pl-3": icon },
                className
              ),
              children: selectedValues.length > 0 ? /* @__PURE__ */ jsxs("div", { className: "flex w-full items-center justify-between", children: [
                /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center", children: [
                  selectedValues.slice(0, maxCount).map((value) => {
                    const option = options.find((o) => o.value === value);
                    return /* @__PURE__ */ jsxs(
                      Badge,
                      {
                        className: cn(
                          "border-foreground/1 bg-transparent text-text-title",
                          "m-1 bg-card border-foreground/10"
                        ),
                        children: [
                          option == null ? void 0 : option.label,
                          /* @__PURE__ */ jsx(
                            "button",
                            {
                              onClick: (event) => {
                                event.stopPropagation();
                                toggleOption(value);
                              },
                              children: /* @__PURE__ */ jsx(Icon, { name: "cross-small", size: 16 })
                            }
                          )
                        ]
                      },
                      value.toString()
                    );
                  }),
                  selectedValues.length > maxCount && /* @__PURE__ */ jsxs(
                    Badge,
                    {
                      className: cn(
                        "border-foreground/1 bg-transparent text-text-title",
                        "m-1 bg-card border-foreground/10"
                      ),
                      children: [
                        `+ ${selectedValues.length - maxCount} more`,
                        /* @__PURE__ */ jsx(
                          "button",
                          {
                            onClick: (event) => {
                              event.stopPropagation();
                              clearExtraOptions();
                            },
                            children: /* @__PURE__ */ jsx(Icon, { name: "cross-small", size: 16 })
                          }
                        )
                      ]
                    }
                  )
                ] }),
                /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
                  /* @__PURE__ */ jsx(
                    "button",
                    {
                      onClick: (event) => {
                        event.stopPropagation();
                        handleClear();
                      },
                      children: /* @__PURE__ */ jsx(XIcon, { className: "mx-2 h-4 cursor-pointer text-text-title" })
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Separator,
                    {
                      orientation: "vertical",
                      className: "flex h-full min-h-6"
                    }
                  ),
                  /* @__PURE__ */ jsx(ChevronDown, { className: "mx-2 h-4 cursor-pointer text-text-title" })
                ] })
              ] }) : /* @__PURE__ */ jsxs("div", { className: "mx-auto flex w-full items-center justify-between", children: [
                /* @__PURE__ */ jsx("span", { className: "mx-3 text-sm text-muted", children: tidPlaceholder ? t(tidPlaceholder) : placeholder }),
                /* @__PURE__ */ jsx(ChevronDown, { className: "mx-2 h-4 cursor-pointer text-text-title" })
              ] })
            }
          ) }),
          /* @__PURE__ */ jsx(
            PopoverContent,
            {
              className: "w-auto p-0",
              align: "start",
              onEscapeKeyDown: () => setIsPopoverOpen(false),
              style: matchTriggerWidth ? popoverStyle : void 0,
              children: /* @__PURE__ */ jsxs(Command, { children: [
                !disableSearch && /* @__PURE__ */ jsx(
                  CommandInput,
                  {
                    placeholder: tidSearch ? t(tidSearch) : "Search...",
                    onKeyDown: handleInputKeyDown
                  }
                ),
                /* @__PURE__ */ jsxs(CommandList, { children: [
                  /* @__PURE__ */ jsx(CommandEmpty, { children: "No results found." }),
                  /* @__PURE__ */ jsxs(CommandGroup, { children: [
                    !disableSelectAll && /* @__PURE__ */ jsxs(
                      CommandItem,
                      {
                        onSelect: toggleAll,
                        className: "cursor-pointer",
                        children: [
                          /* @__PURE__ */ jsx(
                            "div",
                            {
                              className: cn(
                                "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                selectedValues.length === options.length ? "bg-primary text-text-inverted-title" : "opacity-50 [&_svg]:invisible"
                              ),
                              children: /* @__PURE__ */ jsx(CheckIcon$1, { color: "white", className: "size-4" })
                            }
                          ),
                          /* @__PURE__ */ jsxs("span", { children: [
                            "(",
                            tidSelectAll ? t(tidSelectAll) : "Select All",
                            ")"
                          ] })
                        ]
                      },
                      "all"
                    ),
                    options.map((option) => {
                      const isSelected = selectedValues.includes(option.value);
                      return /* @__PURE__ */ jsxs(
                        CommandItem,
                        {
                          onSelect: () => toggleOption(option.value),
                          className: "cursor-pointer",
                          children: [
                            /* @__PURE__ */ jsx(
                              "div",
                              {
                                className: cn(
                                  "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                  isSelected ? "bg-primary text-text-inverted-title" : "opacity-50 [&_svg]:invisible"
                                ),
                                children: /* @__PURE__ */ jsx(CheckIcon$1, { color: "white", className: "size-4" })
                              }
                            ),
                            /* @__PURE__ */ jsx("span", { children: option.label })
                          ]
                        },
                        option.value.toString()
                      );
                    })
                  ] }),
                  /* @__PURE__ */ jsx(CommandSeparator, {}),
                  /* @__PURE__ */ jsx(CommandGroup, { children: /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
                    selectedValues.length > 0 && /* @__PURE__ */ jsxs(Fragment$1, { children: [
                      /* @__PURE__ */ jsx(
                        CommandItem,
                        {
                          onSelect: handleClear,
                          className: "flex-1 cursor-pointer justify-center",
                          children: tidClear ? t(tidClear) : "Clear"
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        Separator,
                        {
                          orientation: "vertical",
                          className: "flex h-full min-h-6"
                        }
                      )
                    ] }),
                    /* @__PURE__ */ jsx(
                      CommandItem,
                      {
                        onSelect: () => setIsPopoverOpen(false),
                        className: "max-w-full flex-1 cursor-pointer justify-center",
                        children: tidClose ? t(tidClose) : "Close"
                      }
                    )
                  ] }) })
                ] })
              ] })
            }
          )
        ]
      }
    );
  }
);
MultiSelect.displayName = "MultiSelect";
const Tooltip = TooltipPrimitive.Root;
const TooltipProvider = TooltipPrimitive.Provider;
const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipArrow = TooltipPrimitive.Arrow;
const TooltipContent = React.forwardRef(({ className, sideOffset = 4, disablePortal = false, ...props }, ref) => {
  const content = /* @__PURE__ */ jsx(
    TooltipPrimitive.Content,
    {
      ref,
      sideOffset,
      className: cn(
        "data-[state=delayed-open]:animate-in data-[state=closed]:animate-out",
        "data-[state=delayed-open]:fade-in data-[state=closed]:fade-out",
        "transition-opacity duration-200 bg-gray-12 text-base-white",
        "rounded-sm shadow-m px-2 py-[5px]",
        className
      ),
      ...props
    }
  );
  if (disablePortal) {
    return content;
  }
  return /* @__PURE__ */ jsx(TooltipPrimitive.Portal, { children: content });
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
const DropdownSearchContent = ({
  className,
  options,
  groups,
  onOptionSelect,
  onValueChange,
  placeholder = "Search...",
  tidPlaceholder,
  tidNoOptionsFound
}) => {
  const { t } = useDefaultTranslation();
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [inputValue, setInputValue] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isListFocused, setIsListFocused] = useState(false);
  const filterOption = useCallback(
    (option) => {
      if (!inputValue.trim()) return true;
      const searchLower = inputValue.toLowerCase();
      if (option.searchValue) {
        return option.searchValue.toLowerCase().includes(searchLower);
      }
      if (option.value.toLowerCase().includes(searchLower)) {
        return true;
      }
      if (typeof option.label === "string") {
        return option.label.toLowerCase().includes(searchLower);
      }
      return false;
    },
    [inputValue]
  );
  const { displayData, flatOptions } = useMemo(() => {
    if (groups && groups.length > 0) {
      const filteredGroups = groups.map((group) => ({
        ...group,
        options: group.options.filter(filterOption)
      })).filter((group) => group.options.length > 0);
      const flat = filteredGroups.flatMap((group) => group.options);
      return {
        displayData: { type: "groups", groups: filteredGroups },
        flatOptions: flat
      };
    }
    const filteredOptions = (options || []).filter(filterOption);
    return {
      displayData: { type: "options", options: filteredOptions },
      flatOptions: filteredOptions
    };
  }, [groups, options, filterOption]);
  useEffect(() => {
    setTimeout(() => {
      var _a;
      return (_a = inputRef.current) == null ? void 0 : _a.focus();
    }, 0);
    setFocusedIndex(-1);
    setIsListFocused(false);
  }, []);
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const listItems = listRef.current.querySelectorAll('[role="option"]');
      if (listItems[focusedIndex]) {
        listItems[focusedIndex].scrollIntoView({ block: "nearest" });
      }
    }
  }, [focusedIndex]);
  useEffect(() => {
    var _a;
    if (isListFocused) {
      (_a = listRef.current) == null ? void 0 : _a.focus();
    }
  }, [isListFocused]);
  const handleChange = useCallback(
    (e) => {
      const value = e.target.value;
      setInputValue(value);
      onValueChange == null ? void 0 : onValueChange(value);
      setFocusedIndex(-1);
    },
    [onValueChange]
  );
  const handleOptionSelect = useCallback(
    (option) => {
      var _a;
      onOptionSelect(option);
      setInputValue("");
      setFocusedIndex(-1);
      setIsListFocused(false);
      (_a = inputRef.current) == null ? void 0 : _a.focus();
    },
    [onOptionSelect]
  );
  const handleInputKeyDown = useCallback(
    (e) => {
      if (flatOptions.length === 0) return;
      if (e.key === "Tab" && !e.shiftKey || e.key === "ArrowDown") {
        e.preventDefault();
        setIsListFocused(true);
        setFocusedIndex(0);
      }
    },
    [flatOptions.length]
  );
  const handleListKeyDown = useCallback(
    (e) => {
      var _a, _b;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex(
            (prev) => Math.min(prev + 1, flatOptions.length - 1)
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((prev) => {
            var _a2;
            if (prev <= 0) {
              setIsListFocused(false);
              (_a2 = inputRef.current) == null ? void 0 : _a2.focus();
              return -1;
            }
            return prev - 1;
          });
          break;
        case "Enter":
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < flatOptions.length) {
            handleOptionSelect(flatOptions[focusedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsListFocused(false);
          setFocusedIndex(-1);
          (_a = inputRef.current) == null ? void 0 : _a.focus();
          break;
        case "Tab":
          if (e.shiftKey) {
            e.preventDefault();
            setIsListFocused(false);
            setFocusedIndex(-1);
            (_b = inputRef.current) == null ? void 0 : _b.focus();
          }
          break;
      }
    },
    [focusedIndex, flatOptions, handleOptionSelect]
  );
  const handleClearInput = useCallback(
    (e) => {
      var _a;
      e.stopPropagation();
      setInputValue("");
      onValueChange == null ? void 0 : onValueChange("");
      setFocusedIndex(-1);
      (_a = inputRef.current) == null ? void 0 : _a.focus();
    },
    [onValueChange]
  );
  const handleListBlur = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsListFocused(false);
      setFocusedIndex(-1);
    }
  }, []);
  return /* @__PURE__ */ jsxs("div", { className: cn("w-full", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center border-b", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          ref: inputRef,
          className: "grow bg-transparent pl-2 text-[13px] leading-9 outline-none",
          type: "text",
          placeholder: t(tidPlaceholder, placeholder),
          onChange: handleChange,
          onKeyDown: handleInputKeyDown,
          value: inputValue
        }
      ),
      /* @__PURE__ */ jsx(
        "button",
        {
          className: "h-full border-0 bg-transparent px-2 text-interactive-state-selected-pressed hover:text-text-title",
          onClick: handleClearInput,
          type: "button",
          children: /* @__PURE__ */ jsx(Icon, { name: "cross-small" })
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      "div",
      {
        ref: listRef,
        className: "max-h-60 overflow-auto py-1 outline-none",
        role: "listbox",
        tabIndex: 0,
        onKeyDown: handleListKeyDown,
        onBlur: handleListBlur,
        children: flatOptions.length > 0 ? displayData.type === "groups" ? /* @__PURE__ */ jsx(
          GroupedOptions,
          {
            groups: displayData.groups,
            flatOptions,
            focusedIndex,
            handleOptionSelect
          }
        ) : displayData.options.map(
          (option, index) => /* @__PURE__ */ jsx(
            OptionWithTooltip,
            {
              option,
              index,
              focusedIndex,
              handleOptionSelect
            },
            option.key
          )
        ) : /* @__PURE__ */ jsx("div", { className: "p-2 text-center text-sm text-text-default", children: /* @__PURE__ */ jsx(Text$1, { tid: tidNoOptionsFound, children: "No options found" }) })
      }
    )
  ] });
};
const GroupedOptions = ({
  groups,
  flatOptions,
  focusedIndex,
  handleOptionSelect
}) => {
  return /* @__PURE__ */ jsx(Fragment$1, { children: groups.map((group, index) => /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx("div", { className: "px-2 py-1.5", children: /* @__PURE__ */ jsx(Text$1, { variant: "subheadline", color: "muted", children: group.label }) }),
    group.options.map((option) => {
      const flatIndex = flatOptions.findIndex(
        (opt) => opt.key === option.key
      );
      return /* @__PURE__ */ jsx(
        OptionWithTooltip,
        {
          option,
          index: flatIndex,
          focusedIndex,
          handleOptionSelect
        },
        option.key
      );
    })
  ] }, index)) });
};
const OptionWithTooltip = ({
  option,
  index,
  focusedIndex,
  handleOptionSelect
}) => {
  var _a, _b;
  const ref = useRef(null);
  const tooltipContent = option.disabled ? (_a = option.tooltip) == null ? void 0 : _a.disabled : (_b = option.tooltip) == null ? void 0 : _b.active;
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!option.disabled) {
      handleOptionSelect(option);
    }
  };
  return /* @__PURE__ */ jsxs(Tooltip, { children: [
    /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        role: "option",
        "aria-selected": focusedIndex === index,
        "aria-disabled": option.disabled,
        className: cn(
          "flex items-center gap-2 px-2 py-1.5 text-sm outline-none",
          !option.disabled && "cursor-pointer hover:bg-gray-alpha-2",
          option.disabled && "text-text-default",
          focusedIndex === index && "bg-gray-alpha-2"
        ),
        onClick: (e) => handleClick(e),
        children: option.label
      },
      option.key
    ) }),
    tooltipContent && /* @__PURE__ */ jsx(TooltipContent, { side: "right", children: /* @__PURE__ */ jsx(Text$1, { color: "base-white", children: tooltipContent }) })
  ] });
};
const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
const DropdownMenuArrow = DropdownMenuPrimitive.Arrow;
const DropdownMenuSubTrigger = React.forwardRef(
  ({
    className,
    inset,
    tid,
    values,
    showIconOnHover = false,
    children,
    ...props
  }, ref) => {
    const { t } = useTranslation();
    return /* @__PURE__ */ jsxs(
      DropdownMenuPrimitive.SubTrigger,
      {
        ref,
        className: cn(
          "flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-overlay-darken-300 data-[state=open]:bg-overlay-darken-300",
          inset && "pl-8",
          showIconOnHover && "group",
          className
        ),
        ...props,
        children: [
          tid ? t(tid, values) : children,
          showIconOnHover ? /* @__PURE__ */ jsx("div", { className: "ml-auto w-0 overflow-hidden transition-all duration-200 ease-in-out group-hover:w-4", children: /* @__PURE__ */ jsx(ChevronRightIcon, { className: "size-4 text-text-title" }) }) : /* @__PURE__ */ jsx(ChevronRightIcon, { className: "ml-auto size-4" })
        ]
      }
    );
  }
);
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
const DropdownMenuSubContent = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.SubContent,
  {
    ref,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
const DropdownMenuContent = React.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
const DropdownMenuItem = React.forwardRef(({ className, inset, tid, values, children, ...props }, ref) => {
  const { t } = useTranslation();
  return /* @__PURE__ */ jsx(
    DropdownMenuPrimitive.Item,
    {
      ref,
      className: cn(
        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-overlay-darken-300 focus:text-text-weak data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-gray-alpha-2 ",
        inset && "pl-8",
        className
      ),
      ...props,
      children: tid ? t(tid, values) : children
    }
  );
});
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
const DropdownMenuCheckboxItem = React.forwardRef(({ className, children, checked, ...props }, ref) => /* @__PURE__ */ jsxs(
  DropdownMenuPrimitive.CheckboxItem,
  {
    ref,
    className: cn(
      "relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-overlay-darken-300 focus:text-text-weak data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    checked,
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(CheckIcon$2, { className: "size-4" }) }) }),
      children
    ]
  }
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;
const DropdownMenuRadioItem = React.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  DropdownMenuPrimitive.RadioItem,
  {
    ref,
    className: cn(
      "relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-overlay-darken-300 focus:text-text-weak data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(DotFilledIcon, { className: "size-4 fill-current" }) }) }),
      children
    ]
  }
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
const DropdownMenuLabel = React.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Label,
  {
    ref,
    className: cn(
      "px-2 py-1.5 text-sm font-semibold",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
const DropdownMenuSeparator = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-gray-5", className),
    ...props
  }
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
const DropdownMenuShortcut = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn("ml-auto text-xs tracking-widest opacity-60", className),
      ...props
    }
  );
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";
const DropdownSubmenuContent = ({
  submenu,
  onOptionSelect,
  tidNoOptionsFound
}) => {
  var _a, _b;
  if (submenu.type === "search") {
    return /* @__PURE__ */ jsx(
      DropdownSearchContent,
      {
        options: submenu.options,
        onOptionSelect,
        tidNoOptionsFound,
        placeholder: (_a = submenu.searchProps) == null ? void 0 : _a.placeholder,
        tidPlaceholder: (_b = submenu.searchProps) == null ? void 0 : _b.tidPlaceholder
      }
    );
  }
  return /* @__PURE__ */ jsx(
    DropdownListContent,
    {
      options: submenu.options,
      onOptionSelect,
      tidNoOptionsFound,
      isOpen: true
    }
  );
};
const DropdownListContent = ({
  className,
  options,
  onOptionSelect,
  tidNoOptionsFound
}) => {
  const handleOptionSelect = useCallback(
    (option) => {
      onOptionSelect(option);
    },
    [onOptionSelect]
  );
  const renderOption = (option) => {
    if (option.submenu) {
      return /* @__PURE__ */ jsxs(DropdownMenuSub, { children: [
        /* @__PURE__ */ jsx(
          DropdownMenuSubTrigger,
          {
            className: "flex items-center gap-2 px-2 py-1.5 text-sm",
            children: option.label
          }
        ),
        /* @__PURE__ */ jsx(DropdownMenuPortal, { children: /* @__PURE__ */ jsx(DropdownMenuSubContent, { className: "min-w-[200px]", children: /* @__PURE__ */ jsx(
          DropdownSubmenuContent,
          {
            submenu: option.submenu,
            onOptionSelect: handleOptionSelect,
            tidNoOptionsFound
          }
        ) }) })
      ] }, option.key);
    }
    return /* @__PURE__ */ jsxs(
      DropdownMenuItem,
      {
        className: "flex items-center justify-between gap-2 px-2 py-1.5",
        onSelect: () => handleOptionSelect(option),
        children: [
          option.label,
          option.icon && /* @__PURE__ */ jsx(Icon, { size: 12, color: "text-default", name: option.icon })
        ]
      },
      option.key
    );
  };
  return /* @__PURE__ */ jsx("div", { className: cn("w-full", className), tabIndex: 0, children: /* @__PURE__ */ jsx("div", { className: "max-h-60 overflow-auto py-1", children: options.length > 0 ? options.map((option) => renderOption(option)) : /* @__PURE__ */ jsx("div", { className: "p-2 text-center text-text-default", children: /* @__PURE__ */ jsx(Text$1, { tid: tidNoOptionsFound, children: "No options found" }) }) }) });
};
const Checkbox = React.forwardRef(({ className, checked, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    CheckboxPrimitive.Root,
    {
      ref,
      className: cn(
        "peer h-4 w-4 shrink-0 rounded-sm border border-interactive-state-selected-pressed shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-5 data-[state=checked]:bg-text-title data-[state=checked]:text-gray-1 self-start",
        className
      ),
      checked,
      ...props,
      children: /* @__PURE__ */ jsxs(
        CheckboxPrimitive.Indicator,
        {
          className: cn("flex items-center justify-center"),
          children: [
            checked === "indeterminate" && /* @__PURE__ */ jsx(DividerHorizontalIcon, {}),
            checked === true && /* @__PURE__ */ jsx(CheckIcon$2, { className: "translate-y-[-0.5px]" })
          ]
        }
      )
    }
  );
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;
const FilterByValueItem = forwardRef(
  ({
    activeIndex,
    index,
    onOptionChange,
    onOptionHover,
    option,
    selectedOptions,
    offsetIndex = 0,
    single = false,
    disabled = false
  }, ref) => {
    const onCheckedChange = useCallback(
      (checked) => onOptionChange(option, checked),
      [onOptionChange, option.key]
    );
    const onMouseEnter = useCallback(() => {
      onOptionHover(index + offsetIndex);
    }, [onOptionHover, index, offsetIndex]);
    const isChecked = !!selectedOptions.find(
      (selectedOption) => selectedOption.key === option.key
    );
    const onClick = useCallback(() => {
      if (single && !disabled) {
        onOptionChange(option, !isChecked);
      }
    }, [onOptionChange, option, isChecked, single, disabled]);
    return /* @__PURE__ */ jsxs(
      Text$1,
      {
        ref,
        as: "label",
        className: cn("flex items-center gap-2 p-2 text-base rounded-sm", {
          "bg-overlay-darken-300": activeIndex === index + offsetIndex,
          "text-muted cursor-not-allowed": disabled
        }),
        onClick,
        onMouseEnter,
        children: [
          single ? /* @__PURE__ */ jsx(Fragment$1, { children: isChecked && /* @__PURE__ */ jsx(Icon, { name: "checkmark-2" }) }) : /* @__PURE__ */ jsx(
            Checkbox,
            {
              checked: isChecked,
              value: option.key,
              onCheckedChange
            }
          ),
          option.label
        ]
      },
      option.key
    );
  }
);
FilterByValueItem.displayName = "FilterByValueItem";
const OPTION_PADDING = 4;
const useKeepOptionsInView = (optionsRef, activeIndex, offsetIndex = 0) => {
  useEffect(() => {
    var _a;
    if (optionsRef.current) {
      const initialScrollTop = optionsRef.current.scrollTop;
      (_a = optionsRef.current.children.item(activeIndex - offsetIndex)) == null ? void 0 : _a.scrollIntoView({
        behavior: "instant",
        block: "nearest",
        inline: "nearest"
      });
      if (initialScrollTop < optionsRef.current.scrollTop) {
        optionsRef.current.scrollBy(0, OPTION_PADDING);
      } else if (initialScrollTop > optionsRef.current.scrollTop) {
        optionsRef.current.scrollBy(0, -OPTION_PADDING);
      }
    }
  }, [activeIndex, offsetIndex]);
};
const FilterByValue = forwardRef(
  ({
    autoFocus,
    className,
    fetchingOptions,
    disabledOptions = [],
    onOpen,
    onClose,
    onOptionSelect,
    onValueChange,
    onAddOptionSelect,
    openOnFocus,
    options,
    selectedOptions,
    value = "",
    single = false,
    addOptions = false,
    tidPlaceholder,
    tidNoValuesFound,
    tidValues,
    tidValuesCount,
    tidFirstFifty,
    tidSelectedValues
  }, parentRef) => {
    const { t } = useDefaultTranslation();
    const rootRef = useRef(null);
    const firstOptionRef = useRef(null);
    const [optionContainerMaxHeight, setOptionContainerMaxHeight] = useState(0);
    const [activeIndex, setActiveIndex] = useState(-1);
    const selectedOptionsRef = useRef(null);
    const unselectedOptionsRef = useRef(null);
    const onChange = useCallback(
      (event) => onValueChange(event.target.value),
      [onValueChange]
    );
    const [optionsOpen, setOptionsOpen] = useState(!openOnFocus);
    const onInputFocus = useCallback(() => {
      if (openOnFocus) {
        setOptionsOpen(true);
        onOpen == null ? void 0 : onOpen(selectedOptions);
        setActiveIndex(-1);
      }
    }, [openOnFocus, onOpen, options]);
    const onOptionHover = useCallback(
      (index) => setActiveIndex(index),
      []
    );
    const autoFocusRef = useCallback(
      (input) => {
        if (autoFocus && input) {
          input.focus();
          onInputFocus();
        }
      },
      [autoFocus, onInputFocus]
    );
    const ref = mergeRefs(parentRef, autoFocusRef);
    const onClearInputClick = useCallback(
      () => onValueChange(""),
      [onValueChange]
    );
    const unselectedOptions = useMemo(
      () => options.filter(
        (option) => !selectedOptions.find(
          (selectedOption) => selectedOption.key === option.key
        )
      ),
      [options, selectedOptions]
    );
    const onKeyDown = useCallback(
      (event) => {
        if (event.key === "ArrowDown") {
          setActiveIndex(
            (prev) => Math.min(
              prev + 1,
              selectedOptions.length + unselectedOptions.length - 1
            )
          );
          event.preventDefault();
        }
        if (event.key === "ArrowUp") {
          setActiveIndex((prev) => Math.max(prev - 1, -1));
          event.preventDefault();
        }
        if ((event.code === "Space" || event.key === "Enter") && activeIndex >= 0) {
          if (activeIndex < selectedOptions.length) {
            onOptionSelect(
              selectedOptions[activeIndex],
              !selectedOptions.find(
                (option) => option.key === selectedOptions[activeIndex].key
              )
            );
          } else {
            onOptionSelect(
              unselectedOptions[activeIndex - selectedOptions.length],
              !selectedOptions.find(
                (option) => option.key === unselectedOptions[activeIndex - selectedOptions.length].key
              )
            );
          }
          if (event.key === "Enter") {
            onClose == null ? void 0 : onClose(selectedOptions);
          }
          event.preventDefault();
        }
      },
      [
        activeIndex,
        unselectedOptions,
        selectedOptions,
        onOptionSelect,
        setActiveIndex
      ]
    );
    useKeepOptionsInView(selectedOptionsRef, activeIndex);
    useKeepOptionsInView(
      unselectedOptionsRef,
      activeIndex,
      selectedOptions.length
    );
    const onClickOutside = useCallback(
      (event) => {
        if (rootRef.current && event.target.isConnected && !rootRef.current.contains(event.target)) {
          setOptionsOpen(false);
          onClose == null ? void 0 : onClose(selectedOptions);
        }
      },
      [onClose, selectedOptions]
    );
    useEffect(() => {
      document.addEventListener("click", onClickOutside);
      return () => document.removeEventListener("click", onClickOutside);
    }, [onClickOutside]);
    useEffect(() => {
      if (firstOptionRef.current) {
        const { height } = firstOptionRef.current.getBoundingClientRect();
        if (height) {
          setOptionContainerMaxHeight(height * 6.5 + OPTION_PADDING);
        }
      }
    }, [optionsOpen]);
    const valuesByline = useMemo(() => {
      if (fetchingOptions) {
        return /* @__PURE__ */ jsx(Spinner, { size: 16 });
      }
      if (options.length >= 50) {
        return /* @__PURE__ */ jsx(Text$1, { className: "text-muted", tid: tidFirstFifty, children: "Showing first 50 options" });
      }
      if (unselectedOptions.length === 0) {
        return /* @__PURE__ */ jsx(
          Text$1,
          {
            className: "text-interactive-state-selected-pressed",
            tid: tidNoValuesFound,
            children: "No values found"
          }
        );
      }
      return /* @__PURE__ */ jsxs(
        Text$1,
        {
          className: "text-interactive-state-selected-pressed",
          values: { count: unselectedOptions.length },
          tid: tidValuesCount,
          children: [
            "Showing ",
            unselectedOptions.length,
            " option",
            unselectedOptions.length === 1 ? "" : "s"
          ]
        }
      );
    }, [options, value, fetchingOptions, unselectedOptions]);
    return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col", className), ref: rootRef, children: [
      /* @__PURE__ */ jsxs("div", { className: "flex h-9 items-center px-1", children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            ref,
            className: "grow bg-transparent pl-2 text-[13px] leading-9 outline-none",
            type: "text",
            placeholder: t(tidPlaceholder, "Search…"),
            onChange,
            onKeyDown,
            value,
            onFocus: onInputFocus
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            className: "h-full border-0 bg-transparent px-2 text-interactive-state-selected-pressed hover:text-text-title",
            onClick: onClearInputClick,
            children: /* @__PURE__ */ jsx(Icon, { name: "cross-small" })
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: cn("border-t", { hidden: !optionsOpen }), children: [
        selectedOptions.length > 0 && !value && /* @__PURE__ */ jsxs(Fragment$1, { children: [
          tidSelectedValues && /* @__PURE__ */ jsx("div", { className: "flex h-7 items-center justify-between px-3 text-xs text-muted", children: /* @__PURE__ */ jsx(Text$1, { tid: tidSelectedValues, children: "Selected values" }) }),
          /* @__PURE__ */ jsx(
            "div",
            {
              ref: selectedOptionsRef,
              className: "flex flex-col overflow-y-auto p-1",
              style: { maxHeight: optionContainerMaxHeight },
              children: selectedOptions.map((option, index) => /* @__PURE__ */ jsx(
                FilterByValueItem,
                {
                  option,
                  activeIndex,
                  onOptionHover,
                  selectedOptions,
                  onOptionChange: onOptionSelect,
                  index,
                  single
                },
                option.key
              ))
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex h-7 items-center justify-between px-3 text-xs text-muted", children: [
          /* @__PURE__ */ jsx(Text$1, { tid: tidValues, children: "Values" }),
          valuesByline
        ] }),
        unselectedOptions.length > 0 && /* @__PURE__ */ jsx(
          "div",
          {
            ref: unselectedOptionsRef,
            className: "flex flex-col overflow-y-auto p-1",
            style: { maxHeight: optionContainerMaxHeight },
            children: unselectedOptions.map((option, index) => /* @__PURE__ */ jsx(
              FilterByValueItem,
              {
                ref: index === 0 ? firstOptionRef : null,
                option,
                activeIndex,
                onOptionHover,
                selectedOptions,
                onOptionChange: onOptionSelect,
                index,
                offsetIndex: selectedOptions.length,
                single,
                disabled: disabledOptions.includes(option.value)
              },
              option.key
            ))
          }
        ),
        addOptions && value && options.length === 0 && onAddOptionSelect && /* @__PURE__ */ jsx("div", { className: "flex flex-col overflow-y-auto p-1", children: /* @__PURE__ */ jsx(
          FilterByValueItem,
          {
            option: {
              key: value,
              value,
              label: `Add New Field: ${value}`
            },
            activeIndex: 0,
            onOptionHover,
            selectedOptions,
            onOptionChange: onAddOptionSelect,
            index: 0,
            offsetIndex: 0,
            single
          },
          "add-option"
        ) })
      ] })
    ] });
  }
);
FilterByValue.displayName = "FilterByValue";
const noop = () => {
};
const initialContext = {
  getTransformedValue: () => "",
  insertFragment: noop,
  setValue: noop,
  activeSearch: "",
  inputElement: null,
  setInputElement: noop,
  selectItem: noop,
  executeActionCallback: noop,
  setActiveItemIndex: noop,
  opened: null,
  index: 0,
  loading: false,
  results: [],
  closeAutocomplete: noop,
  openAutocomplete: noop,
  onBeforeChanges: noop,
  onKeyDown: noop,
  onChanges: noop,
  fixed: true,
  setPositionFixed: noop,
  configs: []
};
const RichMentionsContext = createContext(initialContext);
const extractMentionsFromDefaultValue = (htmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const mentionElements = doc.querySelectorAll("[data-rich-mentions]");
  const mentions = Array.from(mentionElements).map(
    (el) => el.getAttribute("data-rich-mentions") || ""
  );
  return mentions.filter((mention) => mention);
};
function RichMentionsInput({
  defaultValue,
  autoFocus,
  onMentionChange,
  placeholder,
  ...divAttributes
}) {
  const ref = useRef(null);
  const {
    inputElement,
    setInputElement,
    onBeforeChanges,
    onKeyDown,
    onChanges,
    getInitialHTML
  } = useContext(RichMentionsContext);
  const [mentions, setMentions] = useState(() => {
    if (defaultValue && getInitialHTML) {
      const html = getInitialHTML(defaultValue);
      return extractMentionsFromDefaultValue(html);
    }
    return [];
  });
  if (ref.current === null && defaultValue && getInitialHTML) {
    const html = getInitialHTML(defaultValue);
    ref.current = html;
  }
  const mergeOnKeyDown = (event) => {
    onKeyDown(event);
    if (divAttributes.onKeyDown) {
      divAttributes.onKeyDown(event);
    }
  };
  const onInput = (event) => {
    if (divAttributes.onInput) {
      divAttributes.onInput(event);
    }
    onChanges(event);
  };
  const onBeforeInput = (event) => {
    onBeforeChanges(event);
    if (divAttributes.onBeforeInput) {
      divAttributes.onBeforeInput(event);
    }
  };
  React__default.useEffect(() => {
    if (inputElement && autoFocus) {
      const range2 = document.createRange();
      range2.selectNodeContents(inputElement);
      range2.collapse(false);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range2);
      }
      inputElement.focus();
    }
  }, [inputElement, autoFocus]);
  React__default.useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          var _a, _b, _c, _d;
          if ((_b = (_a = node == null ? void 0 : node.parentNode) == null ? void 0 : _a.dataset) == null ? void 0 : _b.richMentions) {
            const ref2 = (_d = (_c = node == null ? void 0 : node.parentNode) == null ? void 0 : _c.dataset) == null ? void 0 : _d.richMentions;
            if (ref2) {
              setMentions((prevMentions) => [...prevMentions, ref2]);
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            if (node == null ? void 0 : node.hasAttribute("data-rich-mentions")) {
              const ref2 = node == null ? void 0 : node.getAttribute("data-rich-mentions");
              if (ref2) {
                setMentions((prevMentions) => [...prevMentions, ref2]);
              }
            }
          }
        });
        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node;
            if (element.hasAttribute("data-rich-mentions")) {
              const ref2 = element.getAttribute("data-rich-mentions");
              if (ref2) {
                setMentions(
                  (prevMentions) => prevMentions.filter((mention) => mention !== ref2)
                );
              }
            }
          }
        });
      });
    });
    const config = { childList: true, subtree: true };
    if (inputElement) {
      observer.observe(inputElement, config);
    }
    return () => observer.disconnect();
  }, [inputElement]);
  React__default.useEffect(() => {
    if (onMentionChange) {
      onMentionChange(mentions);
    }
  }, [mentions, onMentionChange]);
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref: setInputElement,
      ...divAttributes,
      "data-testid": "input",
      contentEditable: true,
      onBeforeInput,
      onKeyDown: mergeOnKeyDown,
      onInput,
      dangerouslySetInnerHTML: { __html: ref.current || "" },
      placeholder
    }
  );
}
function setCursorPosition(element, position) {
  const selection = document.getSelection();
  const range2 = document.createRange();
  range2.setStart(element, position);
  range2.collapse(true);
  if (selection) {
    selection.removeAllRanges();
    selection.addRange(range2);
  }
}
function deleteSelection(selection, event) {
  let deleted = false;
  let lastDeletedRange = null;
  for (let i = 0; i < selection.rangeCount; ++i) {
    const range2 = selection.getRangeAt(i);
    if (range2.startContainer !== range2.endContainer || range2.startOffset !== range2.endOffset) {
      deleted = true;
      lastDeletedRange = range2;
      range2.deleteContents();
    }
  }
  if ((event == null ? void 0 : event.data) && lastDeletedRange) {
    const textNode = document.createTextNode(event.data);
    lastDeletedRange.insertNode(textNode);
    setCursorPosition(textNode, 1);
    event.preventDefault();
  }
  return deleted;
}
function nodeToHtmlElement(node) {
  return node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
}
function escapeFragmentWithValue(element, text, position = "after") {
  const textNode = document.createTextNode(text.replace(/\s/g, " "));
  const parent = element.parentElement;
  if (parent) {
    if (position === "after") {
      parent.insertBefore(textNode, element.nextSibling);
    } else {
      parent.insertBefore(textNode, element);
    }
    setCursorPosition(textNode, text.length);
  }
}
function fixCursorInsertion(event, selection) {
  if (event.defaultPrevented) {
    return;
  }
  const insertion = event.data;
  const container = event.currentTarget;
  for (let i = 0; i < selection.rangeCount; i++) {
    const range2 = selection.getRangeAt(i);
    if (range2.startContainer === range2.endContainer) {
      const element = nodeToHtmlElement(range2.startContainer);
      if (!container.contains(element)) {
        continue;
      }
      if (range2.startContainer instanceof Text && range2.startOffset === 0) {
        const previousChild = range2.startContainer.previousSibling;
        const previousElement = range2.startContainer.previousElementSibling;
        if (previousChild && previousElement && previousChild === previousElement && previousElement.hasAttribute("data-rich-mentions") && !previousElement.hasAttribute("data-integrity")) {
          previousElement.appendChild(document.createTextNode(insertion));
          event.preventDefault();
          continue;
        }
      }
      if (!element || container === element || !element.hasAttribute("data-rich-mentions")) {
        continue;
      }
      if (range2.endOffset === 0) {
        escapeFragmentWithValue(element, insertion, "before");
        event.preventDefault();
        continue;
      }
      const isFinal = element.hasAttribute("data-integrity");
      const text = element.textContent || "";
      if (isFinal && range2.startOffset === text.length) {
        escapeFragmentWithValue(element, insertion, "after");
        event.preventDefault();
        continue;
      }
    }
  }
}
function transformFinalFragment(span, ref, config, item) {
  const displayContent = (item == null ? void 0 : item.displayText) || ref.replace(config.match, config.matchDisplay).replace(/\s/g, " ");
  span.textContent = displayContent;
  span.setAttribute("data-rich-mentions", ref);
  span.setAttribute("spellcheck", "false");
  if (item == null ? void 0 : item.type) {
    span.setAttribute("data-type", item.type);
  }
  if (item == null ? void 0 : item.groupName) {
    span.setAttribute("data-group-name", item.groupName);
  }
  if (item == null ? void 0 : item.groupId) {
    span.setAttribute("data-group-id", item.groupId);
  }
  if (process.env.NODE_ENV !== "production") {
    span.setAttribute("data-testid", "final");
  }
  if (config.customizeFragment) {
    config.customizeFragment(span, true);
  }
  span.setAttribute("data-integrity", span.innerHTML);
  if (span.parentElement) {
    const textNode = document.createTextNode(" ");
    span.parentElement.insertBefore(textNode, span.nextSibling);
    setCursorPosition(textNode, 1);
  }
}
function replaceSpacesWithInsecableSpaces(text) {
  const div = document.createElement("div");
  div.innerHTML = text;
  function recursiveSpaceReplacer(element) {
    Array.from(element.childNodes).forEach((element2) => {
      if (element2 instanceof Text && element2.nodeValue) {
        element2.nodeValue = element2.nodeValue.replace(/( |\t)/g, " ");
      } else if (element2 instanceof HTMLElement) {
        recursiveSpaceReplacer(element2);
        if (element2.hasAttribute("data-rich-mentions") && !element2.hasAttribute("data-integrity")) {
          element2.setAttribute("data-integrity", element2.innerHTML);
        }
      }
    });
  }
  recursiveSpaceReplacer(div);
  return div.innerHTML;
}
function getConfigsInitialValue(configs) {
  return (text) => {
    const formattedTextWithHtml = configs.reduce((acc, config) => {
      return acc.replace(config.match, ($0) => {
        const span = document.createElement("span");
        transformFinalFragment(span, $0, config);
        return span.outerHTML;
      });
    }, text);
    return replaceSpacesWithInsecableSpaces(formattedTextWithHtml);
  };
}
function getFragment(node) {
  const element = nodeToHtmlElement(node);
  return (element == null ? void 0 : element.hasAttribute("data-rich-mentions")) ? element : null;
}
function getTransformedValue(inputElement) {
  if (!inputElement || inputElement.innerHTML === "<br>") {
    return "";
  }
  const brCharacter = `_br_${Date.now()}_`;
  const brMatcher = new RegExp(`\\n?${brCharacter}\\n?`, "g");
  const processedContent = Array.from(inputElement.childNodes).map((el) => {
    const content = getNodeContent(el, brCharacter);
    return content;
  }).join("");
  const result = processedContent.replace(/\u00A0/g, " ").replace(/\n{2,}/g, "\n").replace(brMatcher, "\n").trim();
  return result;
}
function getNodeContent(element, brCharacter) {
  if (element instanceof Text) {
    return element.textContent || "";
  }
  if (element instanceof HTMLBRElement) {
    return brCharacter;
  }
  if (element instanceof Element) {
    const richValue = element.getAttribute("data-rich-mentions");
    if (richValue) {
      return richValue;
    }
    const char = element instanceof HTMLDivElement ? "\n" : "";
    const children = Array.from(element.childNodes).map((node) => getNodeContent(node, brCharacter)).join("");
    return char + children + char;
  }
  return "";
}
function handleFragmentCreation(event, selection, configs, ctx) {
  const { anchorNode, anchorOffset } = selection;
  if (event.defaultPrevented || !anchorNode || getFragment(anchorNode)) {
    return;
  }
  const insertion = event.data;
  const fragmentText = anchorNode.textContent || "";
  const text = fragmentText.substr(0, anchorOffset) + insertion + fragmentText.substr(anchorOffset);
  const config = configs.find((cfg) => text.match(cfg.query));
  if (!config) {
    return;
  }
  const matches = text.match(config.query);
  const index = matches.index || 0;
  const textBeforeQuery = text.substr(0, index);
  if (textBeforeQuery.length && !/\W$/.test(textBeforeQuery) || anchorOffset < index || anchorOffset >= index + matches[0].length) {
    return;
  }
  anchorNode.textContent = textBeforeQuery;
  const fragment = document.createElement("span");
  const textQuery = matches[0].substr(
    0,
    anchorOffset - index + insertion.length
  );
  const afterInsertion = text.substr(index + textQuery.length);
  fragment.setAttribute("data-rich-mentions", "");
  fragment.setAttribute("spellcheck", "false");
  if (process.env.NODE_ENV !== "production") {
    fragment.setAttribute("data-testid", "pending");
  }
  fragment.textContent = textQuery;
  if (config.customizeFragment) {
    config.customizeFragment(fragment, false);
  }
  const after = document.createTextNode(
    /^\s/.test(afterInsertion) ? afterInsertion : " " + afterInsertion
  );
  const isContainer = event.currentTarget === anchorNode;
  const parent = isContainer ? anchorNode : anchorNode.parentElement;
  if (parent) {
    if (isContainer) {
      parent.appendChild(fragment);
      parent.appendChild(after);
    } else {
      parent.insertBefore(after, anchorNode.nextSibling);
      parent.insertBefore(fragment, anchorNode.nextSibling);
    }
  }
  event.preventDefault();
  if (!fragment.childNodes[0]) {
    return;
  }
  setCursorPosition(
    fragment.childNodes[0],
    anchorOffset - textBeforeQuery.length + 1
  );
  ctx.openAutocomplete(fragment, textQuery, config);
}
const removeIfFinalFragment = (node, container) => {
  const element = nodeToHtmlElement(node);
  if (!container.contains(element) || container === element) {
    return;
  }
  if (element && element.hasAttribute("data-integrity")) {
    container.removeChild(element);
  }
};
function handleFragmentDeletion(event, selection) {
  if (event.defaultPrevented) {
    return;
  }
  const container = event.currentTarget;
  for (let i = 0; i < selection.rangeCount; i++) {
    const range2 = selection.getRangeAt(i);
    removeIfFinalFragment(range2.startContainer, container);
    removeIfFinalFragment(range2.endContainer, container);
  }
}
function removeBrokenFragments(inputElement, configs) {
  Array.from(inputElement.children).forEach(
    function fixBrokenElement(element, index) {
      const parent = element.parentElement;
      if (element instanceof HTMLBRElement) {
        if (parent.children.length !== 1 && index !== parent.children.length - 1) {
          const div = document.createElement("div");
          parent.insertBefore(div, element);
          div.appendChild(element);
        }
        return;
      }
      if (element instanceof HTMLDivElement && !element.attributes.length) {
        Array.from(element.children).forEach(fixBrokenElement);
        return;
      }
      const text = element.textContent || "";
      if (!(element instanceof Text) && !element.hasAttribute("data-rich-mentions")) {
        parent.insertBefore(document.createTextNode(text), element);
        parent.removeChild(element);
        return;
      }
      if (element.hasAttribute("data-integrity")) {
        if (element.getAttribute("data-integrity") !== element.innerHTML) {
          const parentNode = element.parentNode;
          if (parentNode) {
            let targetNode = element.previousSibling;
            let position = 0;
            if (targetNode && targetNode.nodeType === Node.TEXT_NODE) {
              position = targetNode.length;
              setCursorPosition(targetNode, position);
            } else if (!targetNode && parentNode.firstChild) {
              setCursorPosition(parentNode.firstChild, 0);
            } else {
              setCursorPosition(parentNode, 0);
            }
            parentNode.removeChild(element);
          }
        }
        return;
      }
      const isValid = configs.some((cfg) => text.match(cfg.query));
      if (!isValid) {
        parent.insertBefore(document.createTextNode(text), element.nextSibling);
        parent.removeChild(element);
      }
    }
  );
}
const isSpace = (char) => /(\u00A0|\s)/.test(char);
const needSpaceBefore = (text, offset, node, defaultValue) => {
  if (offset > 0) {
    return !isSpace(text.charAt(offset - 1));
  }
  if (["DIV", "BR"].includes(node.nodeName)) {
    return false;
  }
  if (node.previousSibling) {
    const prevText = node.previousSibling.textContent || "";
    return !!prevText.length && !isSpace(prevText.charAt(prevText.length - 1));
  }
  return defaultValue;
};
const needSpaceAfter = (text, offset, node) => {
  if (offset < text.length) {
    return !isSpace(text.charAt(text.length - 1));
  }
  if (!node.nextSibling) {
    return true;
  }
  const nextText = node.nextSibling.textContent || "";
  return !nextText.length || !isSpace(nextText.charAt(0));
};
function insertFragment(ref, customFragment, configs, inputElement) {
  var _a, _b, _c, _d, _e, _f;
  const config = configs.find((cfg) => ref.match(cfg.match));
  if (!inputElement || !config && !customFragment) {
    return;
  }
  let insertAfterNode = null;
  let insertBeforeNode = null;
  let addSpaceBefore = false;
  let addSpaceAfter = false;
  const selection = document.getSelection();
  if (selection && selection.anchorNode && inputElement.contains(selection.anchorNode)) {
    let { anchorNode: node, anchorOffset: offset } = selection;
    let fragment = getFragment(node);
    deleteSelection(selection);
    if (fragment && offset === (node.textContent || "").length) {
      if (!fragment.nextSibling) {
        inputElement.insertBefore(document.createTextNode(""), null);
      }
      node = fragment.nextSibling;
      offset = 0;
      fragment = null;
    }
    if (fragment) {
      if (fragment.hasAttribute("data-integrity")) {
        insertBeforeNode = fragment.nextSibling;
        (_a = fragment.parentElement) == null ? void 0 : _a.removeChild(fragment);
      } else {
        const text = node.textContent || "";
        if (offset > 0 && offset < text.length) {
          const firstPart = text.substr(0, offset);
          const secondPart = text.substr(offset);
          const subFragment = document.createTextNode(secondPart);
          inputElement.insertBefore(subFragment, fragment.nextSibling);
          node.textContent = firstPart;
          addSpaceBefore = true;
          insertBeforeNode = subFragment;
        }
        addSpaceBefore = needSpaceBefore(text, offset, node, addSpaceBefore);
        addSpaceAfter = needSpaceAfter(text, offset, node);
      }
    } else {
      let text = node.textContent || "";
      if (offset > 0) {
        insertAfterNode = node;
      } else {
        const element = node;
        if (!text && element.nodeName === "DIV" && !element.attributes.length && element.childNodes.length === 1 && element.firstElementChild instanceof HTMLBRElement) {
          if (element.previousSibling instanceof HTMLDivElement) {
            insertBeforeNode = node;
            element.removeChild(element.firstElementChild);
          } else {
            insertAfterNode = node;
            element.removeChild(element.firstElementChild);
          }
        } else {
          insertBeforeNode = node;
        }
      }
      if (offset > 0 && offset < text.length) {
        const firstPart = text.substr(0, offset);
        const secondPart = text.substr(offset);
        text = firstPart;
        node.textContent = firstPart;
        (_b = node.parentElement) == null ? void 0 : _b.insertBefore(
          document.createTextNode(secondPart),
          node.nextSibling
        );
      }
      addSpaceBefore = needSpaceBefore(text, offset, node, addSpaceBefore);
      addSpaceAfter = needSpaceAfter(text, offset, node);
    }
  } else {
    const text = inputElement.textContent || "";
    addSpaceAfter = true;
    addSpaceBefore = !isSpace(text.charAt(text.length - 1));
  }
  const span = document.createElement("span");
  if (config) {
    transformFinalFragment(span, ref, config);
  } else if (customFragment) {
    span.appendChild(customFragment);
    span.setAttribute("data-rich-mentions", ref);
    span.setAttribute("data-integrity", span.innerHTML);
    span.setAttribute("spellcheck", "false");
    if (process.env.NODE_ENV !== "production") {
      span.setAttribute("data-testid", "final");
    }
  }
  if (insertAfterNode && insertAfterNode !== inputElement) {
    (_c = insertAfterNode.parentElement) == null ? void 0 : _c.insertBefore(
      span,
      insertAfterNode.nextSibling
    );
  } else if (insertBeforeNode && insertBeforeNode !== inputElement) {
    (_d = insertBeforeNode.parentElement) == null ? void 0 : _d.insertBefore(span, insertBeforeNode);
  } else {
    inputElement.appendChild(span);
  }
  if (addSpaceBefore) {
    const space = document.createTextNode(" ");
    (_e = span.parentElement) == null ? void 0 : _e.insertBefore(space, span);
  }
  if (addSpaceAfter) {
    const space = document.createTextNode(" ");
    (_f = span.parentElement) == null ? void 0 : _f.insertBefore(space, span.nextSibling);
  }
  if (span.nextSibling) {
    setCursorPosition(span.nextSibling, addSpaceAfter ? 1 : 0);
  }
  removeBrokenFragments(inputElement, configs);
}
function markFragmentAsStale(element) {
  var _a;
  element.classList.remove("pending");
  element.classList.add("stale");
  element.removeAttribute("data-rich-mentions");
  if (element.nextSibling) {
    setCursorPosition(element.nextSibling, 0);
  } else {
    const textNode = document.createTextNode(" ");
    (_a = element.parentNode) == null ? void 0 : _a.appendChild(textNode);
    setCursorPosition(textNode, 1);
  }
}
function RichMentionsProvider({
  children,
  configs,
  getContext,
  getInitialHTML = getConfigsInitialValue(configs)
}) {
  const ref = useRef({
    // @ts-ignore
    ...initialContext,
    configs,
    getInitialHTML,
    setPositionFixed,
    setInputElement,
    selectItem,
    executeActionCallback,
    onBeforeChanges,
    onChanges,
    onKeyDown,
    closeAutocomplete,
    openAutocomplete,
    setActiveItemIndex,
    getTransformedValue: getTransformedValue$1,
    insertFragment: insertFragment$1,
    setValue
  });
  const [__ctx__, setState] = useState(ref.current);
  const updateState = (data) => {
    ref.current = {
      ...ref.current,
      ...data
    };
    setState(ref.current);
  };
  useEffect(() => {
    document.addEventListener("selectionchange", onSelectionChange, false);
    return () => {
      document.removeEventListener("selectionchange", onSelectionChange, false);
    };
  }, []);
  useEffect(() => {
    if (typeof getContext === "function") {
      getContext(__ctx__);
    } else if (typeof getContext === "object") {
      getContext.current = __ctx__;
    }
  }, [getContext, __ctx__]);
  useEffect(() => {
    updateState({
      configs,
      results: [],
      // Clear any cached results
      index: 0,
      loading: false
    });
    if (ref.current.opened) {
      const text = ref.current.opened.element.textContent || "";
      const config = configs.find((cfg) => text.match(cfg.query));
      if (config) {
        openAutocomplete(ref.current.opened.element, text, config);
      }
    }
  }, [configs]);
  function setPositionFixed(fixed) {
    updateState({ fixed });
  }
  function setInputElement(inputElement) {
    updateState({ inputElement });
  }
  function selectItem(item) {
    const opened = ref.current.opened;
    if (opened == null ? void 0 : opened.element) {
      transformFinalFragment(opened.element, item.ref, opened.config, item);
      if (ref.current.inputElement) {
        const inputEvent = new Event("input", {
          bubbles: true,
          cancelable: true
        });
        ref.current.inputElement.dispatchEvent(inputEvent);
      }
    }
    updateState({
      index: 0,
      results: [],
      opened: null,
      loading: false,
      activeSearch: ""
    });
  }
  function executeActionCallback(item) {
    if (item.callback) {
      item.callback();
      const { opened } = ref.current;
      if (opened == null ? void 0 : opened.element) {
        opened.element.remove();
      }
      updateState({
        index: 0,
        results: [],
        opened: null,
        loading: false,
        activeSearch: ""
      });
    }
  }
  function onBeforeChanges(event) {
    let selection = document.getSelection();
    if (!selection || !selection.anchorNode) {
      return;
    }
    if (deleteSelection(selection, event)) {
      selection = document.getSelection();
      if (!selection || !selection.anchorNode) {
        return;
      }
    }
    fixCursorInsertion(event, selection);
    handleFragmentDeletion(event, selection);
    handleFragmentCreation(event, selection, configs, ref.current);
    removeBrokenFragments(event.currentTarget, configs);
  }
  function onSelectionChange() {
    const { inputElement, opened, closeAutocomplete: closeAutocomplete2, openAutocomplete: openAutocomplete2 } = ref.current;
    const selection = document.getSelection();
    const fragment = (selection == null ? void 0 : selection.anchorNode) && (getFragment(selection.anchorNode) || selection.anchorOffset === 0 && selection.anchorNode.previousSibling && getFragment(selection.anchorNode.previousSibling));
    const shouldOpened = fragment && !fragment.hasAttribute("data-integrity") && inputElement && inputElement.contains(fragment);
    if (opened && !shouldOpened) {
      closeAutocomplete2();
    } else if (shouldOpened && fragment && (!opened || opened.element !== fragment)) {
      const text = fragment.textContent || "";
      const config = configs.find((cfg) => text.match(cfg.query));
      if (config) {
        openAutocomplete2(fragment, text, config);
      }
    }
  }
  function onChanges() {
    const { inputElement, openAutocomplete: openAutocomplete2, closeAutocomplete: closeAutocomplete2 } = ref.current;
    const selection = document.getSelection();
    if (inputElement) {
      removeBrokenFragments(inputElement, configs);
    }
    const fragment = (selection == null ? void 0 : selection.anchorNode) && getFragment(selection.anchorNode);
    if (fragment && !fragment.hasAttribute("data-integrity")) {
      const text = fragment.textContent || "";
      const config = configs.find((cfg) => text.match(cfg.query));
      if (config) {
        openAutocomplete2(fragment, text, config);
      }
    } else if (ref.current.opened) {
      closeAutocomplete2();
    }
  }
  function onKeyDown(event) {
    const { opened, results, index, selectItem: selectItem2, closeAutocomplete: closeAutocomplete2 } = ref.current;
    if (!opened || !results.length) {
      return;
    }
    switch (event.keyCode) {
      case 40:
        event.preventDefault();
        updateState({
          index: index + 1 < results.length ? index + 1 : 0
          // Loop to the top if at the bottom
        });
        break;
      case 38:
        event.preventDefault();
        updateState({
          index: index - 1 >= 0 ? index - 1 : results.length - 1
          // Loop to the bottom if at the top
        });
        break;
      case 9:
      // tab
      case 13:
        if (results[index]) {
          event.preventDefault();
          event.stopPropagation();
          if (results[index].type === "no_results") {
            if (opened == null ? void 0 : opened.element) {
              markFragmentAsStale(opened.element);
            }
          } else if (results[index].type === "action" && typeof results[index].callback === "function") {
            executeActionCallback(results[index]);
          } else {
            selectItem2(results[index]);
          }
        }
        break;
      case 32:
        if (opened && results.length === 1 && results[0].type === "no_results") {
          event.preventDefault();
          markFragmentAsStale(opened.element);
          updateState({
            index: 0,
            results: [],
            opened: null,
            loading: false,
            activeSearch: ""
          });
          return;
        }
        break;
      case 27:
        markFragmentAsStale(opened.element);
        closeAutocomplete2();
        break;
    }
  }
  function closeAutocomplete() {
    updateState({
      opened: null,
      loading: false,
      results: [],
      index: 0,
      activeSearch: ""
    });
  }
  function openAutocomplete(node, text, config) {
    const fixed = ref.current.fixed;
    const rect = { top: 0, right: 0, bottom: 0, left: 0 };
    const nodeRect = node.getBoundingClientRect();
    rect.top = nodeRect.top;
    rect.right = nodeRect.right;
    rect.bottom = nodeRect.bottom;
    rect.left = nodeRect.left;
    if (!fixed && node.offsetParent) {
      const parentRect = node.offsetParent.getBoundingClientRect();
      rect.top -= parentRect.top;
      rect.right = rect.right - parentRect.right + parentRect.width;
      rect.left -= parentRect.left;
      rect.bottom = rect.bottom - parentRect.bottom + parentRect.height;
    }
    const ELEMENT_WIDTH = 200;
    const ELEMENT_HEIGHT = 300;
    const overflowX = nodeRect.left + 10 + ELEMENT_WIDTH - window.innerWidth;
    const overflowY = nodeRect.bottom + ELEMENT_HEIGHT - window.innerHeight;
    const x = overflowX > 0 ? rect.right + 15 : rect.left - 3;
    const y = overflowY > 0 ? rect.top - 3 : rect.bottom + 3;
    updateState({
      loading: true,
      index: 0,
      results: [],
      opened: {
        config,
        fixed,
        bottom: overflowY > 0,
        right: overflowX > 0,
        element: node,
        x,
        y
      },
      activeSearch: text
    });
    const currentConfig = ref.current.configs.find(
      (cfg) => cfg.query.toString() === config.query.toString()
    ) || config;
    const onResolve = (results = []) => {
      var _a;
      if (((_a = ref.current.opened) == null ? void 0 : _a.element) === node) {
        updateState({
          results,
          loading: false
        });
      }
    };
    const p = currentConfig.onMention(text, onResolve);
    if (p instanceof Promise) {
      p.then(onResolve, onResolve);
    } else if (p instanceof Array) {
      onResolve(p);
    }
  }
  function setActiveItemIndex(index) {
    updateState({ index });
  }
  function getTransformedValue$1() {
    return getTransformedValue(ref.current.inputElement);
  }
  function insertFragment$1(code, element = null) {
    insertFragment(code, element, configs, ref.current.inputElement);
  }
  function setValue(text) {
    const { inputElement, closeAutocomplete: closeAutocomplete2 } = ref.current;
    if (inputElement) {
      inputElement.innerHTML = getInitialHTML(text);
      removeBrokenFragments(inputElement, configs);
    }
    closeAutocomplete2();
  }
  return /* @__PURE__ */ jsx(RichMentionsContext.Provider, { value: __ctx__, children });
}
function RichMentionsAutocomplete({
  fixed = true,
  menuDirectionDown = false
}) {
  const {
    opened,
    index,
    //loading,
    results,
    setActiveItemIndex,
    selectItem,
    setPositionFixed,
    executeActionCallback
  } = useContext(RichMentionsContext);
  const listRef = useRef(null);
  const onSelectItem = (item) => () => {
    if (item.type === "action" && item.callback) {
      executeActionCallback(item);
    } else {
      selectItem(item);
    }
  };
  const onHoverOrFocusItem = (index2) => () => setActiveItemIndex(index2);
  const divAttributes = process.env.NODE_ENV !== "production" ? { "data-testid": "autocomplete" } : {};
  const itemAttributes = process.env.NODE_ENV !== "production" ? { "data-testid": "autocomplete_item" } : {};
  useEffect(() => {
    setPositionFixed(fixed);
  }, [fixed]);
  useEffect(() => {
    var _a;
    if (listRef.current && opened) {
      const selectableItems = listRef.current.querySelectorAll(".autocomplete-item");
      if (selectableItems.length > 0 && index < selectableItems.length) {
        let elementToScroll = selectableItems[index];
        const isGroupStart = index === 0 || ((_a = elementToScroll.previousElementSibling) == null ? void 0 : _a.classList.contains(
          "autocomplete-group-name"
        ));
        if (isGroupStart && elementToScroll.previousElementSibling) {
          elementToScroll = elementToScroll.previousElementSibling;
        }
        elementToScroll.scrollIntoView({ block: "nearest" });
      }
    }
  }, [index, opened]);
  const OFFSET = 3;
  return opened && results.length ? /* @__PURE__ */ jsx(
    "div",
    {
      ...divAttributes,
      className: "autocomplete",
      style: {
        position: fixed ? "fixed" : "absolute",
        left: opened.x + OFFSET + "px",
        ...menuDirectionDown ? {
          top: opened.y + "px"
        } : {
          bottom: `calc(100vh - ${opened.y}px)`,
          top: "auto"
        }
      },
      children: /* @__PURE__ */ jsx(
        "div",
        {
          ref: listRef,
          className: "autocomplete-list",
          style: {
            bottom: opened.bottom ? "0px" : "auto",
            right: opened.right ? "0px" : "auto"
          },
          children: results.map((item, i) => {
            const isGroupStart = i === 0 || item.groupName !== results[i - 1].groupName;
            const type = item.type;
            const isMention = item.type === "mention";
            return /* @__PURE__ */ jsxs(React__default.Fragment, { children: [
              isGroupStart && item.groupName && /* @__PURE__ */ jsx("div", { className: "autocomplete-group-name", children: item.groupName }),
              /* @__PURE__ */ jsxs(
                "button",
                {
                  className: `autocomplete-item autocomplete-item-${type} ${i === index ? "autocomplete-item-selected" : ""}`,
                  type: "button",
                  onClick: onSelectItem(item),
                  onMouseOver: onHoverOrFocusItem(i),
                  onFocus: onHoverOrFocusItem(i),
                  ...itemAttributes,
                  children: [
                    /* @__PURE__ */ jsx("span", { className: "autocomplete-item-name", children: item.name }),
                    isMention && /* @__PURE__ */ jsx("span", { className: "autocomplete-item-ref", children: item.display })
                  ]
                }
              )
            ] }, item.ref);
          })
        }
      )
    }
  ) : null;
}
function processMentionsInHTML(htmlContent) {
  const tempElement = document.createElement("div");
  tempElement.innerHTML = htmlContent;
  const mentionSpans = tempElement.querySelectorAll("span[data-rich-mentions]");
  mentionSpans.forEach((span) => {
    var _a;
    const encodedMention = span.getAttribute("data-rich-mentions");
    if (encodedMention) {
      const textNode = document.createTextNode(encodedMention);
      (_a = span.parentNode) == null ? void 0 : _a.replaceChild(textNode, span);
    }
  });
  const result = tempElement.innerText;
  return result;
}
const alertVariants = cva(
  "relative w-full rounded-lg border py-6 pl-6 pr-8 text-sm",
  {
    variants: {
      variant: {
        outline: "bg-gray-1 text-text-title",
        destructive: "bg-destructive text-gray-1"
      }
    },
    defaultVariants: {
      variant: "outline"
    }
  }
);
const Alert = React.forwardRef(({ className, variant, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    role: "alert",
    className: cn(alertVariants({ variant }), className),
    ...props
  }
));
Alert.displayName = "Alert";
const AlertTitle = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "h5",
  {
    ref,
    className: cn(
      "mb-1 font-medium text-lg leading-none tracking-tight",
      className
    ),
    ...props
  }
));
AlertTitle.displayName = "AlertTitle";
const AlertDescription = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("text-sm [&_p]:leading-relaxed", className),
    ...props
  }
));
AlertDescription.displayName = "AlertDescription";
const AlertDialog = AlertDialogPrimitive.Root;
const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
const AlertDialogPortal = AlertDialogPrimitive.Portal;
const AlertDialogOverlay = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AlertDialogPrimitive.Overlay,
  {
    className: cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props,
    ref
  }
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;
const AlertDialogContent = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsxs(AlertDialogPortal, { children: [
  /* @__PURE__ */ jsx(AlertDialogOverlay, {}),
  /* @__PURE__ */ jsx(
    AlertDialogPrimitive.Content,
    {
      ref,
      className: cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-popover p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      ),
      ...props
    }
  )
] }));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;
const AlertDialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    ),
    ...props
  }
);
AlertDialogHeader.displayName = "AlertDialogHeader";
const AlertDialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
AlertDialogFooter.displayName = "AlertDialogFooter";
const AlertDialogTitle = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AlertDialogPrimitive.Title,
  {
    ref,
    className: cn("text-lg font-semibold", className),
    ...props
  }
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;
const AlertDialogDescription = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AlertDialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-text-default", className),
    ...props
  }
));
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;
const AlertDialogAction = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AlertDialogPrimitive.Action,
  {
    ref,
    className: cn(buttonVariants({ variant: "primary" }), className),
    ...props
  }
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;
const AlertDialogCancel = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AlertDialogPrimitive.Cancel,
  {
    ref,
    className: cn(
      buttonVariants({ variant: "outline" }),
      "mt-2 sm:mt-0",
      className
    ),
    ...props
  }
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;
const avatarVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-sm text-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      size: {
        sm: "size-7",
        md: "size-8",
        lg: "size-10"
      }
    },
    defaultVariants: {
      size: "lg"
    }
  }
);
const Avatar = React.forwardRef(({ className, size, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    AvatarPrimitive.Root,
    {
      ref,
      className: cn(
        "relative flex shrink-0 overflow-hidden rounded-sm border",
        avatarVariants({ size }),
        className
      ),
      ...props
    }
  );
});
Avatar.displayName = AvatarPrimitive.Root.displayName;
const AvatarImage = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AvatarPrimitive.Image,
  {
    ref,
    className: cn("aspect-square h-full rounded-sm", className),
    ...props
  }
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;
const AvatarFallback = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AvatarPrimitive.Fallback,
  {
    ref,
    className: cn(
      "h-full w-full flex items-center justify-center rounded-sm bg-gray-3",
      className
    ),
    ...props
  }
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;
const Breadcrumb = React.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx("nav", { ref, "aria-label": "breadcrumb", ...props }));
Breadcrumb.displayName = "Breadcrumb";
const BreadcrumbList = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "ol",
  {
    ref,
    className: cn(
      "flex flex-wrap items-center gap-1.5 break-words text-sm text-text-default sm:gap-2.5",
      className
    ),
    ...props
  }
));
BreadcrumbList.displayName = "BreadcrumbList";
const BreadcrumbItem = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "li",
  {
    ref,
    className: cn("inline-flex items-center gap-1.5", className),
    ...props
  }
));
BreadcrumbItem.displayName = "BreadcrumbItem";
const BreadcrumbLink = React.forwardRef(({ asChild, className, iconName, isActive, children, ...props }, ref) => {
  const Comp = asChild ? Slot : "a";
  return /* @__PURE__ */ jsxs(
    Comp,
    {
      ref,
      className: cn(
        "transition-colors hover:text-text-title flex items-center gap-1.5",
        isActive ? "text-text-title" : "text-text-default",
        className
      ),
      ...props,
      children: [
        iconName && /* @__PURE__ */ jsx(Icon, { name: iconName }),
        children
      ]
    }
  );
});
BreadcrumbLink.displayName = "BreadcrumbLink";
const BreadcrumbPage = React.forwardRef(({ className, iconName, isActive, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  "span",
  {
    ref,
    role: "link",
    "aria-disabled": "true",
    "aria-current": "page",
    className: cn(
      "font-normal flex items-center gap-1.5",
      isActive ? "text-text-title" : "text-text-default",
      className
    ),
    ...props,
    children: [
      iconName && /* @__PURE__ */ jsx(Icon, { name: iconName }),
      children
    ]
  }
));
BreadcrumbPage.displayName = "BreadcrumbPage";
const BreadcrumbSeparator = ({
  children,
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "li",
  {
    role: "presentation",
    "aria-hidden": "true",
    className: cn("[&>svg]:size-3.5", className),
    ...props,
    children: children ?? /* @__PURE__ */ jsx(ChevronRightIcon, {})
  }
);
BreadcrumbSeparator.displayName = "BreadcrumbSeparator";
const BreadcrumbEllipsis = ({
  className,
  ...props
}) => /* @__PURE__ */ jsxs(
  "span",
  {
    role: "presentation",
    "aria-hidden": "true",
    className: cn("flex h-9 w-9 items-center justify-center", className),
    ...props,
    children: [
      /* @__PURE__ */ jsx(DotsHorizontalIcon, { className: "size-4" }),
      /* @__PURE__ */ jsx("span", { className: "sr-only", children: "More" })
    ]
  }
);
BreadcrumbEllipsis.displayName = "BreadcrumbElipssis";
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DayPicker,
    {
      showOutsideDays,
      className: cn("p-3", className),
      classNames: {
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-text-default rounded-md w-8 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-overlay-darken-300 [&:has([aria-selected].day-outside)]:bg-overlay-lighten-100 [&:has([aria-selected].day-range-end)]:rounded-r-md",
          props.mode === "range" ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md" : "[&:has([aria-selected])]:rounded-md"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-8 w-8 p-0 font-normal aria-selected:opacity-100"
        ),
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        day_selected: "bg-primary text-text-inverted-title hover:bg-primary hover:text-text-inverted-title focus:bg-primary focus:text-text-inverted-title",
        day_today: "bg-overlay-darken-300 text-text-weak",
        day_outside: "day-outside text-text-default aria-selected:bg-overlay-lighten-100 aria-selected:text-text-default",
        day_disabled: "text-text-default opacity-50",
        day_range_middle: "aria-selected:bg-overlay-darken-300 aria-selected:text-text-weak",
        day_hidden: "invisible",
        ...classNames
      },
      components: {
        IconLeft: ({ className: className2, ...props2 }) => /* @__PURE__ */ jsx(
          Icon,
          {
            name: "chevron-left",
            className: cn("h-4 w-4", className2),
            ...props2
          }
        ),
        IconRight: ({ className: className2, ...props2 }) => /* @__PURE__ */ jsx(
          Icon,
          {
            name: "chevron-right",
            className: cn("h-4 w-4", className2),
            ...props2
          }
        )
      },
      ...props
    }
  );
}
Calendar.displayName = "Calendar";
const Card = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn(
      "rounded-xl border bg-card text-card-foreground shadow",
      className
    ),
    ...props
  }
));
Card.displayName = "Card";
const CardHeader = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("flex flex-col space-y-1.5 p-6", className),
    ...props
  }
));
CardHeader.displayName = "CardHeader";
const CardTitle = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("font-semibold leading-none tracking-tight", className),
    ...props
  }
));
CardTitle.displayName = "CardTitle";
const CardDescription = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("text-sm text-text-default", className),
    ...props
  }
));
CardDescription.displayName = "CardDescription";
const CardContent = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("p-6 pt-0", className), ...props }));
CardContent.displayName = "CardContent";
const CardFooter = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("flex items-center p-6 pt-0", className),
    ...props
  }
));
CardFooter.displayName = "CardFooter";
const THEMES = { light: "", dark: ".dark" };
const ChartContext = React.createContext(null);
function useChart() {
  const context = React.useContext(ChartContext);
  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }
  return context;
}
const ChartContainer = React.forwardRef(
  ({ id, className, children, config, aspectRatio = "aspect-video", ...props }, ref) => {
    const uniqueId = React.useId();
    const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;
    return /* @__PURE__ */ jsx(ChartContext.Provider, { value: { config }, children: /* @__PURE__ */ jsxs(
      "div",
      {
        "data-chart": chartId,
        ref,
        className: cn(
          `flex ${aspectRatio} justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-text-default [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none`,
          className
        ),
        ...props,
        children: [
          /* @__PURE__ */ jsx(ChartStyle, { id: chartId, config }),
          /* @__PURE__ */ jsx(RechartsPrimitive.ResponsiveContainer, { children })
        ]
      }
    ) });
  }
);
ChartContainer.displayName = "Chart";
const ChartStyle = ({ id, config }) => {
  const colorConfig = Object.entries(config).filter(
    ([, config2]) => config2.theme || config2.color
  );
  if (!colorConfig.length) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    "style",
    {
      dangerouslySetInnerHTML: {
        __html: Object.entries(THEMES).map(
          ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig.map(([key, itemConfig]) => {
            var _a;
            const color = ((_a = itemConfig.theme) == null ? void 0 : _a[theme]) || itemConfig.color;
            return color ? `  --color-${key}: ${color};` : null;
          }).join("\n")}
}
`
        ).join("\n")
      }
    }
  );
};
const ChartTooltip = RechartsPrimitive.Tooltip;
const ChartTooltipContent = React.forwardRef(
  ({
    active,
    payload,
    className,
    indicator = "dot",
    hideLabel = false,
    hideIndicator = false,
    label,
    labelFormatter,
    labelClassName,
    formatter,
    color,
    nameKey,
    labelKey
  }, ref) => {
    const { config } = useChart();
    const tooltipLabel = React.useMemo(() => {
      var _a;
      if (hideLabel || !(payload == null ? void 0 : payload.length)) {
        return null;
      }
      const [item] = payload;
      const key = `${labelKey || (item == null ? void 0 : item.dataKey) || (item == null ? void 0 : item.name) || "value"}`;
      const itemConfig = getPayloadConfigFromPayload(config, item, key);
      const value = !labelKey && typeof label === "string" ? ((_a = config[label]) == null ? void 0 : _a.label) || label : itemConfig == null ? void 0 : itemConfig.label;
      if (labelFormatter) {
        return /* @__PURE__ */ jsx("div", { className: cn("font-medium", labelClassName), children: labelFormatter(value, payload) });
      }
      if (!value) {
        return null;
      }
      return /* @__PURE__ */ jsx("div", { className: cn("font-medium", labelClassName), children: value });
    }, [
      label,
      labelFormatter,
      payload,
      hideLabel,
      labelClassName,
      config,
      labelKey
    ]);
    if (!active || !(payload == null ? void 0 : payload.length)) {
      return null;
    }
    const nestLabel = payload.length === 1 && indicator !== "dot";
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(
          "grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-surface-background px-2.5 py-1.5 text-xs shadow-xl",
          className
        ),
        children: [
          !nestLabel ? tooltipLabel : null,
          /* @__PURE__ */ jsx("div", { className: "grid gap-1.5", children: payload.map((item, index) => {
            const key = `${nameKey || item.name || item.dataKey || "value"}`;
            const itemConfig = getPayloadConfigFromPayload(config, item, key);
            const indicatorColor = color || item.payload.fill || item.color;
            return /* @__PURE__ */ jsx(
              "div",
              {
                className: cn(
                  "flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-text-default",
                  indicator === "dot" && "items-center"
                ),
                children: formatter && (item == null ? void 0 : item.value) !== void 0 && item.name ? formatter(item.value, item.name, item, index, item.payload) : /* @__PURE__ */ jsxs(Fragment$1, { children: [
                  (itemConfig == null ? void 0 : itemConfig.icon) ? /* @__PURE__ */ jsx(itemConfig.icon, {}) : !hideIndicator && /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: cn(
                        "shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg]",
                        {
                          "h-2.5 w-2.5": indicator === "dot",
                          "w-1": indicator === "line",
                          "w-0 border-[1.5px] border-dashed bg-transparent": indicator === "dashed",
                          "my-0.5": nestLabel && indicator === "dashed"
                        }
                      ),
                      style: {
                        "--color-bg": indicatorColor,
                        "--color-border": indicatorColor
                      }
                    }
                  ),
                  /* @__PURE__ */ jsxs(
                    "div",
                    {
                      className: cn(
                        "flex flex-1 justify-between leading-none",
                        nestLabel ? "items-end" : "items-center"
                      ),
                      children: [
                        /* @__PURE__ */ jsxs("div", { className: "grid gap-1.5", children: [
                          nestLabel ? tooltipLabel : null,
                          /* @__PURE__ */ jsx("span", { className: "text-text-default", children: (itemConfig == null ? void 0 : itemConfig.label) || item.name })
                        ] }),
                        item.value && /* @__PURE__ */ jsx("span", { className: "font-mono font-medium tabular-nums text-text-title", children: item.value.toLocaleString() })
                      ]
                    }
                  )
                ] })
              },
              item.dataKey
            );
          }) })
        ]
      }
    );
  }
);
ChartTooltipContent.displayName = "ChartTooltip";
const ChartLegend = RechartsPrimitive.Legend;
const ChartLegendContent = React.forwardRef(
  ({ className, hideIcon = false, payload, verticalAlign = "bottom", nameKey }, ref) => {
    const { config } = useChart();
    if (!(payload == null ? void 0 : payload.length)) {
      return null;
    }
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "flex items-center justify-center gap-4",
          verticalAlign === "top" ? "pb-3" : "pt-3",
          className
        ),
        children: payload.map((item) => {
          const key = `${nameKey || item.dataKey || "value"}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);
          return /* @__PURE__ */ jsxs(
            "div",
            {
              className: cn(
                "flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-text-default"
              ),
              children: [
                (itemConfig == null ? void 0 : itemConfig.icon) && !hideIcon ? /* @__PURE__ */ jsx(itemConfig.icon, {}) : /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: "size-2 shrink-0 rounded-[2px]",
                    style: {
                      backgroundColor: item.color
                    }
                  }
                ),
                itemConfig == null ? void 0 : itemConfig.label
              ]
            },
            item.value
          );
        })
      }
    );
  }
);
ChartLegendContent.displayName = "ChartLegend";
function getPayloadConfigFromPayload(config, payload, key) {
  if (typeof payload !== "object" || payload === null) {
    return void 0;
  }
  const payloadPayload = "payload" in payload && typeof payload.payload === "object" && payload.payload !== null ? payload.payload : void 0;
  let configLabelKey = key;
  if (key in payload && typeof payload[key] === "string") {
    configLabelKey = payload[key];
  } else if (payloadPayload && key in payloadPayload && typeof payloadPayload[key] === "string") {
    configLabelKey = payloadPayload[key];
  }
  return configLabelKey in config ? config[configLabelKey] : config[key];
}
const LineChart = RechartsPrimitive.LineChart;
const BarChart = RechartsPrimitive.BarChart;
const AreaChart = RechartsPrimitive.AreaChart;
const PieChart = RechartsPrimitive.PieChart;
const Line = RechartsPrimitive.Line;
const Bar = RechartsPrimitive.Bar;
const Area = RechartsPrimitive.Area;
const Pie = RechartsPrimitive.Pie;
const Cell = RechartsPrimitive.Cell;
const XAxis = RechartsPrimitive.XAxis;
const YAxis = RechartsPrimitive.YAxis;
const CartesianGrid = RechartsPrimitive.CartesianGrid;
const Legend = RechartsPrimitive.Legend;
const ResponsiveContainer = RechartsPrimitive.ResponsiveContainer;
const ReferenceLine = RechartsPrimitive.ReferenceLine;
const Brush = RechartsPrimitive.Brush;
const ContextMenu = ContextMenuPrimitive.Root;
const ContextMenuTrigger = ContextMenuPrimitive.Trigger;
const ContextMenuRadioGroup = ContextMenuPrimitive.RadioGroup;
const ContextMenuSub = ContextMenuPrimitive.Sub;
const ContextMenuGroup = ContextMenuPrimitive.Group;
const ContextMenuContent = React__default.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(ContextMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx(
  ContextMenuPrimitive.Content,
  {
    ref,
    className: cn(
      "z-50 min-w-[15rem] overflow-hidden rounded border p-1 bg-popover text-popover-foreground shadow-md",
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
ContextMenuContent.displayName = ContextMenuPrimitive.Content.displayName;
const ContextMenuItem = React__default.forwardRef(({ className, inset, destructive, icon, ...props }, ref) => /* @__PURE__ */ jsxs(Fragment$1, { children: [
  icon && /* @__PURE__ */ jsx(Icon, { name: icon, className: "mr-2" }),
  /* @__PURE__ */ jsx(
    ContextMenuPrimitive.Item,
    {
      ref,
      className: cn(
        "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:text-text-weak data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-overlay-darken-300 ",
        inset && "pl-8",
        destructive && "text-destructive",
        className
      ),
      ...props
    }
  )
] }));
ContextMenuItem.displayName = ContextMenuPrimitive.ContextMenuItem.displayName;
const ContextMenuLabel = React__default.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx(
  ContextMenuPrimitive.Label,
  {
    ref,
    className: `${cn(
      "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 uppercase outline-none transition-colors text-xxs",
      inset && "pl-8"
    )} ${className} text-muted`,
    ...props
  }
));
ContextMenuLabel.displayName = ContextMenuPrimitive.ContextMenuLabel.displayName;
const ContextMenuShortcut = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn("ml-auto text-xs tracking-widest opacity-60", className),
      ...props
    }
  );
};
ContextMenuShortcut.displayName = "ContextMenuShortcut";
const ContextMenuIcon = ({ className, name, ...props }) => {
  return /* @__PURE__ */ jsx(Icon, { name, className: cn("mr-2", className), ...props });
};
ContextMenuIcon.displayName = "ContextMenuIcon";
const ContextMenuSubTrigger = React__default.forwardRef(({ className, inset, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  ContextMenuPrimitive.SubTrigger,
  {
    ref,
    className: cn(
      "flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-overlay-darken-300 data-[state=open]:bg-overlay-darken-300 data-[highlighted]:bg-overlay-darken-300",
      inset && "pl-8",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx(ChevronRightIcon, { className: "ml-auto size-4" })
    ]
  }
));
ContextMenuSubTrigger.displayName = ContextMenuPrimitive.SubTrigger.displayName;
const ContextMenuSubContent = React__default.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ContextMenuPrimitive.Content,
  {
    ref,
    className: cn(
      "z-50 min-w-[15rem] overflow-hidden rounded border p-1 bg-popover text-popover-foreground shadow-md",
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
));
ContextMenuSubContent.displayName = ContextMenuPrimitive.SubContent.displayName;
const ContextMenuSeparator = React__default.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ContextMenuPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-gray-5", className),
    ...props
  }
));
ContextMenuSeparator.displayName = ContextMenuPrimitive.Separator.displayName;
const ContextMenuCheckboxItem = React__default.forwardRef(({ className, children, checked, ...props }, ref) => /* @__PURE__ */ jsxs(
  ContextMenuPrimitive.CheckboxItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-overlay-darken-300 focus:text-text-weak data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    checked,
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(ContextMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(CheckIcon$2, { className: "size-4" }) }) }),
      children
    ]
  }
));
ContextMenuCheckboxItem.displayName = ContextMenuPrimitive.CheckboxItem.displayName;
const ContextMenuRadioItem = React__default.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  ContextMenuPrimitive.RadioItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-overlay-darken-300 focus:text-text-weak data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(ContextMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(DotFilledIcon, { className: "size-4 fill-current" }) }) }),
      children
    ]
  }
));
ContextMenuRadioItem.displayName = ContextMenuPrimitive.RadioItem.displayName;
const Drawer = DialogPrimitive.Root;
const DrawerTrigger = DialogPrimitive.Trigger;
const DrawerClose = DialogPrimitive.Close;
const DrawerPortal = DialogPrimitive.Portal;
const DrawerOverlay = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Overlay,
  {
    className: cn(
      "fixed inset-0 z-50 bg-gray-alpha-2  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props,
    ref
  }
));
DrawerOverlay.displayName = DialogPrimitive.Overlay.displayName;
const drawerVariants = cva(
  "fixed z-50 gap-4 bg-surface-base p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom: "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-96 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
        right: "inset-y-0 right-0 h-full w-96 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
      }
    },
    defaultVariants: {
      side: "right"
    }
  }
);
const DrawerContent = React.forwardRef(
  ({ side = "right", displayOverlay = true, className, children, ...props }, ref) => /* @__PURE__ */ jsxs(DrawerPortal, { container: props == null ? void 0 : props.container, children: [
    displayOverlay && /* @__PURE__ */ jsx(DrawerOverlay, {}),
    /* @__PURE__ */ jsx(
      DialogPrimitive.Content,
      {
        ref,
        className: cn(drawerVariants({ side }), className),
        ...props,
        children
      }
    )
  ] })
);
DrawerContent.displayName = DialogPrimitive.Content.displayName;
const DrawerFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
DrawerFooter.displayName = "DrawerFooter";
const DrawerTitle = React.forwardRef(({ className, icon, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("text-lg font-semibold text-text-title", className),
    ...props,
    children: /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2.5", children: [
        icon && /* @__PURE__ */ jsx(Icon, { name: icon, size: 16 }),
        /* @__PURE__ */ jsx(Text$1, { as: "h4", variant: "title3", emphasized: true, children: props.children })
      ] }),
      /* @__PURE__ */ jsxs(
        DialogPrimitive.Close,
        {
          ref: null,
          className: "rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary",
          children: [
            /* @__PURE__ */ jsx(Icon, { name: "cross-large", size: 18 }),
            /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
          ]
        }
      )
    ] })
  }
));
DrawerTitle.displayName = DialogPrimitive.Title.displayName;
const DrawerToggle = ({
  className,
  onClick,
  side,
  isOpen,
  tooltip
}) => {
  const tooltipSide = side === "left" && isOpen || side === "right" && !isOpen ? "left" : "right";
  const button = /* @__PURE__ */ jsxs(
    "button",
    {
      className: cn(
        "width-[18px] height-[18px] relative group block hover:bg-overlay-darken-300 rounded-xs",
        className
      ),
      onClick,
      "data-testid": "drawer-toggle",
      children: [
        /* @__PURE__ */ jsxs(
          "svg",
          {
            className: cn("hidden group-hover:block", {
              "-scale-x-100": side === "left" && isOpen || side === "right" && !isOpen
            }),
            xmlns: "http://www.w3.org/2000/svg",
            width: "18",
            height: "18",
            viewBox: "0 0 18 18",
            fill: "none",
            children: [
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M4 5C4 4.44772 4.44772 4 5 4H13C13.5523 4 14 4.44772 14 5V13C14 13.5523 13.5523 14 13 14H5C4.44772 14 4 13.5523 4 13V5Z",
                  stroke: "currentColor"
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  fillRule: "evenodd",
                  clipRule: "evenodd",
                  d: "M9.26937 12.0815L12.2418 9.04077L9.26937 5.99998L8.55861 6.7271L10.3177 8.52662L5.5 8.52662L5.5 9.55492L10.3176 9.55492L8.55861 11.3544L9.26937 12.0815Z",
                  fill: "currentColor"
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          "svg",
          {
            className: cn("group-hover:hidden", {
              "-scale-x-100": side === "left" && isOpen || side === "right" && !isOpen
            }),
            xmlns: "http://www.w3.org/2000/svg",
            width: "18",
            height: "18",
            viewBox: "0 0 18 18",
            fill: "none",
            children: [
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M4 5C4 4.44772 4.44772 4 5 4H13C13.5523 4 14 4.44772 14 5V13C14 13.5523 13.5523 14 13 14H5C4.44772 14 4 13.5523 4 13V5Z",
                  stroke: "currentColor"
                }
              ),
              /* @__PURE__ */ jsx("path", { d: "M5.25 5.25H6.25V12.75H5.25V5.25Z", fill: "currentColor" })
            ]
          }
        )
      ]
    }
  );
  if (tooltip) {
    return /* @__PURE__ */ jsx(TooltipProvider, { children: /* @__PURE__ */ jsxs(Tooltip, { children: [
      /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: button }),
      /* @__PURE__ */ jsx(
        TooltipContent,
        {
          className: "flex items-center px-2 py-1",
          side: tooltipSide,
          children: /* @__PURE__ */ jsx(Text$1, { variant: "body", className: "text-sm", children: tooltip })
        }
      )
    ] }) });
  }
  return button;
};
const sizes = {
  small: "h-7 text-12 px-3",
  medium: "text-14 h-8 px-2.5 py-2",
  large: "text-14 h-10 px-3"
};
const styles = {
  default: "border-input",
  ghost: "border-none shadow-none p-0"
};
const inputVariants = cva(
  "flex w-full rounded border bg-transparent shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-text-title placeholder:text-text-default focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:border-overlay-darken-300 disabled:bg-overlay-darken-300",
  {
    variants: {
      size: sizes,
      variant: styles
    },
    defaultVariants: {
      size: "medium"
    }
  }
);
const Input = React.forwardRef(
  ({ className, type, hasError, hasWarning, size, variant, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "input",
      {
        type,
        className: cn(
          inputVariants({
            size,
            variant
          }),
          {
            "border-destructive-80": hasError,
            // TODO(ralph): Followup with design on this non-standard color
            "border-[#BF8700]": hasWarning
          },
          className
        ),
        ref,
        ...props
      }
    );
  }
);
Input.displayName = "Input";
const labelVariants = cva(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
);
const Label = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  LabelPrimitive.Root,
  {
    ref,
    className: cn(labelVariants(), className),
    ...props
  }
));
Label.displayName = LabelPrimitive.Root.displayName;
const PaginationContext = React.createContext({
  size: "md",
  iconOnly: false
});
const Pagination = ({
  className,
  size = "md",
  iconOnly = false,
  ...props
}) => /* @__PURE__ */ jsx(PaginationContext.Provider, { value: { size, iconOnly }, children: /* @__PURE__ */ jsx(
  "nav",
  {
    role: "navigation",
    "data-testid": "pagination",
    "aria-label": "pagination",
    className: cn("mx-auto flex w-full justify-center", className),
    ...props
  }
) });
Pagination.displayName = "Pagination";
const PaginationContent = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "ul",
  {
    ref,
    className: cn("flex flex-row items-center gap-1", className),
    ...props
  }
));
PaginationContent.displayName = "PaginationContent";
const PaginationItem = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("li", { ref, className, ...props }));
PaginationItem.displayName = "PaginationItem";
const PaginationLink = ({
  className,
  isActive,
  size: itemSize,
  ...props
}) => {
  const { size: contextSize } = React.useContext(PaginationContext);
  const buttonSize = itemSize ?? (contextSize === "sm" ? "sm" : "icon-md");
  return /* @__PURE__ */ jsx(
    Button,
    {
      "aria-current": isActive ? "page" : void 0,
      variant: isActive ? "outline" : "ghost",
      size: buttonSize,
      className: cn(contextSize === "sm" && "text-sm", className),
      ...props
    }
  );
};
PaginationLink.displayName = "PaginationLink";
const PaginationPrevious = ({
  className,
  iconOnly: itemIconOnly,
  size: itemSize,
  ...props
}) => {
  const { size: contextSize, iconOnly: contextIconOnly } = React.useContext(PaginationContext);
  const size = itemSize ?? contextSize ?? "md";
  const iconOnly = itemIconOnly ?? contextIconOnly ?? false;
  return /* @__PURE__ */ jsxs(
    PaginationLink,
    {
      "aria-label": "Go to previous page",
      size: iconOnly ? size === "sm" ? "icon-sm" : "icon-md" : size,
      className: cn(
        iconOnly ? "" : "gap-1 pl-2.5",
        size === "sm" && !iconOnly && "text-sm",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx(Icon, { name: "chevron-left", size: size === "sm" ? 12 : 16 }),
        !iconOnly && /* @__PURE__ */ jsx("span", { children: "Previous" })
      ]
    }
  );
};
PaginationPrevious.displayName = "PaginationPrevious";
const PaginationNext = ({
  className,
  iconOnly: itemIconOnly,
  size: itemSize,
  ...props
}) => {
  const { size: contextSize, iconOnly: contextIconOnly } = React.useContext(PaginationContext);
  const size = itemSize ?? contextSize ?? "md";
  const iconOnly = itemIconOnly ?? contextIconOnly ?? false;
  return /* @__PURE__ */ jsxs(
    PaginationLink,
    {
      "aria-label": "Go to next page",
      size: iconOnly ? size === "sm" ? "icon-sm" : "icon-md" : size,
      className: cn(
        iconOnly ? "" : "gap-1 pr-2.5",
        size === "sm" && !iconOnly && "text-sm",
        className
      ),
      ...props,
      children: [
        !iconOnly && /* @__PURE__ */ jsx("span", { children: "Next" }),
        /* @__PURE__ */ jsx(Icon, { name: "chevron-right", size: size === "sm" ? 12 : 16 })
      ]
    }
  );
};
PaginationNext.displayName = "PaginationNext";
const PaginationEllipsis = ({
  className,
  ...props
}) => {
  const { size } = React.useContext(PaginationContext);
  return /* @__PURE__ */ jsxs(
    "span",
    {
      "aria-hidden": true,
      className: cn(
        "flex h-9 w-9 items-center justify-center",
        size === "sm" && "h-8 w-8",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx(
          DotsHorizontalIcon,
          {
            className: cn("size-4", size === "sm" && "size-3.5")
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "sr-only", children: "More pages" })
      ]
    }
  );
};
PaginationEllipsis.displayName = "PaginationEllipsis";
const RadioGroup = React.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Root,
    {
      className: cn("grid gap-2", className),
      ...props,
      ref
    }
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;
const RadioGroupItem = React.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Item,
    {
      ref,
      className: cn(
        "font-sans aspect-square h-4 w-4 rounded-full border border-primary text-primary shadow focus:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(RadioGroupPrimitive.Indicator, { className: "flex items-center justify-center", children: /* @__PURE__ */ jsx("div", { className: "size-2.5 shrink-0 rounded-full bg-primary" }) })
    }
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;
const Switch = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SwitchPrimitives.Root,
  {
    className: cn(
      "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-200",
      className
    ),
    ...props,
    ref,
    children: /* @__PURE__ */ jsx(
      SwitchPrimitives.Thumb,
      {
        className: cn(
          "pointer-events-none block h-4 w-4 rounded-full bg-white-opacity-1 shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
        )
      }
    )
  }
));
Switch.displayName = SwitchPrimitives.Root.displayName;
const Table = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { className: cn("relative w-full overflow-auto", className), children: /* @__PURE__ */ jsx(
  "table",
  {
    ref,
    className: cn("w-full caption-bottom text-sm", className),
    ...props
  }
) }));
Table.displayName = "Table";
const TableHeader = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "thead",
  {
    ref,
    className: cn("group [&_tr]:border-b", className),
    ...props
  }
));
TableHeader.displayName = "TableHeader";
const TableBody = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "tbody",
  {
    ref,
    className: cn("[&_tr:last-child]:border-0", className),
    ...props
  }
));
TableBody.displayName = "TableBody";
const TableFooter = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "tfoot",
  {
    ref,
    className: cn(
      "border-t hover:bg-overlay-darken-300 font-medium [&>tr]:last:border-b-0",
      className
    ),
    ...props
  }
));
TableFooter.displayName = "TableFooter";
const TableRow = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "tr",
  {
    ref,
    className: cn(
      "border-b transition-colors group-hover:bg-transparent hover:bg-overlay-darken-300 data-[state=selected]:bg-muted",
      className
    ),
    ...props
  }
));
TableRow.displayName = "TableRow";
const TableHead = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "th",
  {
    ref,
    className: cn(
      "h-10 px-2 text-left align-middle font-medium text-text-default [&>[role=checkbox]]:translate-y-0.5",
      className
    ),
    ...props
  }
));
TableHead.displayName = "TableHead";
const TableCell = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "td",
  {
    ref,
    className: cn(
      "p-2 align-middle [&>[role=checkbox]]:translate-y-0.5",
      className
    ),
    ...props
  }
));
TableCell.displayName = "TableCell";
const TableCaption = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "caption",
  {
    ref,
    className: cn("mt-4 text-sm text-text-default", className),
    ...props
  }
));
TableCaption.displayName = "TableCaption";
const TextArea = React.forwardRef(
  ({ className, hasError, hasWarning, disableResize = false, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "textarea",
      {
        className: cn(
          "border-input file:text-text-title placeholder:text-text-default placeholder:text-base focus-visible:ring-ring disabled:bg-overlay-darken-300 disabled:border-overlay-darken-300 flex w-full rounded border bg-transparent shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed px-2.5 py-2",
          {
            "border-surface-sentiment-subtle-destructive": hasError,
            // TODO(ralph): Followup with design on this non-standard color
            "border-[#BF8700]": hasWarning,
            "resize-none": disableResize
          },
          className
        ),
        ref,
        ...props
      }
    );
  }
);
TextArea.displayName = "Textarea";
const Tabs = ({ variant = "default", className, ...props }) => {
  return /* @__PURE__ */ jsx(
    TabsPrimitive.Root,
    {
      "data-variant": variant,
      className: cn("group", className),
      ...props
    }
  );
};
const TabsList = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.List,
  {
    ref,
    className: cn(
      "inline-flex h-7 p-0.5 items-center justify-center rounded-sm bg-overlay-darken-100 text-text-default group-data-[variant=line]:bg-transparent group-data-[variant=line]:border-b group-data-[variant=line]:border-b-border group-data-[variant=line]:p-0 group-data-[variant=line]:justify-start group-data-[variant=line]:rounded-none",
      className
    ),
    ...props
  }
));
TabsList.displayName = TabsPrimitive.List.displayName;
const TabsTrigger = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.Trigger,
  {
    ref,
    className: cn(
      'inline-flex items-center justify-center grow min-h-6 whitespace-nowrap rounded-xs px-2 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-surface-elevated data-[state=active]:text-text-title data-[state=active]:shadow group-data-[variant=line]:relative group-data-[variant=line]:rounded-none group-data-[variant=line]:py-1.5 group-data-[variant=line]:h-[27px] group-data-[variant=line]:bg-transparent group-data-[variant=line]:px-4 group-data-[variant=line]:grow-0 group-data-[variant=line]:data-[state=active]:bg-transparent group-data-[variant=line]:data-[state=active]:shadow-none group-data-[variant=line]:data-[state=active]:after:content-[""] group-data-[variant=line]:data-[state=active]:after:absolute group-data-[variant=line]:data-[state=active]:after:bottom-[-1px] group-data-[variant=line]:data-[state=active]:after:left-0 group-data-[variant=line]:data-[state=active]:after:right-0 group-data-[variant=line]:data-[state=active]:after:h-px group-data-[variant=line]:data-[state=active]:after:bg-primary',
      className
    ),
    ...props
  }
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
const TabsContent = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.Content,
  {
    ref,
    className: cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    ),
    ...props
  }
));
TabsContent.displayName = TabsPrimitive.Content.displayName;
const magnitudes = ["k", "m", "b", "t"];
const roundToFirstDecimal = (int) => {
  return Math.round(int * 10) / 10;
};
const roundToCharacterLimit = (int, order) => {
  const rounded = roundToFirstDecimal(int);
  const roundedString = rounded.toString();
  const initCharacters = int.toString().split(".");
  const magnitude = magnitudes[order];
  if (roundedString.length > 3) {
    const twiceRounded = Math.round(rounded);
    const twiceRoundedString = twiceRounded.toString();
    if (twiceRoundedString.length === initCharacters[0].length) {
      return twiceRoundedString + magnitude;
    } else {
      return twiceRounded / 1e3 + magnitudes[order + 1];
    }
  }
  return roundedString + magnitude;
};
const formatNumber = (int) => {
  if (int >= 1e9) {
    return roundToCharacterLimit(int / 1e9, 2);
  }
  if (int >= 1e6) {
    return roundToCharacterLimit(int / 1e6, 1);
  }
  if (int >= 1e3) {
    return roundToCharacterLimit(int / 1e3, 0);
  }
  return int.toString();
};
function FilterValidationTabs({
  filteredCounts,
  totalCounts,
  disabled,
  onTabChange,
  activeTab,
  isLoading,
  translations = {
    all: "All",
    valid: "Valid",
    invalid: "Invalid",
    records: "Records"
  }
}) {
  var _a;
  const countData = useMemo(() => {
    if (activeTab === "error") {
      return { count: filteredCounts == null ? void 0 : filteredCounts.error, label: translations.invalid };
    }
    if (activeTab === "valid") {
      return { count: filteredCounts == null ? void 0 : filteredCounts.valid, label: translations.valid };
    }
    return { count: filteredCounts == null ? void 0 : filteredCounts.total, label: translations.records };
  }, [filteredCounts, activeTab, translations]);
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
    /* @__PURE__ */ jsx("div", { className: "flex items-center gap-1", children: /* @__PURE__ */ jsxs("div", { className: "relative flex items-center justify-center gap-1", children: [
      /* @__PURE__ */ jsx(
        Text$1,
        {
          className: "animate-in fade-in",
          color: (countData == null ? void 0 : countData.label) === "errors" && (countData == null ? void 0 : countData.count) > 0 ? "destructive" : "text-inverted-muted",
          variant: "subheadline",
          children: ((_a = countData == null ? void 0 : countData.count) == null ? void 0 : _a.toLocaleString()) ?? 0
        }
      ),
      /* @__PURE__ */ jsx(
        Text$1,
        {
          color: (countData == null ? void 0 : countData.label) === "errors" ? "destructive" : "text-inverted-muted",
          className: "text-xs",
          variant: "subheadline",
          children: countData == null ? void 0 : countData.label
        }
      )
    ] }) }),
    totalCounts && !isLoading && /* @__PURE__ */ jsx(
      TabsComponent,
      {
        disabled,
        filteredCounts,
        onTabChange,
        activeTab,
        translations
      }
    )
  ] });
}
const TabsComponent = ({
  filteredCounts,
  onTabChange,
  disabled,
  activeTab,
  translations
}) => {
  const handleTabChange = (value) => {
    onTabChange == null ? void 0 : onTabChange(value);
  };
  return /* @__PURE__ */ jsx(
    Tabs,
    {
      value: activeTab ?? "none",
      onValueChange: handleTabChange,
      className: cn(
        "w-fit rounded-sm bg-surface-sentiment-subtle-neutral theme-static"
      ),
      children: /* @__PURE__ */ jsxs(TabsList, { children: [
        /* @__PURE__ */ jsx(TabsTrigger, { className: "w-24", value: "none", disabled, children: /* @__PURE__ */ jsx(
          TabWithoutCount,
          {
            active: activeTab === "none",
            label: translations.all
          }
        ) }),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "w-[1px] h-4 bg-overlay-darken-300 opacity-0 transition-opacity duration-300",
              {
                "opacity-100": activeTab === "error"
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(TabsTrigger, { className: "w-24", value: "valid", disabled, children: (filteredCounts == null ? void 0 : filteredCounts.valid) > 0 ? /* @__PURE__ */ jsx(
          TabWithCount,
          {
            label: translations.valid,
            color: "primary",
            count: filteredCounts == null ? void 0 : filteredCounts.valid
          }
        ) : /* @__PURE__ */ jsx(TabWithoutCount, { label: translations.valid, color: "primary" }) }),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "w-[1px] h-4 bg-overlay-darken-300 opacity-0 transition-opacity duration-300",
              {
                "opacity-100": activeTab === "none"
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(TabsTrigger, { className: "w-24", value: "error", disabled, children: (filteredCounts == null ? void 0 : filteredCounts.error) > 0 ? /* @__PURE__ */ jsx(
          TabWithCount,
          {
            label: translations.invalid,
            color: "destructive",
            active: activeTab === "error",
            count: filteredCounts == null ? void 0 : filteredCounts.error
          }
        ) : /* @__PURE__ */ jsx(TabWithoutCount, { label: translations.invalid, color: "primary" }) })
      ] })
    }
  );
};
const TabWithCount = ({
  label,
  count: count2,
  color,
  active
}) => {
  const badgeBgColor = color === "destructive" ? "bg-[var(--primitives-color-destructive-30)]" : "bg-overlay-darken-200";
  return /* @__PURE__ */ jsxs("div", { className: "relative flex items-center gap-1", children: [
    /* @__PURE__ */ jsx(Text$1, { emphasized: active, color, children: label }),
    /* @__PURE__ */ jsx("div", { className: "relative flex h-[15px] items-center justify-start", children: /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center", children: /* @__PURE__ */ jsx(
      Badge,
      {
        className: cn(
          "text-xxs min-w-4 items-center flex h-4 px-1 justify-center shadow-none",
          badgeBgColor
        ),
        radius: "full",
        children: /* @__PURE__ */ jsx(Text$1, { variant: "caption1", color, children: count2 > 0 ? formatNumber(count2) : "0" })
      }
    ) }) })
  ] });
};
const TabWithoutCount = ({
  label,
  color,
  active
}) => {
  return /* @__PURE__ */ jsx("div", { className: "relative flex h-[15px] items-center justify-center px-3", children: /* @__PURE__ */ jsx(
    Text$1,
    {
      emphasized: active,
      color,
      className: "flex items-center justify-center",
      children: label
    }
  ) });
};
function FieldFilterDropdown({
  children,
  disabled,
  onOpenChange,
  onFieldSelected,
  options,
  footerActions
}) {
  const [open, setOpen] = useState(false);
  const handleOpenChange = (open2) => {
    setOpen(open2);
    onOpenChange == null ? void 0 : onOpenChange(open2);
  };
  const handleSelect = (option) => {
    onFieldSelected(option);
    setOpen(false);
  };
  const handleSecondaryAction = (e, option) => {
    if (!option.secondaryAction) return;
    e.stopPropagation();
    option.secondaryAction.onClick(option.key);
  };
  return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: handleOpenChange, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { disabled, asChild: true, children }),
    /* @__PURE__ */ jsx(PopoverContent, { className: "min-w-80 p-0", align: "start", children: /* @__PURE__ */ jsxs(Command, { className: "flex flex-col", children: [
      /* @__PURE__ */ jsx(CommandInput, { placeholder: "Search...", className: "h-9" }),
      /* @__PURE__ */ jsxs(CommandList, { className: "flex-1 overflow-y-auto", children: [
        /* @__PURE__ */ jsx(CommandEmpty, { children: "No results found." }),
        options.map((group, index) => /* @__PURE__ */ jsxs(React__default.Fragment, { children: [
          /* @__PURE__ */ jsx(CommandGroup, { heading: group.label, children: group.options.map((option) => /* @__PURE__ */ jsx(
            CommandItem,
            {
              value: option.key,
              onSelect: () => handleSelect(option),
              className: "cursor-pointer",
              children: /* @__PURE__ */ jsx(
                OptionItem,
                {
                  option,
                  onClick: handleSecondaryAction
                }
              )
            },
            option.key
          )) }),
          index < options.length - 1 && /* @__PURE__ */ jsx(CommandSeparator, {})
        ] }, group.label))
      ] }),
      footerActions && /* @__PURE__ */ jsx(
        FilterFooterActions,
        {
          footerActions,
          setOpen
        }
      )
    ] }) })
  ] });
}
const OptionItem = ({
  option,
  onClick
}) => {
  const btnRef = useRef(null);
  return /* @__PURE__ */ jsxs("div", { className: "flex w-full items-center justify-between", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      option.icon && /* @__PURE__ */ jsx("div", { style: { color: option.iconColor }, children: /* @__PURE__ */ jsx(Icon, { name: option.icon, size: 16, variant: option.iconVariant }) }),
      /* @__PURE__ */ jsx(Text$1, { children: option.label })
    ] }),
    option.badge && /* @__PURE__ */ jsx(
      Badge,
      {
        className: "max-h-4 rounded-full px-1.5",
        variant: option.badge.variant,
        children: option.badge.label
      }
    ),
    option.secondaryAction && /* @__PURE__ */ jsxs(Tooltip, { children: [
      /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
        Button,
        {
          variant: "ghost",
          size: "icon-sm",
          onClick: (e) => onClick(e, option),
          ref: btnRef,
          icon: option.secondaryAction.icon
        }
      ) }),
      option.secondaryAction.tooltip && /* @__PURE__ */ jsx(TooltipContent, { side: "right", className: "z-1200", children: /* @__PURE__ */ jsx(Text$1, { color: "base-white", variant: "subheadline", children: option.secondaryAction.tooltip }) })
    ] })
  ] });
};
const FilterFooterActions = ({
  footerActions,
  setOpen
}) => {
  return /* @__PURE__ */ jsxs(Fragment$1, { children: [
    /* @__PURE__ */ jsx("div", { className: "px-2", children: /* @__PURE__ */ jsx(Separator, {}) }),
    /* @__PURE__ */ jsx("ul", { className: "flex flex-col p-1", children: footerActions.map((action, index) => /* @__PURE__ */ jsxs(
      "li",
      {
        className: "flex cursor-pointer items-center gap-2 p-2",
        onClick: () => {
          action.onClick();
          setOpen(false);
        },
        role: "menuitem",
        children: [
          /* @__PURE__ */ jsx(Icon, { name: action.icon, size: 16, color: "muted" }),
          /* @__PURE__ */ jsx(Text$1, { children: action.label })
        ]
      },
      index
    )) })
  ] });
};
const FilterFieldElement = ({
  query,
  truncate = false
}) => {
  const truncateClass = truncate ? "max-w-16 truncate" : "";
  return /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2 rounded-l-xs bg-surface-sentiment-subtle-neutral py-1 pl-2 pr-1.5", children: /* @__PURE__ */ jsx(Text$1, { variant: "callout", className: truncateClass, children: query.queryField }) });
};
const getOptionByKey = (groups, key) => {
  if (!groups || groups.length === 0) return void 0;
  for (const group of groups) {
    if (!group.options) continue;
    const option = group.options.find((opt) => (opt == null ? void 0 : opt.key) === key);
    if (option) return option;
  }
  return void 0;
};
const FilterOperationElement = ({
  query,
  options,
  interactive,
  onUpdateQueryOperation,
  onOperationOpenChange,
  truncate = false
}) => {
  const [isOpen, setIsOpen] = React__default.useState(false);
  const selectedOperation = useMemo(
    () => getOptionByKey(options, query.queryOperation),
    [options, query.queryOperation]
  );
  const handleOptionSelect = (key) => {
    onUpdateQueryOperation(key);
    onOperationOpenChange == null ? void 0 : onOperationOpenChange(false);
    setIsOpen(false);
  };
  const handleOperationOpen = (change) => {
    setIsOpen(change);
    onOperationOpenChange == null ? void 0 : onOperationOpenChange(change);
  };
  return /* @__PURE__ */ jsx(Fragment$1, { children: interactive ? /* @__PURE__ */ jsxs(Popover, { open: isOpen, onOpenChange: handleOperationOpen, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs("button", { className: "flex items-center gap-0.5 bg-surface-sentiment-subtle-neutral px-1.5 py-1", children: [
      /* @__PURE__ */ jsx(
        Text$1,
        {
          variant: "callout",
          color: "muted",
          className: cn({ "block max-w-12 truncate": truncate }),
          children: selectedOperation == null ? void 0 : selectedOperation.label
        }
      ),
      /* @__PURE__ */ jsx(Icon, { size: 12, color: "muted", name: "chevron-down-small" })
    ] }) }),
    /* @__PURE__ */ jsx(PopoverContent, { className: "mt-2 w-32 p-0", align: "start", children: /* @__PURE__ */ jsx("div", { className: "flex flex-col", children: options.map((group, index) => /* @__PURE__ */ jsxs(React__default.Fragment, { children: [
      (group == null ? void 0 : group.label) && /* @__PURE__ */ jsx("div", { className: "px-2 py-1.5 text-xs font-medium text-text-default", children: group == null ? void 0 : group.label }),
      /* @__PURE__ */ jsx("ul", { children: (Array.isArray(group == null ? void 0 : group.options) ? group.options : []).map(
        (option, opIndex) => /* @__PURE__ */ jsx(
          "li",
          {
            onClick: () => handleOptionSelect(option == null ? void 0 : option.key),
            className: "flex cursor-pointer whitespace-nowrap px-2 py-1.5 text-sm hover:bg-overlay-darken-300 hover:text-text-weak",
            role: "option",
            tabIndex: 0,
            children: /* @__PURE__ */ jsx(Text$1, { children: option == null ? void 0 : option.label })
          },
          opIndex
        )
      ) }),
      index < options.length - 1 && /* @__PURE__ */ jsx("hr", { className: "border-border m-1" })
    ] }, index)) }) })
  ] }) : /* @__PURE__ */ jsx(
    Text$1,
    {
      className: cn(
        "bg-surface-sentiment-subtle-neutral flex items-center gap-1 px-1.5 py-1",
        {
          "block max-w-12 truncate": truncate
        }
      ),
      color: "muted",
      variant: "callout",
      children: /* @__PURE__ */ jsx("span", { className: cn({ "block max-w-12 truncate": truncate }), children: selectedOperation == null ? void 0 : selectedOperation.label })
    }
  ) });
};
const FilterValueDisplayElement = ({
  query
}) => {
  var _a;
  return /* @__PURE__ */ jsx(Fragment$1, { children: ((_a = query.queryValues) == null ? void 0 : _a.length) > 0 && /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2 rounded-r-sm bg-surface-sentiment-subtle-neutral px-1.5 py-1", children: /* @__PURE__ */ jsx("div", { className: "flex items-center", children: query.queryValues.length === 1 ? /* @__PURE__ */ jsx(
    Text$1,
    {
      variant: "callout",
      color: "muted",
      className: "block max-w-[150px] truncate",
      children: query.queryValues[0]
    }
  ) : /* @__PURE__ */ jsxs(Fragment$1, { children: [
    /* @__PURE__ */ jsx(
      Text$1,
      {
        variant: "callout",
        color: "muted",
        className: "block max-w-[100px] truncate",
        children: query.queryValues[0]
      }
    ),
    /* @__PURE__ */ jsx(
      Text$1,
      {
        variant: "callout",
        color: "muted",
        className: "ml-1 whitespace-nowrap",
        children: ` +${query.queryValues.length - 1} more`
      }
    )
  ] }) }) }) });
};
const MultiSelectContent = ({
  query,
  valueOptions,
  operationOptions,
  searchValue,
  onOpenOperation,
  onSearchValueChange,
  onUpdateQueryValue,
  onUpdateQueryOperation,
  isLoadingOptions,
  activeIndex,
  setActiveIndex,
  handleClearInput
}) => {
  var _a;
  const totalCount = valueOptions.length;
  const handleOptionHover = useCallback(
    (index) => setActiveIndex(index),
    [setActiveIndex]
  );
  const onOptionChange = useCallback(
    (value, checked) => {
      const currentValues = query.queryValues || [];
      if (checked) {
        onUpdateQueryValue([...currentValues, value]);
      } else {
        onUpdateQueryValue(currentValues.filter((v) => v !== value));
      }
    },
    [query.queryValues, onUpdateQueryValue]
  );
  const valuesByline = useMemo(() => {
    if (isLoadingOptions) {
      return /* @__PURE__ */ jsx(Spinner, { size: 16 });
    }
    if (valueOptions.length === 0) {
      return /* @__PURE__ */ jsx(Text$1, { className: "text-interactive-state-selected-pressed", children: "No values found" });
    }
    return /* @__PURE__ */ jsxs(Text$1, { className: "text-interactive-state-selected-pressed", children: [
      "Showing ",
      totalCount,
      " option",
      valueOptions.length === 1 ? "" : "s"
    ] });
  }, [valueOptions, isLoadingOptions, totalCount]);
  const unselectedOptions = useMemo(
    () => valueOptions.filter((value) => {
      var _a2;
      return !((_a2 = query.queryValues) == null ? void 0 : _a2.includes(value.key));
    }),
    [valueOptions, query.queryValues]
  );
  return /* @__PURE__ */ jsxs(Fragment$1, { children: [
    /* @__PURE__ */ jsxs("div", { className: "flex h-9 items-center border-b px-1", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          className: "grow bg-transparent pl-2 text-[13px] leading-9 outline-none",
          type: "text",
          placeholder: "Search...",
          onChange: onSearchValueChange,
          value: searchValue ?? ""
        }
      ),
      /* @__PURE__ */ jsx(
        "button",
        {
          className: "h-full border-0 bg-transparent px-2 text-interactive-state-selected-pressed hover:text-text-title",
          onClick: handleClearInput,
          children: /* @__PURE__ */ jsx(Icon, { name: "cross-small" })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center p-2", children: [
      /* @__PURE__ */ jsx(FilterFieldElement, { query, truncate: true }),
      /* @__PURE__ */ jsx(
        FilterOperationElement,
        {
          query,
          options: operationOptions,
          onUpdateQueryOperation,
          onOperationOpenChange: onOpenOperation,
          interactive: true,
          truncate: true
        }
      ),
      /* @__PURE__ */ jsx(FilterValueDisplayElement, { query })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "px-2", children: /* @__PURE__ */ jsx(Separator, {}) }),
    ((_a = query.queryValues) == null ? void 0 : _a.length) > 0 && /* @__PURE__ */ jsx("div", { className: "flex max-h-36 flex-col overflow-y-auto p-1", children: query.queryValues.map((value, index) => /* @__PURE__ */ jsxs(
      Text$1,
      {
        as: "label",
        onMouseEnter: () => handleOptionHover(index),
        className: cn(
          "flex items-center gap-2 p-2 text-base rounded-sm",
          {
            "bg-overlay-darken-300": activeIndex === index
          }
        ),
        children: [
          /* @__PURE__ */ jsx(
            Checkbox,
            {
              checked: true,
              value,
              onCheckedChange: () => onOptionChange(value, false)
            }
          ),
          value
        ]
      },
      value
    )) }),
    /* @__PURE__ */ jsxs("div", { className: "flex h-7 items-center justify-between px-3 text-xs text-muted", children: [
      /* @__PURE__ */ jsx(Text$1, { children: "Values" }),
      valuesByline
    ] }),
    unselectedOptions.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex max-h-36 flex-col overflow-y-auto p-1", children: [
      unselectedOptions.map((value, index) => {
        var _a2;
        return /* @__PURE__ */ jsxs(
          Text$1,
          {
            as: "label",
            onMouseEnter: () => {
              var _a3;
              return handleOptionHover(index + (((_a3 = query.queryValues) == null ? void 0 : _a3.length) || 0));
            },
            className: cn(
              "flex items-center gap-2 p-2 text-base rounded-sm",
              {
                "bg-overlay-darken-300": activeIndex === index + (((_a2 = query.queryValues) == null ? void 0 : _a2.length) || 0)
              }
            ),
            children: [
              /* @__PURE__ */ jsx(
                Checkbox,
                {
                  checked: false,
                  value: value.key,
                  onCheckedChange: () => onOptionChange(value.key, true)
                }
              ),
              value.label
            ]
          },
          value.key
        );
      }),
      isLoadingOptions && /* @__PURE__ */ jsx("div", { className: "flex justify-center py-2", children: /* @__PURE__ */ jsx(Spinner, { size: 16 }) })
    ] })
  ] });
};
const InputContent = ({
  query,
  operationOptions,
  onSubmit,
  onUpdateQueryOperation,
  onOpenOperation
}) => {
  var _a;
  const [inputValue, setInputValue] = React__default.useState(
    ((_a = query.queryValues) == null ? void 0 : _a[0]) || ""
  );
  const inputRef = useRef(null);
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };
  const handleSubmitValue = () => {
    onSubmit(inputValue);
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmitValue();
    }
  };
  React__default.useEffect(() => {
    var _a2;
    setInputValue(((_a2 = query.queryValues) == null ? void 0 : _a2[0]) || "");
  }, [query.queryValues]);
  React__default.useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, []);
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2 p-2", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
      /* @__PURE__ */ jsx(FilterFieldElement, { query, truncate: true }),
      /* @__PURE__ */ jsx(
        FilterOperationElement,
        {
          query,
          options: operationOptions,
          onUpdateQueryOperation,
          onOperationOpenChange: onOpenOperation,
          interactive: true,
          truncate: true
        }
      ),
      /* @__PURE__ */ jsx(FilterValueDisplayElement, { query })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "px-2", children: /* @__PURE__ */ jsx(Separator, {}) }),
    /* @__PURE__ */ jsx(
      Input,
      {
        ref: inputRef,
        className: "mt-1 w-full",
        placeholder: "Enter value...",
        value: inputValue,
        onChange: handleInputChange,
        onBlur: handleSubmitValue,
        onKeyDown: handleKeyDown
      }
    )
  ] });
};
const FilterValueElement = ({
  query,
  isActive,
  valueOptions,
  operationOptions,
  onRemoveQuery,
  onUpdateQueryValue,
  onUpdateQueryOperation,
  onQueryValueSelected,
  onQueryDeactivated,
  searchValue,
  onSearchValueChange,
  isLoadingOptions,
  entryMode = "multi-select"
}) => {
  const filterValueRef = useRef(null);
  const [activeIndex, setActiveIndex] = React__default.useState(-1);
  const [operationsOpen, setOperationsOpen] = React__default.useState(false);
  useEffect(() => {
    const handleClickOutside = (event) => {
      var _a;
      if (isActive && !operationsOpen && filterValueRef.current && !filterValueRef.current.contains(event.target)) {
        if (!((_a = query.queryValues) == null ? void 0 : _a.length)) {
          onRemoveQuery(query.id);
        }
        onSearchValueChange("");
        onQueryDeactivated();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    isActive,
    onRemoveQuery,
    query.id,
    query.queryValues,
    onQueryDeactivated,
    operationsOpen
  ]);
  const handleInputValueChange = useCallback(
    (value) => {
      onUpdateQueryValue([value]);
      onQueryDeactivated();
      onSearchValueChange("");
    },
    [onUpdateQueryValue, onQueryDeactivated]
  );
  const handleSearchValueChange = useCallback(
    (event) => {
      onSearchValueChange(event.target.value);
    },
    [onSearchValueChange]
  );
  const handleOpenOperation = useCallback((change) => {
    setOperationsOpen(change);
  }, []);
  const handleClearInput = useCallback(() => {
    onSearchValueChange("");
  }, [onSearchValueChange]);
  const renderTriggerContent = () => {
    var _a, _b;
    if (entryMode === "input") {
      return /* @__PURE__ */ jsx(
        Text$1,
        {
          variant: "callout",
          color: "muted",
          className: "block max-w-[100px] truncate",
          children: ((_a = query.queryValues) == null ? void 0 : _a[0]) || "Enter value..."
        }
      );
    } else {
      if (((_b = query.queryValues) == null ? void 0 : _b.length) > 0) {
        if (query.queryValues.length === 1) {
          return /* @__PURE__ */ jsx(
            Text$1,
            {
              variant: "callout",
              color: "muted",
              className: "block max-w-[100px] truncate",
              children: query.queryValues[0]
            }
          );
        } else {
          return /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
            /* @__PURE__ */ jsx(
              Text$1,
              {
                variant: "callout",
                color: "muted",
                className: "block max-w-[100px] truncate",
                children: query.queryValues[0]
              }
            ),
            /* @__PURE__ */ jsx(
              Text$1,
              {
                variant: "callout",
                color: "muted",
                className: "whitespace-nowrap",
                children: ` +${query.queryValues.length - 1} more`
              }
            )
          ] });
        }
      }
      return /* @__PURE__ */ jsx(Text$1, { variant: "callout", color: "muted", children: "Select values..." });
    }
  };
  return /* @__PURE__ */ jsxs(Popover, { open: isActive, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
      "button",
      {
        onClick: onQueryValueSelected,
        className: "flex grow items-center gap-2 bg-surface-sentiment-subtle-neutral px-1.5 py-1 outline-none",
        children: renderTriggerContent()
      }
    ) }),
    /* @__PURE__ */ jsx(PopoverContent, { className: "mt-2 w-[336px] p-0", align: "start", children: /* @__PURE__ */ jsx("div", { ref: filterValueRef, className: "flex flex-col", children: entryMode === "input" ? /* @__PURE__ */ jsx(
      InputContent,
      {
        query,
        operationOptions,
        onSubmit: handleInputValueChange,
        onUpdateQueryOperation,
        onOpenOperation: handleOpenOperation
      }
    ) : /* @__PURE__ */ jsx(
      MultiSelectContent,
      {
        query,
        valueOptions,
        operationOptions,
        searchValue,
        onSearchValueChange: handleSearchValueChange,
        onUpdateQueryValue,
        onUpdateQueryOperation,
        isLoadingOptions,
        activeIndex,
        setActiveIndex,
        handleClearInput,
        onOpenOperation: handleOpenOperation
      }
    ) }) })
  ] });
};
const FilterBlock = ({
  query,
  className,
  searchValue,
  isActive,
  operationOptions,
  onQueryUpdate,
  onQueryActivate,
  onRemoveQuery,
  valueOptions,
  onSearchValueChange,
  isLoadingValueOptions,
  flagData
}) => {
  var _a;
  const noValueRequiredOperation = query.queryOperation === "is empty" || query.queryOperation === "not empty" || query.queryOperation === "is error" || query.queryOperation === "is valid";
  const isInputMode = query.queryOperation === "ilike" || query.queryOperation === "gt" || query.queryOperation === "lt";
  const handleDeleteQuery = (e) => {
    e.stopPropagation();
    onRemoveQuery(query.id);
    if (isActive) {
      onQueryActivate(void 0);
    }
  };
  const handleUpdateQueryOperation = (operation) => {
    onQueryUpdate({
      ...query,
      queryOperation: operation
    });
  };
  const handleUpdateQueryValue = (value) => {
    onQueryUpdate({
      ...query,
      queryValues: value
    });
  };
  const handleQueryValueSelected = () => {
    onQueryActivate(query.id);
  };
  const handleQueryDeactivated = () => {
    onQueryActivate(void 0);
  };
  const isFlagFilter = query.queryField === "metadata.flags" && query.queryOperation === "contains";
  const flagSlug = isFlagFilter ? (_a = query.queryValues) == null ? void 0 : _a[0] : null;
  const flag = flagSlug && flagData ? flagData.find((f) => f.slug === flagSlug) : null;
  if (isFlagFilter && (flag || (flagData == null ? void 0 : flagData.length) && (flagData == null ? void 0 : flagData.length) > 0)) {
    const displayFlag = flag || flagData && flagData[0];
    return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-0.5", className), children: [
      /* @__PURE__ */ jsxs("div", { className: "flex cursor-pointer items-center rounded-l-xs bg-surface-sentiment-subtle-neutral px-2 py-1", children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "flex size-4 items-center justify-center",
            style: { color: (displayFlag == null ? void 0 : displayFlag.colorHex) || "#0090FF" },
            children: /* @__PURE__ */ jsx(Icon, { name: "IconFlag1", variant: "filled", size: 12 })
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "ml-2 font-sans text-sm font-normal text-text-title", children: (displayFlag == null ? void 0 : displayFlag.label) || flagSlug || "Flag" })
      ] }),
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: handleDeleteQuery,
          className: "flex items-center gap-2 rounded-r-xs bg-surface-sentiment-subtle-neutral px-1.5 py-[5.5px]",
          children: /* @__PURE__ */ jsx(Icon, { size: 12, name: "cross-small" })
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-0.5", className), children: [
    /* @__PURE__ */ jsxs(
      "div",
      {
        onClick: handleQueryValueSelected,
        className: "flex cursor-pointer items-center",
        children: [
          /* @__PURE__ */ jsx(FilterFieldElement, { query }),
          /* @__PURE__ */ jsx(
            FilterOperationElement,
            {
              query,
              options: operationOptions,
              onUpdateQueryOperation: handleUpdateQueryOperation,
              interactive: noValueRequiredOperation
            }
          ),
          !noValueRequiredOperation && /* @__PURE__ */ jsx(
            FilterValueElement,
            {
              query,
              isActive,
              valueOptions,
              operationOptions,
              onRemoveQuery,
              onUpdateQueryValue: handleUpdateQueryValue,
              onUpdateQueryOperation: handleUpdateQueryOperation,
              onQueryValueSelected: handleQueryValueSelected,
              onQueryDeactivated: handleQueryDeactivated,
              onSearchValueChange,
              isLoadingOptions: isLoadingValueOptions,
              searchValue,
              entryMode: isInputMode ? "input" : "multi-select"
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      "button",
      {
        onClick: handleDeleteQuery,
        className: "flex items-center gap-2 rounded-r-xs bg-surface-sentiment-subtle-neutral px-1.5 py-[5.5px]",
        children: /* @__PURE__ */ jsx(Icon, { size: 12, name: "cross-small" })
      }
    )
  ] });
};
const reservedValues$1 = ["error", "valid", ""];
const AtomicCollection = ({
  queryCollection,
  searchValue,
  isPending,
  activeQueryId,
  filterOperations,
  valueOptionsList,
  onUpdateCollectionQuery,
  onQueryActivate,
  onCollectionActivate,
  onRemoveCollection,
  onSearchValueChange,
  flagData
}) => {
  const query = queryCollection.queries[0];
  const onRemoveQuery = () => {
    onRemoveCollection(queryCollection.id);
  };
  const handleQueryUpdated = (query2) => {
    if (query2.queryOperation === "is empty" || query2.queryOperation === "not empty") {
      onUpdateCollectionQuery(queryCollection.id, {
        ...query2,
        queryValues: [""]
      });
    } else if (query2.queryOperation === "is error" || query2.queryOperation === "is valid") {
      const value = query2.queryOperation === "is error" ? "error" : "valid";
      onUpdateCollectionQuery(queryCollection.id, {
        ...query2,
        queryValues: [value]
      });
    } else {
      const cleanQueryValues = query2.queryValues.filter(
        (value) => !reservedValues$1.includes(value)
      );
      onUpdateCollectionQuery(queryCollection.id, {
        ...query2,
        queryValues: cleanQueryValues
      });
    }
  };
  const handleQueryActivated = (queryId) => {
    onQueryActivate(queryId);
    onCollectionActivate(queryCollection.id);
  };
  return /* @__PURE__ */ jsx(
    FilterBlock,
    {
      query,
      searchValue,
      isActive: query.id === activeQueryId,
      isLoadingValueOptions: isPending,
      operationOptions: filterOperations,
      valueOptions: valueOptionsList ?? [],
      onQueryUpdate: handleQueryUpdated,
      onQueryActivate: handleQueryActivated,
      onRemoveQuery,
      onSearchValueChange,
      flagData
    },
    query.id
  );
};
const FilterJoinElement = ({
  query,
  isFirst,
  onSelectPrefix
}) => {
  const { joinPrefix } = query;
  const [isOpen, setIsOpen] = React__default.useState(false);
  const joinOptions = [
    { key: "and", label: "and" },
    { key: "or", label: "or" }
  ];
  const handleOptionSelect = (key) => {
    onSelectPrefix(key);
    setIsOpen(false);
  };
  return /* @__PURE__ */ jsx(Fragment$1, { children: isFirst ? /* @__PURE__ */ jsx("div", { className: "rounded-l-sm py-0.5", children: /* @__PURE__ */ jsx(Text$1, { children: "Where" }) }) : /* @__PURE__ */ jsxs(Popover, { open: isOpen, onOpenChange: setIsOpen, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx("button", { className: "flex w-10 justify-center gap-1 rounded-sm bg-surface-sentiment-subtle-neutral px-2 py-0.5", children: /* @__PURE__ */ jsx(Text$1, { color: "muted", children: joinPrefix }) }) }),
    /* @__PURE__ */ jsx(PopoverContent, { className: "mt-2 w-20 p-0", align: "start", children: /* @__PURE__ */ jsx(Command, { children: /* @__PURE__ */ jsx(CommandList, { children: joinOptions.map((option) => /* @__PURE__ */ jsx(
      "button",
      {
        onClick: () => handleOptionSelect(option.key),
        className: cn(
          "flex w-full items-center px-2 py-1.5 text-sm outline-none hover:bg-overlay-darken-300 hover:text-text-weak",
          joinPrefix === option.key && "bg-overlay-darken-300 text-text-weak"
        ),
        children: /* @__PURE__ */ jsx(Text$1, { children: option.label })
      },
      option.key
    )) }) }) })
  ] }) });
};
const reservedValues = ["error", "valid", ""];
const AdvancedCollections = (props) => {
  const {
    searchValue,
    activeQueryId,
    activeCollectionId,
    queryCollection,
    valueOptionsList,
    filterOperations,
    fieldOptionGroups,
    onRemoveCollection,
    onAddCollectionQuery,
    onRemoveQuery,
    onUpdateCollectionQuery,
    onQueryActivate,
    onCollectionActivate,
    onSearchValueChange,
    onUpdateCollection,
    translations = {
      filter: "Filter",
      addCondition: "Add condition",
      deleteFilter: "Delete Filter"
    }
  } = props;
  const [open, setOpen] = useState(
    () => activeCollectionId === queryCollection.id
  );
  const containerRef = useRef(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id && onUpdateCollection) {
      const oldIndex = queryCollection.queries.findIndex(
        (item) => item.id === active.id
      );
      const newIndex = queryCollection.queries.findIndex(
        (item) => item.id === over.id
      );
      const newQueries = arrayMove(queryCollection.queries, oldIndex, newIndex);
      onUpdateCollection(queryCollection.id, { queries: newQueries });
    }
  };
  const handleDeleteCollection = (e) => {
    e.stopPropagation();
    onRemoveCollection(props.queryCollection.id);
  };
  const handleSelectQueryField = (option) => {
    if (option.type === "field") {
      const newQuery = {
        queryField: option.key,
        queryOperation: "eq",
        queryValues: [],
        joinPrefix: "and"
      };
      onAddCollectionQuery(props.queryCollection.id, newQuery);
    }
  };
  const handleRemoveQuery = (queryId) => {
    onRemoveQuery(props.queryCollection.id, queryId);
  };
  const handleCollectionActivate = () => {
    onCollectionActivate(queryCollection.id);
  };
  const handleUpdateQuery = (query) => {
    if (query.queryOperation === "is empty" || query.queryOperation === "not empty") {
      onUpdateCollectionQuery(queryCollection.id, {
        ...query,
        queryValues: [""]
      });
    } else if (query.queryOperation === "is error" || query.queryOperation === "is valid") {
      const value = query.queryOperation === "is error" ? "error" : "valid";
      onUpdateCollectionQuery(queryCollection.id, {
        ...query,
        queryValues: [value]
      });
    } else {
      const cleanQueryValues = query.queryValues.filter(
        (value) => !reservedValues.includes(value)
      );
      onUpdateCollectionQuery(queryCollection.id, {
        ...query,
        queryValues: cleanQueryValues
      });
    }
  };
  const handleUpdateQueryPrefix = (queryId, prefix) => {
    const query = queryCollection.queries.find((query2) => query2.id === queryId);
    if (!query) return;
    onUpdateCollectionQuery(queryCollection.id, {
      ...query,
      joinPrefix: prefix
    });
  };
  const handleOpenChange = (open2) => {
    setOpen(open2);
    if (!open2) {
      onCollectionActivate(void 0);
      onQueryActivate(void 0);
    }
  };
  const queryFieldsDisplay = () => {
    const queries = queryCollection.queries;
    const distinctQueryFields = queries.reduce((queryFields, query) => {
      if (!queryFields.includes(query.queryField)) {
        return [...queryFields, query.queryField];
      }
      return queryFields;
    }, []);
    const firstQueryField = distinctQueryFields[0];
    const additionalQueryFields = distinctQueryFields.length - 1;
    if (distinctQueryFields.length === 0) {
      return null;
    } else if (distinctQueryFields.length === 1) {
      return /* @__PURE__ */ jsx(Text$1, { color: "text-default", children: firstQueryField });
    } else {
      return /* @__PURE__ */ jsxs(Text$1, { color: "text-default", children: [
        firstQueryField,
        " +",
        additionalQueryFields
      ] });
    }
  };
  return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: handleOpenChange, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { children: /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-0.5", children: [
      /* @__PURE__ */ jsxs(
        "div",
        {
          onClick: handleCollectionActivate,
          className: "theme-static flex items-center gap-2 rounded-l-sm bg-surface-sentiment-subtle-neutral py-0.5 pl-2 pr-1",
          children: [
            /* @__PURE__ */ jsx(Icon, { name: "settings-slider-three" }),
            /* @__PURE__ */ jsx(Text$1, { children: translations.filter }),
            /* @__PURE__ */ jsx(Text$1, { color: "text-default", children: queryFieldsDisplay() })
          ]
        }
      ),
      /* @__PURE__ */ jsx(
        "div",
        {
          onClick: handleDeleteCollection,
          className: "theme-static flex items-center gap-2 rounded-r-sm bg-surface-sentiment-subtle-neutral px-1 py-0.5",
          children: /* @__PURE__ */ jsx(Icon, { name: "cross-small" })
        }
      )
    ] }) }),
    /* @__PURE__ */ jsxs(PopoverContent, { className: "mt-2 w-auto min-w-72 p-0", align: "start", children: [
      /* @__PURE__ */ jsxs(
        "div",
        {
          ref: containerRef,
          className: "flex max-h-[300px] flex-col gap-3 overflow-y-auto overflow-x-hidden p-3",
          children: [
            queryCollection.queries.length > 0 && /* @__PURE__ */ jsx(
              DndContext,
              {
                sensors,
                collisionDetection: closestCenter,
                onDragEnd: handleDragEnd,
                modifiers: [restrictToParentElement, restrictToWindowEdges],
                children: /* @__PURE__ */ jsx(
                  SortableContext,
                  {
                    items: queryCollection.queries.map((query) => query.id),
                    strategy: verticalListSortingStrategy,
                    children: queryCollection.queries.map((query, index) => /* @__PURE__ */ jsx(
                      SortableQueryItem,
                      {
                        query,
                        index,
                        activeQueryId,
                        searchValue,
                        valueOptionsList,
                        filterOperations,
                        onQueryUpdate: handleUpdateQuery,
                        onQueryActivate,
                        onRemoveQuery: handleRemoveQuery,
                        onSearchValueChange,
                        onUpdateQueryPrefix: handleUpdateQueryPrefix,
                        flagData: props.flagData
                      },
                      query.id
                    ))
                  }
                )
              }
            ),
            /* @__PURE__ */ jsx(
              FieldFilterDropdown,
              {
                onFieldSelected: handleSelectQueryField,
                options: fieldOptionGroups,
                children: /* @__PURE__ */ jsx(
                  Button,
                  {
                    className: "w-full justify-start px-0",
                    variant: "ghost",
                    icon: "plus-small",
                    size: "sm",
                    children: translations.addCondition
                  }
                )
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsx("div", { className: "flex flex-row gap-2 border-t p-3", children: /* @__PURE__ */ jsx(
        Button,
        {
          className: "w-full justify-start px-0",
          variant: "ghost",
          icon: "trash-can-2",
          size: "sm",
          onClick: handleDeleteCollection,
          children: translations.deleteFilter
        }
      ) })
    ] })
  ] });
};
const SortableQueryItem = ({
  query,
  index,
  activeQueryId,
  searchValue,
  valueOptionsList,
  filterOperations,
  onQueryUpdate,
  onQueryActivate,
  onRemoveQuery,
  onSearchValueChange,
  onUpdateQueryPrefix,
  flagData
}) => {
  const {
    attributes,
    listeners: listeners2,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: query.id });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };
  return /* @__PURE__ */ jsxs("div", { ref: setNodeRef, style, className: "flex flex-row gap-2", children: [
    /* @__PURE__ */ jsx(
      FilterJoinElement,
      {
        query,
        isFirst: index === 0,
        onSelectPrefix: (prefix) => onUpdateQueryPrefix(query.id, prefix)
      }
    ),
    /* @__PURE__ */ jsx(
      FilterBlock,
      {
        className: "grow",
        query,
        searchValue,
        isActive: activeQueryId === query.id,
        isLoadingValueOptions: false,
        operationOptions: filterOperations,
        valueOptions: valueOptionsList,
        onQueryUpdate,
        onQueryActivate,
        onRemoveQuery,
        onSearchValueChange,
        flagData
      },
      query.id
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        ...attributes,
        ...listeners2,
        className: "cursor-grab self-center rounded-sm hover:bg-gray-100 active:cursor-grabbing",
        children: /* @__PURE__ */ jsx(
          Icon,
          {
            name: "dot-grid-2-3",
            className: cn("size-4 text-muted cursor-move", {
              "text-text-title": isDragging
            })
          }
        )
      }
    )
  ] });
};
const FilterCollection = (props) => {
  if (props.queryCollection.type === "atomic") {
    return /* @__PURE__ */ jsx(AtomicCollection, { ...props });
  } else if (props.queryCollection.type === "advanced") {
    return /* @__PURE__ */ jsx(AdvancedCollections, { ...props, translations: props.translations });
  }
  return null;
};
const SimpleSearchBar = forwardRef(
  ({
    searchTerm,
    onSearchChange,
    className,
    placeholder,
    icon = "magnifying-glass",
    size = "md",
    variant = "default",
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs("div", { className: cn("relative", className), ...props, ref, children: [
      /* @__PURE__ */ jsx(
        Icon,
        {
          name: icon,
          size: size === "sm" ? 16 : 18,
          className: "pointer-events-none absolute top-1/2 ml-3 -translate-y-1/2"
        }
      ),
      /* @__PURE__ */ jsx(
        Input,
        {
          placeholder,
          className: cn(
            "w-full !pl-9",
            size === "sm" ? "text-sm h-8 py-1" : ""
          ),
          value: searchTerm,
          onChange: (e) => onSearchChange(e.target.value),
          variant
        }
      ),
      !!searchTerm.length && /* @__PURE__ */ jsx(
        Button,
        {
          onClick: () => onSearchChange(""),
          variant: "ghost",
          size: size === "sm" ? "sm" : "md",
          className: "absolute right-0 top-1/2 -translate-y-1/2",
          children: "×"
        }
      )
    ] });
  }
);
SimpleSearchBar.displayName = "SearchBar";
const isTrayHeader = createComponentDisplayNameFilter("TrayHeader");
const isTrayFooter = createComponentDisplayNameFilter("TrayFooter");
const isTrayTab = createComponentDisplayNameFilter("TrayTab");
const Tray = ({ className, children }) => {
  const childrenArray = Children.toArray(children).flatMap(
    (child) => isValidElement(child) && isFragment(child) ? Children.toArray(child.props.children) : child
  );
  const header = childrenArray.find(isTrayHeader);
  const footer = childrenArray.find(isTrayFooter);
  const tabs = childrenArray.filter(isTrayTab);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const setActiveTab = useCallback((index) => {
    setActiveTabIndex(index);
  }, []);
  let content;
  if (tabs.length > 0) {
    const names = tabs.map(
      (tab) => tab.props.title
    );
    content = /* @__PURE__ */ jsxs(Fragment$1, { children: [
      /* @__PURE__ */ jsx("div", { className: "flex gap-2 px-5", children: names.map((name, index) => /* @__PURE__ */ jsx(
        "button",
        {
          className: cn("h-7 px-3 rounded-xs", {
            "bg-overlay-darken-300": index === activeTabIndex
          }),
          onClick: () => setActiveTab(index),
          children: /* @__PURE__ */ jsx(Text$1, { variant: "body", emphasized: true, children: name })
        },
        name
      )) }),
      tabs[activeTabIndex]
    ] });
  } else {
    content = childrenArray.filter(
      (child) => !isTrayTab(child) && !isTrayHeader(child) && !isTrayFooter(child)
    );
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "bg-popover w-96 border border-gray-3 rounded-sm pt-5 flex flex-col gap-4",
        className
      ),
      children: [
        header,
        content,
        footer
      ]
    }
  );
};
const TrayHeader = ({ className, children, onClose }) => /* @__PURE__ */ jsxs("div", { className: cn("px-5 flex items-start", className), children: [
  /* @__PURE__ */ jsx(Text$1, { className: "grow", variant: "title2", children }),
  onClose && /* @__PURE__ */ jsx("button", { type: "button", onClick: onClose, children: /* @__PURE__ */ jsx(Icon, { name: "cross-large", size: 12 }) })
] });
TrayHeader.displayName = "TrayHeader";
const TrayFooter = ({ children, className }) => /* @__PURE__ */ jsx("div", { className: cn("px-5 py-4 border-t border-gray-3", className), children });
TrayFooter.displayName = "TrayFooter";
const TrayTab = ({ children, className }) => /* @__PURE__ */ jsx("div", { className: cn("px-5", className), children });
TrayTab.displayName = "TrayTab";
const Progress = React.forwardRef(({ className, value, ...props }, ref) => /* @__PURE__ */ jsx(
  ProgressPrimitive.Root,
  {
    ref,
    className: cn(
      "relative h-2 w-full overflow-hidden rounded-full bg-primary/20",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx(
      ProgressPrimitive.Indicator,
      {
        className: "size-full flex-1 bg-primary transition-all",
        style: { transform: `translateX(-${100 - value}%)` }
      }
    )
  }
));
Progress.displayName = ProgressPrimitive.Root.displayName;
const useProgressTimer = (durationInSeconds) => {
  const [progress, setProgress] = React.useState(0);
  React.useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress >= 100) {
          clearInterval(timer);
          return 100;
        }
        return oldProgress + 100 / durationInSeconds;
      });
    }, 1e3);
    return () => {
      clearInterval(timer);
    };
  }, [durationInSeconds]);
  return progress;
};
const AutoProgress = React.forwardRef(({ durationInSeconds = 60, ...props }, ref) => {
  const progress = useProgressTimer(durationInSeconds);
  return /* @__PURE__ */ jsx(Progress, { ref, value: progress, ...props });
});
const AutoProgressHairline = React.forwardRef(({ durationInSeconds = 60, className, ...props }, ref) => {
  const progress = useProgressTimer(durationInSeconds);
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn("h-px bg-primary transition-all", className),
      style: { width: `${progress}%` },
      ...props
    }
  );
});
AutoProgressHairline.displayName = "AutoProgressHairline";
AutoProgress.displayName = "AutoProgress";
const Filter = ({
  buttonText,
  icon,
  filters,
  variant,
  align = "center"
}) => {
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Button, { variant: variant ?? "outline", className: "mr-2 leading-none", children: [
      /* @__PURE__ */ jsx(Icon, { name: icon || "filter-1" }),
      buttonText || "Filter"
    ] }) }),
    /* @__PURE__ */ jsx(DropdownMenuContent, { align, className: "w-56", children: filters.map((filter, i) => {
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs(DropdownMenuGroup, { children: [
          /* @__PURE__ */ jsx(DropdownMenuLabel, { children: filter.label }),
          /* @__PURE__ */ jsx(DropdownMenuSeparator, {}, "separator-1"),
          /* @__PURE__ */ jsx(
            DropdownMenuRadioGroup,
            {
              onValueChange: filter.onSelectFilter,
              value: filter.currentValue,
              children: filter.options.map((option, i2) => {
                return /* @__PURE__ */ jsx(
                  DropdownMenuRadioItem,
                  {
                    value: option.value,
                    children: option.label
                  },
                  `${option.value}-${i2}`
                );
              })
            }
          )
        ] }, `${filter.label}-${i}`),
        i !== filters.length - 1 && /* @__PURE__ */ jsx(DropdownMenuSeparator, {})
      ] }, `${filter.label}-${i}`);
    }) })
  ] });
};
const Accordion = AccordionPrimitive.Root;
const AccordionItem = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AccordionPrimitive.Item,
  {
    ref,
    className: cn("border-b", className),
    ...props
  }
));
AccordionItem.displayName = "AccordionItem";
const AccordionTrigger = React.forwardRef(
  ({
    className,
    children,
    chevronPosition = "right",
    showChevron = true,
    ...props
  }, ref) => {
    const chevron = showChevron ? /* @__PURE__ */ jsx(ChevronDownIcon, { className: "size-4 shrink-0 text-text-inverted-muted transition-transform duration-200" }) : null;
    return /* @__PURE__ */ jsx(AccordionPrimitive.Header, { className: "flex overflow-visible", children: /* @__PURE__ */ jsxs(
      AccordionPrimitive.Trigger,
      {
        ref,
        className: cn(
          "AccordionTrigger",
          "flex flex-1 items-center py-4 text-sm font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
          chevronPosition === "right" ? "justify-between" : "justify-start gap-2",
          className
        ),
        ...props,
        children: [
          chevronPosition === "left" && chevron,
          children,
          chevronPosition === "right" && chevron
        ]
      }
    ) });
  }
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
const AccordionContent = React.forwardRef(({ className, children, contentClassName, ...props }, ref) => /* @__PURE__ */ jsx(
  AccordionPrimitive.Content,
  {
    ref,
    className: cn(
      "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm",
      contentClassName
    ),
    ...props,
    children: /* @__PURE__ */ jsx("div", { className: cn("pt-0 pb-4", className), children })
  }
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;
const Collapsible = CollapsiblePrimitive.Root;
const CollapsibleTrigger = React.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(
  CollapsiblePrimitive.Trigger,
  {
    ref,
    className: cn("flex items-center", className),
    ...props,
    children
  }
));
CollapsibleTrigger.displayName = CollapsiblePrimitive.Trigger.displayName;
const CollapsibleContent = React.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(
  CollapsiblePrimitive.Content,
  {
    ref,
    className: cn(
      "data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden",
      className
    ),
    ...props,
    children
  }
));
CollapsibleContent.displayName = CollapsiblePrimitive.Content.displayName;
const ProgressBar = ({ statuses, currentStatus }) => {
  const statusIndex = statuses.findIndex(
    (status) => status.key === currentStatus
  );
  const currentStepIndex = statusIndex !== -1 ? statusIndex : 0;
  return /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2", children: statuses.map((status, index) => /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Status,
      {
        label: status.label,
        complete: currentStepIndex > index,
        active: currentStepIndex === index,
        stepNumber: index + 1,
        isLast: index === statuses.length - 1
      }
    ),
    index < statuses.length - 1 && /* @__PURE__ */ jsx(ChevronRightIcon, {})
  ] }, status.key)) });
};
const Status = ({
  label,
  complete,
  active,
  stepNumber,
  isLast
}) => {
  const getTextColor = () => {
    if (complete) return "green-9";
    if (active && isLast) return "green-9";
    if (active) return "primary";
    return "text-default";
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(StatusIcon, { complete, active, stepNumber }),
    /* @__PURE__ */ jsx(Text$1, { color: getTextColor(), children: label })
  ] });
};
const StatusIcon = ({ complete, active, stepNumber }) => {
  if (complete || active) {
    return /* @__PURE__ */ jsx(Icon, { name: "circle-check", color: "green-9", variant: "filled", size: 16 });
  }
  return /* @__PURE__ */ jsx("div", { className: "flex size-4 items-center justify-center rounded-full bg-text-default", children: /* @__PURE__ */ jsx(Text$1, { color: "white-opacity-1 leading-none", variant: "footnote", children: stepNumber }) });
};
const SheetTabs = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.Root,
  {
    ref,
    className: cn("flex flex-col", className),
    ...props
  }
));
SheetTabs.displayName = TabsPrimitive.Root.displayName;
const SheetTabsList = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.List,
  {
    ref,
    className: cn(
      "gap-1 inline-flex items-center bg-surface-base-elevated text-text-default",
      className
    ),
    ...props
  }
));
SheetTabsList.displayName = TabsPrimitive.List.displayName;
const SheetTabsTrigger = React.forwardRef(({ className, children, iconName = "table", ...props }, ref) => /* @__PURE__ */ jsxs(
  TabsPrimitive.Trigger,
  {
    ref,
    className: cn(
      "gap-1 border border-transparent inline-flex items-center justify-center whitespace-nowrap px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      "data-[state=active]:text-text-title data-[state=active]:shadow-sm data-[state=active]:border-border-100 data-[state=active]:rounded-sm data-[state=active]:bg-base-white data-[state=active]:font-semibold",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx(Icon, { name: iconName, size: 16 }),
      children
    ]
  }
));
SheetTabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
const SheetTabsContent = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.Content,
  {
    ref,
    className: cn(
      "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    ),
    ...props
  }
));
SheetTabsContent.displayName = TabsPrimitive.Content.displayName;
const MappingRow = ({
  sourceField,
  destinationField,
  className,
  sourceOptions,
  destinationOptions,
  onSourceFieldChange,
  onDestinationFieldChange,
  onAccept,
  onReject,
  onRuleChange,
  defaultRule = "assign"
}) => {
  const [sourceOpen, setSourceOpen] = useState(false);
  const [destinationOpen, setDestinationOpen] = useState(false);
  const [ruleOpen, setRuleOpen] = useState(false);
  const [source, setSource] = useState(sourceField);
  const [destination, setDestination] = useState(destinationField);
  const [rule, setRule] = useState(defaultRule);
  const sourceDropdownOptions = sourceOptions == null ? void 0 : sourceOptions.map((option) => ({
    key: option,
    label: option,
    value: option
  }));
  const dropdownOptions = destinationOptions == null ? void 0 : destinationOptions.map((option) => ({
    key: option,
    label: option,
    value: option
  }));
  const ruleOptions = [
    {
      key: "assign",
      label: "Assign",
      icon: "assign"
    },
    {
      key: "combine",
      label: "Combine",
      icon: "merge"
    },
    {
      key: "singleValue",
      label: "Single Value",
      icon: "single-value"
    },
    {
      key: "firstAvailable",
      label: "First Available",
      icon: "first-available"
    },
    {
      key: "list",
      label: "List",
      icon: "list"
    },
    {
      key: "split",
      label: "Split",
      icon: "split"
    }
  ];
  const selectedRule = useMemo(() => ruleOptions.find((option) => option.key === rule), [rule]) || ruleOptions[0];
  useEffect(() => {
    if (source !== sourceField) {
      setSource(sourceField);
    }
  }, [sourceField]);
  useEffect(() => {
    if (destination !== destinationField) {
      setDestination(destinationField);
    }
  }, [destinationField]);
  const handleSourceOptionSelect = (option) => {
    setSourceOpen(false);
    setSource(option.value);
    onSourceFieldChange == null ? void 0 : onSourceFieldChange(option.value);
  };
  const handleDestinationOptionSelect = (option) => {
    setDestinationOpen(false);
    setDestination(option.value);
    onDestinationFieldChange == null ? void 0 : onDestinationFieldChange(option.value);
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "[.mapping-section_&]:py-3 [.mapping-section_&]:rounded-none [.mapping-section_&]:px-0",
        "flex bg-card w-full gap-4 rounded-sm p-3",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex basis-1/2 items-center justify-between gap-4", children: [
          /* @__PURE__ */ jsx("div", { className: "flex-1", children: sourceOptions && sourceOptions.length > 0 ? /* @__PURE__ */ jsxs(DropdownMenu, { open: sourceOpen, onOpenChange: setSourceOpen, children: [
            /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
              Button,
              {
                variant: "outline",
                className: "w-full justify-between rounded-xs",
                children: [
                  source || "Select source field",
                  /* @__PURE__ */ jsx(
                    Icon,
                    {
                      name: "chevron-down-small",
                      className: `ml-2 size-4 opacity-50 transition-transform ${sourceOpen ? "rotate-180" : ""}`
                    }
                  )
                ]
              }
            ) }),
            /* @__PURE__ */ jsx(DropdownMenuContent, { className: "w-[var(--radix-dropdown-menu-trigger-width)] rounded-xs p-0", children: /* @__PURE__ */ jsx(
              DropdownSearchContent,
              {
                options: sourceDropdownOptions ?? [],
                onOptionSelect: handleSourceOptionSelect,
                placeholder: "Select a source field"
              }
            ) })
          ] }) : /* @__PURE__ */ jsx(
            Text$1,
            {
              variant: "body",
              className: "rounded-xs border border-card px-2 py-1",
              children: source
            }
          ) }),
          /* @__PURE__ */ jsxs(DropdownMenu, { open: ruleOpen, onOpenChange: setRuleOpen, children: [
            /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Button, { variant: "ghost", className: "justify-between rounded-xs px-1", children: [
              /* @__PURE__ */ jsx(
                Icon,
                {
                  name: selectedRule == null ? void 0 : selectedRule.icon,
                  className: "size-4",
                  size: 16
                }
              ),
              selectedRule == null ? void 0 : selectedRule.label
            ] }) }),
            /* @__PURE__ */ jsx(DropdownMenuContent, { className: "w-[var(--radix-dropdown-menu-trigger-width)] rounded-xs p-0", children: ruleOptions.map((option) => /* @__PURE__ */ jsxs(
              DropdownMenuItem,
              {
                className: "flex items-start gap-2",
                onSelect: () => {
                  setRule(option.key);
                  onRuleChange == null ? void 0 : onRuleChange(option.key);
                },
                children: [
                  /* @__PURE__ */ jsx(Icon, { name: option.icon, size: 16 }),
                  option.label
                ]
              },
              option.key
            )) })
          ] }),
          /* @__PURE__ */ jsx(Icon, { name: "arrow-right", size: 16 })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex basis-1/2 items-center justify-between", children: [
          destinationOptions && destinationOptions.length > 0 ? /* @__PURE__ */ jsxs(
            DropdownMenu,
            {
              open: destinationOpen,
              onOpenChange: setDestinationOpen,
              children: [
                /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
                  Button,
                  {
                    variant: "outline",
                    className: "w-full justify-between rounded-xs",
                    children: [
                      destination || "Select destination field",
                      /* @__PURE__ */ jsx(
                        Icon,
                        {
                          name: "chevron-down-small",
                          className: `ml-2 size-4 opacity-50 transition-transform ${destinationOpen ? "rotate-180" : ""}`
                        }
                      )
                    ]
                  }
                ) }),
                /* @__PURE__ */ jsx(DropdownMenuContent, { className: "w-[var(--radix-dropdown-menu-trigger-width)] rounded-xs p-0", children: /* @__PURE__ */ jsx(
                  DropdownSearchContent,
                  {
                    options: dropdownOptions ?? [],
                    onOptionSelect: handleDestinationOptionSelect,
                    placeholder: "Select a destination"
                  }
                ) })
              ]
            }
          ) : /* @__PURE__ */ jsx(Text$1, { variant: "body", className: "rounded-xs border px-2 py-1", children: destinationField }),
          (onAccept || onReject) && /* @__PURE__ */ jsx(Fragment$1, { children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
            onReject && /* @__PURE__ */ jsx(
              "button",
              {
                onClick: onReject,
                className: cn(
                  "size-5 rounded-xs border border-transparent flex items-center justify-center",
                  "hover:border-gray-3"
                ),
                children: /* @__PURE__ */ jsx(Icon, { name: "cross-large", size: 12 })
              }
            ),
            onAccept && /* @__PURE__ */ jsx(
              "button",
              {
                onClick: onAccept,
                className: cn(
                  "flex size-5 items-center justify-center rounded-xs border border-transparent text-green-9",
                  "hover:bg-green-9 hover:text-card"
                ),
                children: /* @__PURE__ */ jsx(Icon, { name: "checkmark", size: 12 })
              }
            )
          ] }) })
        ] })
      ]
    }
  );
};
const MappingGroup = ({
  title,
  description,
  children,
  className,
  defaultExpanded = true,
  onDismiss,
  onApplyAll
}) => {
  const [isExpanded, setIsExpanded] = React__default.useState(defaultExpanded);
  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex flex-col bg-purple-alpha-1 border border-purple-alpha-4 rounded-sm w-full overflow-hidden p-4",
        className
      ),
      children: [
        /* @__PURE__ */ jsx("div", { className: "flex items-center justify-between", children: /* @__PURE__ */ jsxs("div", { className: "flex w-full items-start gap-2", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex grow flex-col gap-1", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx(Text$1, { children: title }),
              /* @__PURE__ */ jsx(
                "button",
                {
                  className: "flex size-4 items-center justify-center",
                  onClick: toggleExpanded,
                  children: /* @__PURE__ */ jsx(
                    motion.div,
                    {
                      animate: {
                        rotate: isExpanded ? 0 : 90
                      },
                      transition: {
                        duration: 0.2,
                        ease: "easeInOut"
                      },
                      children: /* @__PURE__ */ jsx(
                        Icon,
                        {
                          name: "chevron-bottom",
                          className: "origin-center",
                          size: 12
                        }
                      )
                    }
                  )
                }
              )
            ] }),
            description && /* @__PURE__ */ jsx(Text$1, { color: "muted", children: description })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
            /* @__PURE__ */ jsx("button", { className: "text-sm text-muted", onClick: onDismiss, children: "Dismiss" }),
            /* @__PURE__ */ jsx("button", { className: "text-sm", onClick: onApplyAll, children: "Apply all" })
          ] })
        ] }) }),
        /* @__PURE__ */ jsx(
          motion.div,
          {
            initial: false,
            animate: {
              height: isExpanded ? "auto" : 0,
              opacity: isExpanded ? 1 : 0
            },
            transition: {
              duration: 0.2,
              ease: "easeInOut"
            },
            className: "overflow-hidden",
            children: /* @__PURE__ */ jsx("div", { className: "mt-4 flex flex-col gap-4", children })
          }
        )
      ]
    }
  );
};
const MappingSection = ({
  children,
  className
}) => {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "mapping-section",
        "flex flex-col bg-surface-base border border-border-80 rounded-sm w-full overflow-hidden",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex gap-4 px-3 pb-2 pt-3", children: [
          /* @__PURE__ */ jsx(Text$1, { className: "basis-1/2", variant: "callout", color: "muted", emphasized: true, children: "Source" }),
          /* @__PURE__ */ jsx(Text$1, { className: "basis-1/2", variant: "callout", color: "muted", emphasized: true, children: "Destination" })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "flex flex-col divide-y bg-base-white px-3", children })
      ]
    }
  );
};
const ToastProvider = ToastPrimitives.Provider;
const ToastViewport = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Viewport,
  {
    ref,
    className: cn(
      "fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
      className
    ),
    ...props
  }
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;
const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border p-4 pr-6 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        default: "border bg-surface-base text-text-title",
        destructive: "group border-text-sentiment-destructive bg-surface-base text-text-sentiment-destructive"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
const Toast = React.forwardRef(({ className, variant, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    ToastPrimitives.Root,
    {
      ref,
      className: cn(toastVariants({ variant }), className),
      ...props
    }
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;
const ToastAction = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Action,
  {
    ref,
    className: cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive",
      className
    ),
    ...props
  }
));
ToastAction.displayName = ToastPrimitives.Action.displayName;
const ToastClose = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Close,
  {
    ref,
    className: cn(
      "absolute right-1 top-1 rounded-md p-1 text-text-title/50 opacity-0 transition-opacity hover:text-text-title focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600",
      className
    ),
    "toast-close": "",
    ...props,
    children: /* @__PURE__ */ jsx(Icon, { name: "cross-small", size: 12 })
  }
));
ToastClose.displayName = ToastPrimitives.Close.displayName;
const ToastTitle = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Title,
  {
    ref,
    className: cn("text-sm font-semibold [&+div]:text-xs", className),
    ...props
  }
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;
const ToastDescription = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Description,
  {
    ref,
    className: cn("text-sm opacity-90", className),
    ...props
  }
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;
const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1e6;
let count = 0;
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}
const toastTimeouts = /* @__PURE__ */ new Map();
const addToRemoveQueue = (toastId) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }
  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: "REMOVE_TOAST",
      toastId
    });
  }, TOAST_REMOVE_DELAY);
  toastTimeouts.set(toastId, timeout);
};
const reducer = (state, action) => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT)
      };
    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === action.toast.id ? { ...t, ...action.toast } : t
        )
      };
    case "DISMISS_TOAST": {
      const { toastId } = action;
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast2) => {
          addToRemoveQueue(toast2.id);
        });
      }
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === toastId || toastId === void 0 ? {
            ...t,
            open: false
          } : t
        )
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === void 0) {
        return {
          ...state,
          toasts: []
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId)
      };
  }
};
const listeners = [];
let memoryState = { toasts: [] };
function dispatch(action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}
function toast({ ...props }) {
  const id = genId();
  const update = (props2) => dispatch({
    type: "UPDATE_TOAST",
    toast: { ...props2, id }
  });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });
  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      }
    }
  });
  return {
    id,
    dismiss,
    update
  };
}
function useToast() {
  const [state, setState] = React.useState(memoryState);
  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);
  return {
    ...state,
    toast,
    dismiss: (toastId) => dispatch({ type: "DISMISS_TOAST", toastId })
  };
}
function Toaster() {
  const { toasts } = useToast();
  return /* @__PURE__ */ jsxs(ToastProvider, { children: [
    toasts.map(function({ id, title, description, action, ...props }) {
      return /* @__PURE__ */ jsxs(Toast, { ...props, children: [
        /* @__PURE__ */ jsxs("div", { className: "grid gap-1", children: [
          title && /* @__PURE__ */ jsx(ToastTitle, { children: title }),
          description && /* @__PURE__ */ jsx(ToastDescription, { children: description })
        ] }),
        action,
        /* @__PURE__ */ jsx(ToastClose, {})
      ] }, id);
    }),
    /* @__PURE__ */ jsx(ToastViewport, {})
  ] });
}
const VisuallyHidden = VisuallyHiddenPrimative.Root;
VisuallyHidden.displayName = VisuallyHiddenPrimative.Root.displayName;
const usePaginate = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const onChangePage = useCallback(
    (page) => {
      const newPage = typeof page === "string" ? Number.parseInt(page) : page;
      if (!isNaN(newPage)) {
        setCurrentPage(newPage);
      }
    },
    [setCurrentPage, currentPage]
  );
  return { onChangePage, currentPage, setCurrentPage };
};
const range = (start, end) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};
const buildDisplayRange = (currentPage, rangeSize, totalPageCount) => {
  if (currentPage <= 2) {
    return range(2, rangeSize + 1);
  }
  if (currentPage >= totalPageCount - 1) {
    return range(totalPageCount - rangeSize, totalPageCount - 1);
  }
  return range(currentPage - 1, currentPage + rangeSize - 2);
};
const usePagination = ({
  totalCount,
  displayedPages = 5,
  pageSize = 10,
  currentPage
}) => {
  const pagination = useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize);
    const variableRangeSize = displayedPages - 2;
    const maxPages = displayedPages > 5 ? displayedPages : 5;
    if (maxPages >= totalPageCount) {
      return { displayRange: range(1, totalPageCount), totalPageCount };
    }
    const displayRange = [1, totalPageCount];
    const midRange = buildDisplayRange(
      currentPage,
      variableRangeSize,
      totalPageCount
    );
    displayRange.splice(1, 0, ...midRange);
    if (!midRange.includes(2)) {
      displayRange.splice(1, 0, "...");
    }
    if (!midRange.includes(totalPageCount - 1)) {
      displayRange.splice(displayRange.length - 1, 0, "...");
    }
    return { displayRange, totalPageCount };
  }, [totalCount, pageSize, displayedPages, currentPage]);
  return pagination;
};
const useSort = ({
  data = []
}) => {
  const [currentSortKey, setCurrentSortKey] = useState("");
  const [currentSortDirection, setCurrentSortDirection] = useState("none");
  const handleSort = useCallback(
    (key, direction) => {
      setCurrentSortKey(key);
      setCurrentSortDirection(direction);
      return direction === "none" ? data : [...data].sort((a, b) => {
        var _a, _b;
        const valA = (_a = a[key]) == null ? void 0 : _a.toLowerCase();
        const valB = (_b = b[key]) == null ? void 0 : _b.toLowerCase();
        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    },
    [data]
  );
  const sortedData = useMemo(
    () => data ? handleSort(currentSortKey, currentSortDirection) : [],
    [data, currentSortKey, currentSortDirection]
  );
  const SortButton = useCallback(
    ({ sortKey, sortType, ...props }) => /* @__PURE__ */ jsx(
      SortButtonElement,
      {
        handleSort,
        sortType,
        sortKey,
        direction: currentSortKey === sortKey ? currentSortDirection : "none",
        ...props
      }
    ),
    [handleSort, currentSortKey, currentSortDirection]
  );
  return {
    SortButton,
    sortedData
  };
};
const nextDirection = {
  none: "asc",
  asc: "desc",
  desc: "none"
};
const sortIcon = {
  asc: {
    alphabetical: "filter-ascending",
    numeric: "filter-asc"
  },
  desc: {
    alphabetical: "filter-descending",
    numeric: "filter-desc"
  },
  none: {
    alphabetical: "arrow-top-bottom",
    numeric: "arrow-top-bottom"
  }
};
const SortButtonElement = ({
  sortKey,
  sortType,
  handleSort,
  direction,
  iconSize,
  children,
  ...props
}) => {
  const handleClick = () => {
    const newDirection = nextDirection[direction];
    handleSort(sortKey, newDirection);
  };
  return /* @__PURE__ */ jsxs(Button, { onClick: handleClick, variant: "outline", ...props, children: [
    /* @__PURE__ */ jsx(Icon, { name: sortIcon[direction][sortType], size: iconSize }),
    children
  ] });
};
export {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertTitle,
  Area,
  AreaChart,
  AutoProgress,
  AutoProgressHairline,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Bar,
  BarChart,
  BoundedDialogContainer,
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Brush,
  Button,
  CUSTOM_LIST,
  Calendar,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  CartesianGrid,
  Cell,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  ChatInput,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuIcon,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
  Dialog,
  DialogClose,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerToggle,
  DrawerTrigger,
  DropdownInput,
  DropdownListContent,
  DropdownMenu,
  DropdownMenuArrow,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  DropdownSearchContent,
  FieldFilterDropdown,
  Filter,
  FilterBlock,
  FilterByValue,
  FilterCollection,
  FilterFieldElement,
  FilterOperationElement,
  FilterValidationTabs,
  FilterValueElement,
  Icon,
  Input,
  LEGACY_ICON_MAP,
  Label,
  Legend,
  Line,
  LineChart,
  MappingGroup,
  MappingRow,
  MappingSection,
  MultiSelect,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Pie,
  PieChart,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  Progress,
  ProgressBar,
  RadioGroup,
  RadioGroupItem,
  ReferenceLine,
  ResponsiveContainer,
  RichMentionsAutocomplete,
  RichMentionsContext,
  RichMentionsInput,
  RichMentionsProvider,
  Separator,
  SheetTabs,
  SheetTabsContent,
  SheetTabsList,
  SheetTabsTrigger,
  SimpleSearchBar,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text$1 as Text,
  TextArea,
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  Toaster,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Tray,
  TrayFooter,
  TrayHeader,
  TrayTab,
  VisuallyHidden,
  XAxis,
  YAxis,
  buttonVariants,
  cn,
  processMentionsInHTML,
  textVariants,
  toast,
  usePaginate,
  usePagination,
  useSort,
  useToast
};
